import React, { useState, useEffect, useMemo, useRef } from 'react';
import Workstation from './Workstation';
import {
  Trash2, ExternalLink, FileText, Upload, Eye, Plus, CheckCircle,
  AlertTriangle, X, Download, Copy, TrendingUp, Scissors, Filter,
  Maximize, Calculator, Settings, Image as ImageIcon, Link as LinkIcon,
  Save, RefreshCw, BarChart2, DollarSign, Clock, Calendar, ChevronLeft,
  ChevronDown, ChevronUp, Edit3, Package, Layers, Search, Star,
  ZoomIn, ArrowUpDown, BarChart, PieChart, Activity
} from 'lucide-react';

// =================================================================================================
// 1. CONSTANTS & CONFIG
// =================================================================================================
const API_BASE = 'https://nirvikalp.integrashion.com/api';
const AI_BASE = 'https://shayana.pythonanywhere.com';

// =================================================================================================
// 2. SYSTEM UTILITIES & AI EXTRACTION
// =================================================================================================
const processFile = (file) => {
  return new Promise((resolve, reject) => {
    if (file.type.match(/image.*/)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let w = img.width, h = img.height;
          const MAX = 1200;
          if (w > h) { if (w > MAX) { h *= MAX / w; w = MAX; } }
          else { if (h > MAX) { w *= MAX / h; h = MAX; } }
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          resolve({ data: canvas.toDataURL("image/jpeg", 0.7), type: 'image' });
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    } else if (file.type.match(/text.*/) || file.name.endsWith('.csv')) {
      const reader = new FileReader();
      reader.onload = (e) => resolve({ data: e.target.result, type: 'text' });
      reader.readAsText(file);
    } else {
      alert("For PDF/Excel, please upload a SCREENSHOT (Image) or save as CSV.");
      reject(new Error("Unsupported file type"));
    }
  });
};

const extractDocumentData = async (file, type) => {
  try {
    const processed = await processFile(file);
    const response = await fetch(`${API_BASE}/ai_service.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ file: processed.data, fileType: processed.type, docType: type })
    });
    const text = await response.text();
    let data;
    try { data = JSON.parse(text); }
    catch (e) { throw new Error("Server Error: " + text.substring(0, 100)); }
    if (data.error) throw new Error(data.error);
    return data;
  } catch (err) { throw err; }
};

// ---- PO SIZE TABLE HELPER ----
// Parses PO color+size breakdown and saves to po_size_table in DB
// Each PO line has: num, col (color), qty, sizeBreakdown (string "S:10,M:20" or object)
// Each color may have its own size split; if not, we distribute PO qty proportionally across sizes
const savePOSizeTable = async (styleRef, pos) => {
  const rows = [];
  pos.forEach(po => {
    const poNo = po.num || po.po_no || '-';
    // sizeBreakdown can be:
    //   1. An array of {color, size, qty} objects  => best case from AI
    //   2. An object { S: 10, M: 20, L: 30 }       => total size split
    //   3. A string "S:10,M:20,L:30"               => total size split string
    //   4. Empty / undefined                        => just store color+total with size=ALL
    const sb = po.sizeBreakdown || po.size_breakdown;
    
    if (Array.isArray(sb) && sb.length > 0 && sb[0].size) {
      // Case 1: Array of {color, size, qty}
      sb.forEach(item => {
        rows.push({
          po_no: poNo, color: item.color || po.col || 'All',
          size: item.size || 'ALL', qty: parseInt(item.qty) || 0,
          ex_fty_date: po.date || null, fob: parseFloat(po.fob) || 0, destination: po.dest || ''
        });
      });
    } else if (typeof sb === 'object' && sb !== null && !Array.isArray(sb)) {
      // Case 2: {S: 10, M: 20}
      Object.entries(sb).forEach(([size, qty]) => {
        rows.push({
          po_no: poNo, color: po.col || 'All',
          size: size, qty: parseInt(qty) || 0,
          ex_fty_date: po.date || null, fob: parseFloat(po.fob) || 0, destination: po.dest || ''
        });
      });
    } else if (typeof sb === 'string' && sb.trim()) {
      // Case 3: "S:10,M:20,L:30"
      const parts = sb.split(',');
      parts.forEach(part => {
        const [size, qty] = part.split(':').map(s => s.trim());
        if (size) {
          rows.push({
            po_no: poNo, color: po.col || 'All',
            size: size, qty: parseInt(qty) || 0,
            ex_fty_date: po.date || null, fob: parseFloat(po.fob) || 0, destination: po.dest || ''
          });
        }
      });
    } else {
      // Case 4: No size breakdown — store as single row with size='ALL'
      rows.push({
        po_no: poNo, color: po.col || 'All',
        size: 'ALL', qty: parseInt(po.qty) || 0,
        ex_fty_date: po.date || null, fob: parseFloat(po.fob) || 0, destination: po.dest || ''
      });
    }
  });

  if (rows.length === 0 || !styleRef) return;
  try {
    const response = await fetch(`${API_BASE}/save_universal.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category: 'po_size_table', data: { styleRef, rows } })
    });
    const result = await response.json();
    return result;
  } catch (err) {
    console.error('Failed to save PO size table:', err);
  }
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) return reject("No file provided");
    if (file.size > 10 * 1024 * 1024) return reject("File too large! Max limit is 10MB.");
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const safeClone = (obj) => {
  try { return JSON.parse(JSON.stringify(obj)); }
  catch (e) { return { ...obj }; }
};

const analyzeImageFeatures = (base64) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 300; canvas.height = 300;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, 300, 300);
      const zones = [];
      const zoneSize = 100;
      for (let y = 0; y < 3; y++) {
        for (let x = 0; x < 3; x++) {
          const imageData = ctx.getImageData(x * zoneSize, y * zoneSize, zoneSize, zoneSize).data;
          let r = 0, g = 0, b = 0, count = 0;
          for (let i = 0; i < imageData.length; i += 4) {
            if (imageData[i + 3] > 128 && (imageData[i] < 250 || imageData[i + 1] < 250 || imageData[i + 2] < 250)) {
              r += imageData[i]; g += imageData[i + 1]; b += imageData[i + 2]; count++;
            }
          }
          if (count === 0) { zones.push({ r: 255, g: 255, b: 255, empty: true }); }
          else { zones.push({ r: Math.floor(r / count), g: Math.floor(g / count), b: Math.floor(b / count), empty: false }); }
        }
      }
      resolve(zones);
    };
    img.onerror = () => resolve(null);
  });
};

const calculateSimilarity = (zones1, zones2) => {
  if (!zones1 || !zones2 || zones1.length !== 9 || zones2.length !== 9) return 0;
  let totalScore = 0, activeZones = 0;
  for (let i = 0; i < 9; i++) {
    const z1 = zones1[i], z2 = zones2[i];
    if (z1.empty && z2.empty) continue;
    if (z1.empty !== z2.empty) { activeZones++; continue; }
    activeZones++;
    const dist = Math.sqrt(Math.pow(z1.r - z2.r, 2) + Math.pow(z1.g - z2.g, 2) + Math.pow(z1.b - z2.b, 2));
    totalScore += Math.max(0, (1 - dist / 441) * 100);
  }
  return activeZones === 0 ? 100 : totalScore / activeZones;
};

// =================================================================================================
// 3. SUB-COMPONENTS
// =================================================================================================

const WorkstationView = ({ data }) => {
  if (!data || !data.items || data.items.length === 0) return <div style={{ color: '#9699a6', fontSize: '0.8rem', textAlign: 'center' }}>No Layout</div>;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#e6e9ef', borderRadius: '8px' }}>
      {data.items.map(p => (
        <div key={p.instId} style={{
          position: 'absolute', left: p.x * 0.4, top: p.y * 0.4, width: p.w * 0.4, height: p.h * 0.4,
          backgroundImage: `url(${p.localTexture || data.globalTexture})`, maskImage: `url(${p.image})`, WebkitMaskImage: `url(${p.image})`,
          backgroundColor: (p.localTexture || data.globalTexture) ? 'transparent' : '#9699a6',
          maskSize: 'contain', maskRepeat: 'no-repeat', maskPosition: 'center',
          transform: `rotate(${p.rotate}deg)`
        }} />
      ))}
    </div>
  );
};

// Mini bar chart for production follow-up
const ProductionBar = ({ label, value, total, color }) => {
  const pct = total > 0 ? Math.min(100, (value / total) * 100) : 0;
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#676879', marginBottom: '3px' }}>
        <span>{label}</span><span style={{ fontWeight: 'bold', color }}>{value.toLocaleString()}</span>
      </div>
      <div style={{ background: '#f0f1f4', borderRadius: '6px', height: '8px' }}>
        <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: '6px', transition: 'width 0.4s' }} />
      </div>
    </div>
  );
};

// Supplier comparison card
const SupplierQuoteCard = ({ supplier, costHeads, isSelected }) => (
  <div style={{
    border: `2px solid ${isSelected ? '#00a65e' : '#e6e9ef'}`, borderRadius: '8px', padding: '12px',
    background: isSelected ? '#f0fdf4' : 'white', minWidth: '180px'
  }}>
    <div style={{ fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '8px', color: isSelected ? '#00a65e' : '#323338' }}>
      {supplier} {isSelected && <CheckCircle size={12} style={{ display: 'inline' }} />}
    </div>
    {Object.entries(costHeads).map(([k, v]) => (
      <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', padding: '2px 0', borderBottom: '1px solid #f0f1f4' }}>
        <span style={{ color: '#676879' }}>{k}</span>
        <span style={{ fontWeight: 'bold' }}>{typeof v === 'number' ? v.toFixed(2) : v}</span>
      </div>
    ))}
  </div>
);

// Season history row component
const SeasonHistoryRow = ({ season, supplier, qty, placedQty, price, leadtimePlan, leadtimeActual, smv, efficiency, isPlaced }) => {
  const ltDiff = leadtimeActual - leadtimePlan;
  return (
    <tr style={{ background: isPlaced ? '#f0fdf4' : 'white' }}>
      <td>{season}</td>
      <td style={{ fontWeight: isPlaced ? 'bold' : 'normal', color: isPlaced ? '#00a65e' : '#323338' }}>
        {supplier} {isPlaced && <Star size={12} color="#f59e0b" style={{ display: 'inline' }} />}
      </td>
      <td style={{ textAlign: 'right' }}>{qty?.toLocaleString()}</td>
      <td style={{ textAlign: 'right', color: '#0073ea', fontWeight: 'bold' }}>{(placedQty || 0).toLocaleString()}</td>
      <td style={{ textAlign: 'right', color: '#0073ea' }}>{price}</td>
      <td style={{ textAlign: 'right' }}>{leadtimePlan}d</td>
      <td style={{ textAlign: 'right', color: ltDiff > 0 ? '#e2445c' : '#00a65e' }}>
        {leadtimeActual}d {ltDiff !== 0 && <span style={{ fontSize: '0.7rem' }}>({ltDiff > 0 ? '+' : ''}{ltDiff})</span>}
      </td>
      <td style={{ textAlign: 'right' }}>{smv}</td>
      <td style={{ textAlign: 'right', color: efficiency >= 80 ? '#00a65e' : '#f59e0b' }}>{efficiency}%</td>
    </tr>
  );
};

// Collapsible section component
const Section = ({ title, icon, color = '#323338', bg = '#f5f6f8', children, defaultOpen = true, badge = null }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: '16px', border: '1px solid #e6e9ef', borderRadius: '10px', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: bg, cursor: 'pointer', borderBottom: open ? '1px solid #e6e9ef' : 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize: '0.85rem', color }}>
          {icon} {title}
          {badge && <span style={{ background: color, color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem' }}>{badge}</span>}
        </div>
        {open ? <ChevronUp size={16} color={color} /> : <ChevronDown size={16} color={color} />}
      </div>
      {open && <div style={{ padding: '16px', background: 'white' }}>{children}</div>}
    </div>
  );
};

// OB Supplier Upload Card
const OBSupplierCard = ({ ob, standard, onRemove, idx }) => {
  const obOps = ob.operations || [];
  const stdOps = standard || [];
  const deviations = obOps.filter(op => {
    const stdOp = stdOps.find(s => s.op?.toLowerCase() === op.op?.toLowerCase());
    return stdOp && Math.abs(parseFloat(op.smv) - parseFloat(stdOp.smv)) > 0.05;
  });
  const totalSMV = obOps.reduce((a, b) => a + (parseFloat(b.smv) || 0), 0);
  return (
    <div style={{ border: '1px solid #e6e9ef', borderRadius: '8px', overflow: 'hidden', flex: '0 0 300px' }}>
      <div style={{ background: '#f5f6f8', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e6e9ef' }}>
        <span style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{ob.supplier_name || `Supplier OB ${idx + 1}`}</span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ background: '#f0fdf4', color: '#00a65e', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>SMV: {totalSMV.toFixed(3)}</span>
          {deviations.length > 0 && <span style={{ background: '#fef2f2', color: '#e2445c', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem' }}>{deviations.length} deviations</span>}
          <button onClick={() => onRemove(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={14} color="#e2445c" /></button>
        </div>
      </div>
      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead><tr style={{ background: '#f5f6f8' }}><th style={{ padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid #e6e9ef' }}>Op</th><th style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid #e6e9ef' }}>SMV</th><th style={{ padding: '4px 8px', borderBottom: '1px solid #e6e9ef' }}>Δ</th></tr></thead>
          <tbody>
            {obOps.slice(0, 15).map((op, i) => {
              const stdOp = stdOps.find(s => s.op?.toLowerCase() === op.op?.toLowerCase());
              const diff = stdOp ? (parseFloat(op.smv) - parseFloat(stdOp.smv)).toFixed(3) : null;
              const hasDeviation = diff !== null && Math.abs(parseFloat(diff)) > 0.05;
              return (
                <tr key={i} style={{ background: hasDeviation ? '#fef9c3' : 'white', borderBottom: '1px solid #f0f1f4' }}>
                  <td style={{ padding: '3px 8px', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.op}</td>
                  <td style={{ padding: '3px 8px', textAlign: 'right' }}>{parseFloat(op.smv || 0).toFixed(3)}</td>
                  <td style={{ padding: '3px 8px', textAlign: 'center', color: hasDeviation ? (parseFloat(diff) > 0 ? '#e2445c' : '#00a65e') : '#9699a6', fontWeight: hasDeviation ? 'bold' : 'normal' }}>{diff !== null ? (parseFloat(diff) > 0 ? `+${diff}` : diff) : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Notification toast
const Toast = ({ message, type = 'info' }) => {
  if (!message) return null;
  const colors = { info: '#0073ea', success: '#00a65e', error: '#e2445c', warning: '#f59e0b' };
  return (
    <div style={{ position: 'fixed', top: '70px', right: '20px', zIndex: 99999, background: colors[type] || colors.info, color: 'white', padding: '12px 20px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: '0.85rem', maxWidth: '350px', animation: 'slideIn 0.3s ease' }}>
      <style>{`@keyframes slideIn { from { transform: translateX(50px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
      {message}
    </div>
  );
};

// Editable table for BOM/PO/Spec rows
const EditableTable = ({ headers, rows, onAddRow, onDeleteRow, onEditRow, emptyMsg }) => {
  const [editIdx, setEditIdx] = useState(null);
  const [editRow, setEditRow] = useState({});
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table className="npd-table" style={{ fontSize: '0.8rem', width: '100%' }}>
          <thead>
            <tr>{headers.map(h => <th key={h}>{h}</th>)}<th style={{ width: '80px' }}>Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={headers.length + 1} style={{ textAlign: 'center', color: '#9699a6', padding: '20px' }}>{emptyMsg}</td></tr>}
            {rows.map((row, i) => editIdx === i ? (
              <tr key={i} style={{ background: '#e8f0ff' }}>
                {headers.map(h => (
                  <td key={h}><input value={editRow[h.toLowerCase().replace(/ /g, '_')] || ''} onChange={e => setEditRow({ ...editRow, [h.toLowerCase().replace(/ /g, '_')]: e.target.value })} style={{ width: '100%', border: '1px solid #93c5fd', padding: '2px 4px', borderRadius: '3px', fontSize: '0.78rem' }} /></td>
                ))}
                <td>
                  <button onClick={() => { onEditRow(i, editRow); setEditIdx(null); }} style={{ background: '#00a65e', color: 'white', border: 'none', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: '0.7rem', marginRight: '3px' }}>✓</button>
                  <button onClick={() => setEditIdx(null)} style={{ background: '#e6e9ef', border: 'none', padding: '2px 6px', borderRadius: '3px', cursor: 'pointer', fontSize: '0.7rem' }}>✗</button>
                </td>
              </tr>
            ) : (
              <tr key={i}>
                {headers.map(h => <td key={h}>{row[h.toLowerCase().replace(/ /g, '_')] || row[h] || '-'}</td>)}
                <td>
                  <button onClick={() => { setEditIdx(i); setEditRow({ ...row }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '4px' }}><Edit3 size={12} color="#0073ea" /></button>
                  <button onClick={() => onDeleteRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={12} color="#e2445c" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={onAddRow} style={{ marginTop: '8px', padding: '5px 14px', background: '#e8f0ff', border: '1px dashed #3b82f6', borderRadius: '6px', color: '#0073ea', cursor: 'pointer', fontSize: '0.78rem' }}>+ Add Row Manually</button>
    </div>
  );
};

// Chart view for production data
const ProductionChartView = ({ data, totalQty }) => {
  const stages = [
    { label: 'Cut', key: 'cut', color: '#f59e0b' },
    { label: 'Sew', key: 'sew', color: '#3b82f6' },
    { label: 'Finish', key: 'pack', color: '#8b5cf6' },
    { label: 'Shipped', key: 'ship', color: '#00a65e' },
  ];
  const max = Math.max(totalQty, ...Object.values(data));
  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', padding: '12px 0', height: '120px' }}>
      {stages.map(s => {
        const val = data[s.key] || 0;
        const pct = max > 0 ? (val / max) * 100 : 0;
        return (
          <div key={s.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{val.toLocaleString()}</span>
            <div style={{ width: '100%', background: '#f0f1f4', borderRadius: '4px 4px 0 0', height: '80px', display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ width: '100%', background: s.color, borderRadius: '4px 4px 0 0', height: `${pct}%`, transition: 'height 0.5s' }} />
            </div>
            <span style={{ fontSize: '0.72rem', color: '#676879' }}>{s.label}</span>
          </div>
        );
      })}
    </div>
  );
};

// =================================================================================================
// 4. MAIN COMPONENT: STYLE REGISTRY
// =================================================================================================

// =================================================================================================
// FLIGHT BOARD DASHBOARD COMPONENT
// =================================================================================================
const useFlightClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now;
};

const workingDaysUntil = (targetDateStr) => {
  if (!targetDateStr) return null;
  const target = new Date(targetDateStr);
  const today = new Date(); today.setHours(0,0,0,0);
  if (isNaN(target)) return null;
  let count = 0, d = new Date(today);
  while (d < target) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
};

const getStageProgress = (prod, totalQty) => {
  if (!totalQty) return { stage: '—', pct: 0 };
  const { cut = 0, sew = 0, pack = 0, ship = 0 } = prod;
  if (ship > 0) return { stage: 'SHIPPED', pct: 100 };
  if (pack > 0) return { stage: 'PACKED', pct: Math.round((pack / totalQty) * 100) };
  if (sew > 0) return { stage: 'SEWING', pct: Math.round((sew / totalQty) * 100) };
  if (cut > 0) return { stage: 'CUTTING', pct: Math.round((cut / totalQty) * 100) };
  return { stage: 'PRE-PROD', pct: 0 };
};

const getOnTimeChance = (daysLeft, prod, totalQty, smv) => {
  if (daysLeft === null) return null;
  if (daysLeft <= 0) return 0;
  const { sew = 0 } = prod;
  const balance = Math.max(0, (totalQty || 0) - sew);
  if (balance === 0) return 100;
  const dailyCap = smv > 0 ? Math.floor((480 / smv) * 0.75) : 200;
  const canMake = dailyCap * daysLeft;
  return Math.min(100, Math.round((canMake / balance) * 100));
};

const FlightStatusBadge = ({ label, ok, warn, neutral }) => {
  const bg = neutral ? '#323338' : ok ? '#166534' : warn ? '#92400e' : '#7f1d1d';
  const color = neutral ? '#9699a6' : ok ? '#4ade80' : warn ? '#fcd34d' : '#fca5a5';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: bg, color, padding: '3px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
      {ok ? '✓' : warn ? '!' : neutral ? '–' : '✗'} {label}
    </span>
  );
};

const StageDot = ({ label, active, done, input, output, wip, pct }) => {
  const ref  = React.useRef(null);
  const [tip, setTip] = React.useState(null);   // {left, top} when showing

  const showTip = () => {
    if (!ref.current) return;
    const r   = ref.current.getBoundingClientRect();
    const TW  = 155;   // tooltip width
    const left = Math.max(8, Math.min(r.left + r.width/2 - TW/2, window.innerWidth - TW - 8));
    const top  = r.top;   // transform will push it above
    setTip({ left, top, arrowLeft: r.left + r.width/2 - left });
  };

  const fK = n => (n || 0).toLocaleString();
  const clr = done ? '#4ade80' : active ? '#fbbf24' : '#2d3f50';
  const bdr = done ? '#00a65e' : active ? '#f59e0b' : '#4a5568';
  const hasData = (output || 0) > 0;

  return (
    <div ref={ref}
      onMouseEnter={showTip}
      onMouseLeave={() => setTip(null)}
      style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'2px',
               cursor:'pointer', userSelect:'none', position:'relative' }}>

      <div style={{ width:11, height:11, borderRadius:'50%', background:clr,
        border:`1.5px solid ${bdr}`,
        boxShadow: active ? '0 0 8px #fbbf24' : done ? '0 0 5px #4ade80' : 'none' }} />
      <span style={{ fontSize:'0.62rem', color:clr, fontWeight:'700', letterSpacing:'0.03em' }}>
        {label}
      </span>

      {tip && (
        <div style={{
          position: 'fixed',
          left:      tip.left,
          top:       tip.top - 8,
          transform: 'translateY(-100%)',
          zIndex:    2147483647,
          width:     '155px',
          background: '#0a1628',
          border:    '1px solid #2d4a6e',
          borderRadius: '8px',
          padding:   '10px 12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.85)',
          pointerEvents: 'none',
        }}>
          {/* Arrow */}
          <div style={{
            position: 'absolute', top: '100%',
            left: tip.arrowLeft, transform: 'translateX(-50%)',
            width:0, height:0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid #2d4a6e',
          }} />
          {/* Header */}
          <div style={{ fontWeight:'bold', color:'#7dd3fc', fontSize:'0.72rem',
            borderBottom:'1px solid #1e3a5f', paddingBottom:'4px', marginBottom:'6px' }}>
            {label}&nbsp;{done ? '✓ Done' : active ? '⟳ Active' : '○ Pending'}
          </div>
          {/* Data */}
          {hasData ? (
            <div style={{ display:'grid', gridTemplateColumns:'auto 1fr',
              gap:'3px 8px', fontSize:'0.71rem' }}>
              <span style={{color:'#64748b'}}>Input:</span>
              <span style={{color:'#e2e8f0',fontWeight:'bold',textAlign:'right'}}>{fK(input)}</span>
              <span style={{color:'#64748b'}}>Output:</span>
              <span style={{color:done?'#4ade80':'#fbbf24',fontWeight:'bold',textAlign:'right'}}>{fK(output)}</span>
              <span style={{color:'#64748b'}}>WIP:</span>
              <span style={{color:(wip||0)>0?'#f87171':'#4ade80',fontWeight:'bold',textAlign:'right'}}>{fK(wip)}</span>
              {pct !== undefined && <>
                <span style={{color:'#64748b'}}>Done:</span>
                <span style={{color:'#e2e8f0',textAlign:'right'}}>{Math.round(pct)}%</span>
                <div style={{gridColumn:'1/-1',marginTop:'5px',
                  background:'#1e3a5f',borderRadius:'3px',height:'4px'}}>
                  <div style={{
                    width:`${Math.min(pct,100)}%`,height:'100%',borderRadius:'3px',
                    background:pct>=100?'#4ade80':pct>=60?'#fbbf24':'#3b82f6',
                    transition:'width 0.4s'
                  }}/>
                </div>
              </>}
            </div>
          ) : (
            <div style={{color:'#475569',fontSize:'0.71rem'}}>No production data yet</div>
          )}
        </div>
      )}
    </div>
  );
};



const FlightBoardDashboard = ({ db, getCostSummary, getProdSummary, format, quotations, productionLogs, plansList, dark, T, toggleTheme }) => {
  const now = useFlightClock();

  // theme (dark/T/toggleTheme) received via props from StyleRegistry

  const [boardFilter, setBoardFilter] = useState({ brand: '', status: '', search: '' });
  const [sortCol, setSortCol] = useState('exFty');
  const stylesList = Array.isArray(db?.mgmtStyles) ? db.mgmtStyles : [];

  const rows = useMemo(() => {
    return stylesList
      .filter(s => s && s.id)
      .filter(s =>
        (!boardFilter.brand || s.brand === boardFilter.brand) &&
        (!boardFilter.status || (s.status || '').toLowerCase().includes(boardFilter.status.toLowerCase())) &&
        (!boardFilter.search || (s.styleRef || '').toLowerCase().includes(boardFilter.search.toLowerCase()))
      )
      .map(s => {
        const cost = getCostSummary(s.id);
        const prod = getProdSummary(s.styleRef);
        const sortedPos = (s.pos || []).filter(p => p.date).sort((a, b) => new Date(a.date) - new Date(b.date));
        const firstPO = sortedPos[0];
        const exFtyDate = firstPO?.date || null;
        const daysLeft = workingDaysUntil(exFtyDate);
        const stageInfo = getStageProgress(prod, s.totalQty || 0);
        const smv = s.workstationData?.aggregatedOps?.reduce((a, b) => a + (parseFloat(b.smv) || 0), 0) || 0;
        const onTimeChance = getOnTimeChance(daysLeft, prod, s.totalQty, smv);
        const supplier = (() => {
          const q = (quotations||[]).find(q=>q.name===s.styleRef&&q.status==='Selected')?.supplier; if(q) return q;
          const al=(db.allocations||[]).find(l=>l.style===s.styleRef);
          if(al){const d=al.factory||al.factoryName||al.supplierName||'';if(d)return d;const fId=al.factoryId||al.factory_id||'';if(fId){const f=(db.factories||[]).find(x=>String(x.id)===String(fId));if(f?.name)return f.name;}}
          const alLine=(s.allocatedLines||[]).find(l=>l.factory||l.factoryName||l.factoryId);
          if(alLine){const dn=alLine.factory||alLine.factoryName||'';if(dn)return dn;const fId2=alLine.factoryId||alLine.factory_id||'';if(fId2){const f2=(db.factories||[]).find(x=>String(x.id)===String(fId2));if(f2?.name)return f2.name;}}
          return null;
        })();
        const plan = (plansList || []).find(p => p.style === s.styleRef);
        const planProcesses = plan?.processes || [];
        const sampleApproved = planProcesses.some(p => p.name?.toLowerCase().includes('sample') && p.approvalStatus === 'Approved');
        const costApproved = cost?.status === 'Approved';
        const hasPOs = (s.pos || []).length > 0;
        const poLabel = hasPOs ? firstPO?.num || s.pos[0]?.num : s.status || 'Development';
        const bom = s.bom || [];
        const materialPlaced = bom.length > 0 && bom.every(b => b.status === 'Ordered' || b.ordered);
        const inhouseStatus = bom.length > 0 && bom.some(b => b.inhouse || b.received) ? (bom.every(b => b.inhouse || b.received) ? 'All' : 'Partial') : '—';
        const inspLogs = (productionLogs || []).filter(l => l.style === s.styleRef && (l.type === 'INSPECTION' || l.type === 'QC'));
        const lastInsp = inspLogs.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        const inspStatus = lastInsp?.result || 'Not Yet';
        const shipLogs = (productionLogs || []).filter(l => l.style === s.styleRef && l.type === 'SHIP');
        let exFtyStatus = 'Not Yet';
        if (shipLogs.length > 0) exFtyStatus = shipLogs[0]?.status || 'Transit';
        else if (prod.ship > 0) exFtyStatus = 'Documentation';
        const actualShipLog = shipLogs.find(l => l.date);
        let ontimePct = null;
        if (actualShipLog && exFtyDate) {
          const planD = new Date(exFtyDate), actualD = new Date(actualShipLog.date);
          ontimePct = Math.round((actualD - planD) / (1000 * 60 * 60 * 24));
        }
        const alloc = s.allocatedLines || s.planningData?.allocatedLines || [];
        const prodLine = alloc.length > 0 ? (alloc[0].name || alloc[0].lineName || '—') : '—';
        return { s, cost, prod, exFtyDate, daysLeft, stageInfo, onTimeChance, supplier, poLabel, hasPOs, sampleApproved, costApproved, materialPlaced, inhouseStatus, inspStatus, exFtyStatus, ontimePct, prodLine, smv };
      })
      .sort((a, b) => {
        if (sortCol === 'exFty') return (a.exFtyDate || '9999') > (b.exFtyDate || '9999') ? 1 : -1;
        if (sortCol === 'days') return (a.daysLeft ?? 9999) - (b.daysLeft ?? 9999);
        if (sortCol === 'chance') return (b.onTimeChance ?? -1) - (a.onTimeChance ?? -1);
        if (sortCol === 'style') return (a.s.styleRef || '').localeCompare(b.s.styleRef || '');
        return 0;
      });
  }, [stylesList, boardFilter, sortCol, db.costings, db.productionLogs]);

  const brands = [...new Set(stylesList.map(s => s.brand).filter(Boolean))];
  const TH = ({ col, label, w }) => (
    <th onClick={() => setSortCol(col)} style={{ padding: '8px 6px', textAlign: 'left', color: sortCol === col ? '#fbbf24' : '#676879', fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.05em', cursor: 'pointer', whiteSpace: 'nowrap', width: w, borderBottom: '1px solid #1e3a5f', background: '#060d1a', position: 'sticky', top: 0, zIndex: 10 }}>
      {label}{sortCol === col ? ' ▾' : ''}
    </th>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#060d1a', color: 'white', overflow: 'hidden', fontFamily: "'Courier New', monospace" }}>
      <div style={{ background: 'linear-gradient(90deg, #0d1b2a, #0e3460, #0d1b2a)', borderBottom: '2px solid #1e40af', padding: '0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ background: '#1e40af', borderRadius: '6px', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ fontSize: '1.1rem' }}>✈</div>
              <div>
                <div style={{ fontSize: '0.6rem', color: '#93c5fd', letterSpacing: '0.15em' }}>NIRVIKALP</div>
                <div style={{ fontSize: '0.72rem', fontWeight: 'bold', color: 'white', letterSpacing: '0.1em' }}>STYLE STATUS</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select value={boardFilter.brand} onChange={e => setBoardFilter(f => ({ ...f, brand: e.target.value }))}
                style={{ background: '#0d1b2a', border: '1px solid #1e3a5f', color: '#93c5fd', padding: '4px 8px', borderRadius: '6px', fontSize: '0.72rem' }}>
                <option value="">ALL BRANDS</option>
                {brands.map(b => <option key={b} value={b}>{b.toUpperCase()}</option>)}
              </select>
              <select value={boardFilter.status} onChange={e => setBoardFilter(f => ({ ...f, status: e.target.value }))}
                style={{ background: '#0d1b2a', border: '1px solid #1e3a5f', color: '#93c5fd', padding: '4px 8px', borderRadius: '6px', fontSize: '0.72rem' }}>
                <option value="">ALL STATUS</option>
                {['Development', 'Production', 'Shipped', 'Hold'].map(s => <option key={s}>{s.toUpperCase()}</option>)}
              </select>
              <div style={{ display: 'flex', alignItems: 'center', background: '#0d1b2a', border: '1px solid #1e3a5f', borderRadius: '6px', padding: '4px 8px' }}>
                <input value={boardFilter.search} onChange={e => setBoardFilter(f => ({ ...f, search: e.target.value }))}
                  placeholder="SEARCH STYLE..." style={{ background: 'transparent', border: 'none', outline: 'none', color: '#93c5fd', fontSize: '0.72rem', width: '130px', fontFamily: 'inherit' }} />
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#fbbf24', letterSpacing: '0.1em', fontFamily: "'Courier New', monospace" }}>
              {now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div style={{ fontSize: '0.62rem', color: '#676879', letterSpacing: '0.08em' }}>
              {now.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
            </div>
            <div style={{ fontSize: '0.6rem', color: '#323338', marginTop: '1px' }}>{rows.length} STYLES TRACKED</div>
          </div>
        </div>
        <div style={{ background: '#0a1628', borderTop: '1px solid #1e3a5f', padding: '4px 16px', display: 'flex', gap: '16px', fontSize: '0.58rem', color: '#676879' }}>
          {[['#4ade80', 'ON TRACK'], ['#fbbf24', 'AT RISK'], ['#f87171', 'CRITICAL'], ['#9699a6', 'PENDING']].map(([clr, lbl]) => (
            <span key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: clr, display: 'inline-block' }} />{lbl}</span>
          ))}
          <span style={{ marginLeft: 'auto' }}>Click column headers to sort</span>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', minWidth: '1600px' }}>
          <thead>
            <tr>
              <TH col="style" label="BRAND / STYLE" w="140px" />
              <TH col="po" label="PO / STAGE" w="110px" />
              <TH col="qty" label="QTY" w="70px" />
              <TH col="sample" label="SAMPLE" w="80px" />
              <TH col="costing" label="COSTING" w="80px" />
              <TH col="material" label="MATERIAL" w="80px" />
              <TH col="inhouse" label="INHOUSE" w="75px" />
              <TH col="exFty" label="EX-FTY DATE" w="95px" />
              <TH col="days" label="DAYS↓SHIP" w="75px" />
              <TH col="chance" label="ONTIME?" w="75px" />
              <TH col="line" label="PROD LINE" w="90px" />
              <TH col="stage" label="STAGE" w="160px" />
              <TH col="insp" label="INSPECTION" w="100px" />
              <TH col="exftystat" label="EX-FACTORY" w="100px" />
              <TH col="delay" label="DELAY" w="75px" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={15} style={{ textAlign: 'center', padding: '60px', color: '#323338', fontSize: '0.8rem', letterSpacing: '0.1em' }}>NO STYLES FOUND — ADJUST FILTERS</td></tr>
            )}
            {rows.map(({ s, cost, prod, exFtyDate, daysLeft, stageInfo, onTimeChance, supplier, poLabel, hasPOs, sampleApproved, costApproved, materialPlaced, inhouseStatus, inspStatus, exFtyStatus, ontimePct, prodLine }, idx) => {
              const isUrgent = daysLeft !== null && daysLeft <= 7;
              const isWarn = daysLeft !== null && daysLeft <= 14 && daysLeft > 7;
              const rowBg = idx % 2 === 0 ? '#080f1e' : '#060d1a';
              const urgentBg = isUrgent ? 'rgba(239,68,68,0.06)' : isWarn ? 'rgba(245,158,11,0.06)' : rowBg;
              const stageOrder = ['CUTTING','SEWING','WASHING','FINISHING','PACKED','SHIPPED','PRE-PROD'];
              return (
                <tr key={s.id} style={{ background: urgentBg, borderBottom: '1px solid #0d1b2a', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#0e1f3a'}
                  onMouseLeave={e => e.currentTarget.style.background = urgentBg}>
                  <td style={{ padding: '9px 8px' }}>
                    <div style={{ fontWeight: 'bold', color: '#e6e9ef', fontSize: '0.72rem', letterSpacing: '0.05em' }}>{s.styleRef}</div>
                    <div style={{ fontSize: '0.6rem', color: '#676879', marginTop: '1px' }}>{s.brand} · {s.season}</div>
                    {supplier && <div style={{ fontSize: '0.58rem', color: '#3b82f6', marginTop: '1px' }}>🏭 {supplier}</div>}
                  </td>
                  <td style={{ padding: '9px 8px' }}>
                    {hasPOs ? <span style={{ color: '#93c5fd', fontWeight: 'bold', fontSize: '0.68rem' }}>{poLabel}</span>
                      : <span style={{ background: '#323338', color: '#676879', padding: '1px 5px', borderRadius: '3px', fontSize: '0.6rem' }}>{(poLabel || 'DEVELOPMENT').toUpperCase()}</span>}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', color: '#cbd5e1', fontWeight: 'bold', fontFamily: 'monospace' }}>{(s.totalQty || 0).toLocaleString()}</td>
                  <td style={{ padding: '9px 8px' }}><FlightStatusBadge label={sampleApproved ? 'APPROVED' : 'PENDING'} ok={sampleApproved} neutral={!sampleApproved} /></td>
                  <td style={{ padding: '9px 8px' }}><FlightStatusBadge label={costApproved ? 'APPROVED' : cost ? 'PENDING' : 'NONE'} ok={costApproved} warn={cost && !costApproved} neutral={!cost} /></td>
                  <td style={{ padding: '9px 8px' }}><FlightStatusBadge label={materialPlaced ? 'ALL PLACED' : 'INCOMPLETE'} ok={materialPlaced} warn={!materialPlaced} /></td>
                  <td style={{ padding: '9px 8px' }}><FlightStatusBadge label={inhouseStatus === 'All' ? 'ALL IN' : inhouseStatus === 'Partial' ? 'PARTIAL' : 'NONE'} ok={inhouseStatus === 'All'} warn={inhouseStatus === 'Partial'} neutral={inhouseStatus === '—'} /></td>
                  <td style={{ padding: '9px 8px', fontFamily: 'monospace', fontSize: '0.68rem', color: isUrgent ? '#f87171' : isWarn ? '#fbbf24' : '#93c5fd', fontWeight: isUrgent ? 'bold' : 'normal' }}>
                    {exFtyDate || '—'}
                    {isUrgent && <div style={{ fontSize: '0.55rem', color: '#f87171', letterSpacing: '0.05em', marginTop: '1px', animation: 'blink 1s step-end infinite' }}>⚠ URGENT</div>}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right' }}>
                    {daysLeft !== null ? <span style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '0.88rem', color: isUrgent ? '#f87171' : isWarn ? '#fbbf24' : '#4ade80' }}>{daysLeft}</span>
                      : <span style={{ color: '#323338' }}>—</span>}
                    {daysLeft !== null && <div style={{ fontSize: '0.55rem', color: '#676879' }}>WORKING DAYS</div>}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right' }}>
                    {onTimeChance !== null ? (
                      <div>
                        <div style={{ fontFamily: 'monospace', fontWeight: '800', fontSize: '0.9rem', color: onTimeChance >= 80 ? '#4ade80' : onTimeChance >= 50 ? '#fbbf24' : '#f87171' }}>{onTimeChance}%</div>
                        <div style={{ background: '#0d1b2a', borderRadius: '2px', height: '3px', marginTop: '3px', width: '50px' }}>
                          <div style={{ width: `${onTimeChance}%`, height: '100%', background: onTimeChance >= 80 ? '#4ade80' : onTimeChance >= 50 ? '#fbbf24' : '#f87171', borderRadius: '2px', transition: 'width 0.5s' }} />
                        </div>
                      </div>
                    ) : <span style={{ color: '#323338' }}>—</span>}
                  </td>
                  <td style={{ padding: '9px 8px', fontSize: '0.65rem', color: '#7dd3fc', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prodLine}</td>
                  <td style={{ padding: '9px 8px', overflow: 'visible', position: 'relative', zIndex: 20 }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', overflow: 'visible' }}>
                      {['CUT', 'SEW', 'WASH', 'FIN', 'PACK'].map(st => {
                        const stageMap = { 'CUT': 'CUTTING', 'SEW': 'SEWING', 'WASH': 'WASHING', 'FIN': 'FINISHING', 'PACK': 'PACKED' };
                        const fullStage = stageMap[st];
                        const done   = stageOrder.indexOf(stageInfo.stage) > stageOrder.indexOf(fullStage);
                        const active = stageInfo.stage === fullStage;
                        const qty = s.totalQty || 0;
                        const dd = { CUT:{input:qty,output:prod.cut,wip:Math.max(0,qty-prod.cut)},
                          SEW:{input:prod.cut,output:prod.sew,wip:Math.max(0,prod.cut-prod.sew)},
                          WASH:{input:prod.sew,output:prod.sew,wip:0},
                          FIN:{input:prod.sew,output:prod.pack,wip:Math.max(0,prod.sew-prod.pack)},
                          PACK:{input:prod.pack,output:prod.ship,wip:Math.max(0,prod.pack-prod.ship)}}[st]||{};
                        const pct = dd.input>0 ? dd.output/dd.input*100 : 0;
                        return <StageDot key={st} label={st} active={active} done={done}
                          input={dd.input} output={dd.output} wip={dd.wip} pct={pct}/>;
                      })}
                    </div>
                    {stageInfo.pct > 0 && (
                      <div style={{ marginTop: '4px', background: '#1e3a5f', borderRadius: '2px', height: '3px', width: '75px', overflow:'hidden' }}>
                        <div style={{ width: `${Math.min(stageInfo.pct,100)}%`, height: '100%',
                          background: stageInfo.pct>=100?'#4ade80':stageInfo.pct>=60?'#fbbf24':'#3b82f6',
                          borderRadius: '2px', transition:'width 0.5s' }} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '9px 8px' }}>
                    {(() => {
                      const iMap = { 'Not Yet': [false, false, true], 'Passed': [true, false, false], 'Rescreened': [false, true, false], 'Failed': [false, false, false], 'Rejected': [false, false, false] };
                      const [ok, warn, neutral] = iMap[inspStatus] || [false, false, true];
                      return <FlightStatusBadge label={inspStatus.toUpperCase()} ok={ok} warn={warn} neutral={neutral} />;
                    })()}
                  </td>
                  <td style={{ padding: '9px 8px' }}>
                    {(() => {
                      const ok = exFtyStatus === 'Transit' || exFtyStatus === 'Delivered';
                      const warn = exFtyStatus === 'Documentation';
                      const neutral = exFtyStatus === 'Not Yet';
                      return <FlightStatusBadge label={exFtyStatus.toUpperCase()} ok={ok} warn={warn} neutral={neutral} />;
                    })()}
                  </td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                    {ontimePct !== null
                      ? <span style={{ color: ontimePct <= 0 ? '#4ade80' : ontimePct <= 3 ? '#fbbf24' : '#f87171', fontWeight: 'bold', fontSize: '0.78rem' }}>
                          {ontimePct <= 0 ? 'ON TIME' : `+${ontimePct}d`}
                        </span>
                      : <span style={{ color: '#323338', fontSize: '0.65rem' }}>IN PROGRESS</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <style>{`
        @keyframes blink { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
      `}</style>
    </div>
  );
};

export default function StyleRegistry({ db, setDb, saveToDB, currency, setCurrency: setCurrencyProp, view, setView, targetStyle, setTargetStyle, setActiveTab, setActiveBlock, loadCosting, userRole }) {

  // ---- STATE ----
  const [form, setForm] = useState({
    brand: "", styleRef: "", buyer: "", season: "", fabric: "", product: "",
    content: "", construction: "", sizeRange: "", img: null, tech: null, spec: null,
    status: "Development", measurements: null, bom: null, files: [], options: [],
    obSettings: { eff: 75, hours: 8 }, allocatedSuppliers: []
  });
  const [pos, setPos] = useState([]);
  const [samples, setSamples] = useState([]);
  const [inputs, setInputs] = useState({ poN: "", poC: "", poQ: "", poD: "", poFob: "", poDest: "", poSB: "" });
  const [sampIn, setSampIn] = useState({ type: "", qty: "", date: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [notifType, setNotifType] = useState('info');

  // Visual Search
  const [searchImage, setSearchImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [scoredStyles, setScoredStyles] = useState([]);
  const [filters, setFilters] = useState({ brand: "", style: "", season: "", status: "" });

  // Studio
  const [showVisualStudio, setShowVisualStudio] = useState(false);

  // Passport-specific state
  const [editingProcess, setEditingProcess] = useState(null);
  const [obEff, setObEff] = useState(75);
  const [obHours, setObHours] = useState(8);
  const [prodChartMode, setProdChartMode] = useState('bar'); // bar | tracker
  const [passportTab, setPassportTab] = useState('overview'); // overview | docs | ob | history
  const [supplierOBs, setSupplierOBs] = useState([]); // uploaded supplier OBs
  const [obLoading, setObLoading] = useState(false);
  const [predictedFob, setPredictedFob] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);

  // ── Theme for status board — lives here so header + board share same state ──
  const [dark, setDark] = React.useState(() => {
    try { return localStorage.getItem('nirv_board_theme') !== 'light'; } catch { return true; }
  });
  const toggleTheme = () => setDark(d => {
    const next = !d;
    try { localStorage.setItem('nirv_board_theme', next ? 'dark' : 'light'); } catch {}
    return next;
  });
  const T = dark ? {
    bg:'#060d1a', hBg:'#0a1525', rowBg:'#060d1a', rowAlt:'#07101d', rowHover:'#0d1b2a',
    border:'#1a3050', text:'#c8d6e5', textSub:'#4a6a8a', textBold:'#e8f0fa',
    thBg:'#060d1a', thColor:'#4a6a8a', tdColor:'#b0c4d8', badgeBg:'#0a1f38',
  } : {
    bg:'#f0f1f4', hBg:'#fff', rowBg:'#fff', rowAlt:'#f8f9fc', rowHover:'#eff6ff',
    border:'#e6e9ef', text:'#323338', textSub:'#676879', textBold:'#1e1f2e',
    thBg:'#f0f1f4', thColor:'#676879', tdColor:'#323338', badgeBg:'#edf2ff',
  };

  // Passport editable sections state
  const [editingBom, setEditingBom] = useState(false);
  const [editingSpec, setEditingSpec] = useState(false);
  const [localBom, setLocalBom] = useState([]);
  const [localSpec, setLocalSpec] = useState(null);
  const [localPos, setLocalPos] = useState([]);

  // Image upload modal in passport
  const [showImgUpload, setShowImgUpload] = useState(false);
  // PO size editing modal — super admin only
  const [editingPOSizes, setEditingPOSizes]   = useState(null);
  const [poSizeEdits, setPoSizeEdits]         = useState({});
  const [poSizeSaving, setPoSizeSaving]       = useState(false);
  // PO extraction review — shows extracted data for editing before DB save
  const [pendingExtractedPOs, setPendingExtractedPOs] = useState(null);
  // Derive super-admin flag from userRole prop
  const isSuperAdmin = typeof userRole === 'string'
    && (userRole.toLowerCase().includes('admin') || userRole.toLowerCase().includes('super'));

  // Layout helpers
  const containerStyle = { height: '100%', display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' };
  const scrollableContentStyle = { flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px', boxSizing: 'border-box', scrollBehavior: 'smooth' };

  // ---- DATA ACCESS ----
  const stylesList = Array.isArray(db?.mgmtStyles) ? db.mgmtStyles : [];
  const costingsList = Array.isArray(db?.costings) ? db.costings : [];
  const plansList = Array.isArray(db?.stylePlans) ? db.stylePlans : [];
  const brandsList = Array.isArray(db?.brands) ? db.brands : [];
  const buyersList = Array.isArray(db?.buyers) ? db.buyers : [];
  const seasonsList = Array.isArray(db?.seasons) ? db.seasons : [];
  const fabricsList = Array.isArray(db?.fabrics) ? db.fabrics : [];
  const productsList = Array.isArray(db?.products) ? db.products : [];
  const quotations = db.sourcingQuotes || [];
  const productionLogs = Array.isArray(db?.productionLogs) ? db.productionLogs : [];

  const baseCurrency = db?.currencies?.find(c => c.isBase) || { name: 'USD', rate: 1 };

  // ── LOCAL CURRENCY OVERRIDE — persisted in localStorage ────────────────────
  // This means users can switch display currency RIGHT HERE without any prop chain.
  const [localCurrencyName, setLocalCurrencyName] = React.useState(() => {
    try { return localStorage.getItem('nirv_displayCurrency') || ''; } catch { return ''; }
  });
  const setDisplayCurrency = (name) => {
    setLocalCurrencyName(name);
    try { localStorage.setItem('nirv_displayCurrency', name); } catch {}
    // Also update App.js state so ALL components use same currency
    const found = activeCurrencyList.find(c => c.name === name);
    if (found && typeof setCurrencyProp === 'function') {
      setCurrencyProp({ name: found.name, rate: parseFloat(found.rate) || 1 });
    }
  };

  // Priority: 1. local override  2. currency prop  3. db selected  4. base
  const activeCurrencyList = Array.isArray(db?.currencies) ? db.currencies : [];
  const activeCurrency = (localCurrencyName && activeCurrencyList.find(c => c.name === localCurrencyName))
    || (currency && currency.name ? currency : null)
    || activeCurrencyList.find(c => c.isSelected || c.isActive || c.isDefault)
    || baseCurrency;
  const currentRate        = parseFloat(activeCurrency?.rate) || 1;
  const baseRate           = parseFloat(baseCurrency?.rate)   || 1;
  const activeCurrencyName = activeCurrency?.name || baseCurrency?.name || 'USD';
  const conversionFactor   = baseRate > 0 ? (currentRate / baseRate) : 1;
  const format = (val) => `${activeCurrencyName} ${((parseFloat(val) || 0) * (conversionFactor !== 1 ? 1 : 1)).toFixed(2)}`;

  // ---- NOTIFICATIONS ----
  const notify = (msg, type = 'info') => {
    setNotification(msg); setNotifType(type);
    setTimeout(() => setNotification(null), 4000);
  };

  // ---- SCROLL HELPER ----
  const scrollContainer = (amount) => {
    const container = document.getElementById('main-scroll-container');
    if (container) container.scrollBy({ top: amount, behavior: 'smooth' });
  };

  const NavigationArrows = () => (
    <div style={{ position: 'fixed', bottom: '30px', right: '30px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <button onClick={() => scrollContainer(-400)} style={{ width: '46px', height: '46px', borderRadius: '50%', background: '#323338', color: 'white', border: 'none', fontSize: '20px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↑</button>
      <button onClick={() => scrollContainer(400)} style={{ width: '46px', height: '46px', borderRadius: '50%', background: '#323338', color: 'white', border: 'none', fontSize: '20px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↓</button>
    </div>
  );

  // ---- COST HELPERS ----
  const getCostSummary = (styleId) => {
    try {
      const c = costingsList.find(x => x.styleId === styleId);
      if (!c) return null;
      const d = c.data || {};
      // Renamed inner param item (was b) — eliminates minifier TDZ collision
      const sum = (arr) => (Array.isArray(arr) ? arr : []).reduce((acc, item) => acc + (parseFloat(item?.total) || 0), 0);
      const style = stylesList.find(s => s.id === styleId);
      const _wsAggSmv = style?.workstationData?.aggregatedOps
        ? style.workstationData.aggregatedOps.reduce((s, op) => s + (parseFloat(op.smv) || 0), 0)
        : 0;
      let liveSMV = _wsAggSmv || parseFloat(style?.workstationData?.smv) || parseFloat(style?.manualSmv) || parseFloat(d.cm?.sam) || 0;
      const cmEff  = Math.max(1, parseFloat(d.cm?.eff  || 0));
      const cmBase = d.cm ? (d.cm.cost > 0 ? parseFloat(d.cm.cost) : (liveSMV * parseFloat(d.cm.cpm || 0)) / (cmEff / 100)) : 0;
      const ohPct  = parseFloat(d.comm?.oh) || 0;
      const prPct  = parseFloat(d.comm?.profit) || 0;
      const fobRaw = (sum(d.material) + sum(d.trims) + sum(d.va) + sum(d.finishing) + cmBase) * (1 + (ohPct + prPct) / 100);
      return { m: sum(d.material)*conversionFactor, t: sum(d.trims)*conversionFactor, v: sum(d.va)*conversionFactor, f: sum(d.finishing)*conversionFactor, cm: cmBase*conversionFactor, fob: fobRaw*conversionFactor, status: d.status||'Pending', smv: liveSMV, rawData: d, currency: d.currency||'' };
    } catch(err) { console.error('[getCostSummary]', styleId, err); return null; }
  };

  const cdcSrcPrio = (l) => {
    const s=l.source||'',t=l.type||'';
    if(s==='SCAN'||t==='SCAN') return 3;
    if(s==='FOLLOWUP'||t==='DAILY_LOG') return 2;
    if(t==='CUT'||t==='SEW'||t==='PACK'||t==='SHIP') return 2;
    if(s==='DATAPAD'||t==='DATAPAD_LOG') return 1;
    return 2;
  };
  const cdcAggregate = (logs, stage) => {
    // FOLLOWUP entries = daily cumulative totals entered by supervisor → SUM all
    // DATAPAD entries  = per-operator per-period counts → deduplicate same op+period
    const result = [];
    const dataPadMap = {};
    logs.forEach(l => {
      const val = parseFloat(l[stage]||0); if (val<=0) return;
      const src = l.source || l.type || '';
      const isFollowUp = src==='FOLLOWUP' || src==='DAILY_LOG' || (src!=='DATAPAD'&&src!=='DATAPAD_LOG');
      if (isFollowUp) {
        // FollowUp: add directly — already a cumulative daily figure
        result.push({ val, prio:2, color: l.color||'' });
      } else {
        // DataPad: deduplicate by op+period (different operators still separate)
        const key = `${l.date||''}|${l.line||l.section||''}|${l.hour||''}|${l.operator||l.operator_id||'_'}`;
        const prio = cdcSrcPrio(l);
        if (!dataPadMap[key]) dataPadMap[key] = { val, prio, color: l.color||'' };
        else if (prio > dataPadMap[key].prio) dataPadMap[key] = { val, prio, color: l.color||'' };
        else if (prio === dataPadMap[key].prio) dataPadMap[key].val += val;
      }
    });
    return [...result, ...Object.values(dataPadMap)];
  };
  const getProdSummary = (styleRef) => {
    const rawLogs = productionLogs.filter(l => l.style === styleRef || l.style_ref === styleRef);
    const logs = rawLogs.map(l=>{
      if(l.cutting!==undefined||l.sewing!==undefined) return l;
      const qty=parseInt(l.quantity)||0,t=l.type||'';
      return {...l,cutting:t==='CUT'?qty:0,sewing:t==='SEW'?qty:0,
              finishing:t==='PACK'||t==='FINISHING'?qty:0,ship_qty:t==='SHIP'?qty:0};
    });
    const cutC=cdcAggregate(logs,'cutting'),sewC=cdcAggregate(logs,'sewing');
    const packC=cdcAggregate(logs.map(l=>({...l,finishing:l.finishing||l.pack||0})),'finishing');
    const shipC=cdcAggregate(logs.map(l=>({...l,ship_qty:l.ship_qty||0})),'ship_qty');
    const cut=cutC.reduce((s,e)=>s+e.val,0),sew=sewC.reduce((s,e)=>s+e.val,0);
    const pack=packC.reduce((s,e)=>s+e.val,0),ship=shipC.reduce((s,e)=>s+e.val,0);
    const safeSew=cut>0?Math.min(sew,cut):sew,safePack=safeSew>0?Math.min(pack,safeSew):pack;
    const warnings=[];
    if(sew>cut&&cut>0) warnings.push(`⚠ SEW(${Math.round(sew)}) > CUT(${Math.round(cut)}) — check DataPad entries`);
    const colorMap={};
    // Include ALL entries — those without color go into '(Unspecified)'
    // This ensures: sum of all color rows = total header figure
    [...cutC.map(e=>({...e,st:'cut'})),...sewC.map(e=>({...e,st:'sew'})),...packC.map(e=>({...e,st:'pack'}))].forEach(e=>{
      const colorKey = e.color || '(Unspecified)';
      if(!colorMap[colorKey]) colorMap[colorKey]={cut:0,sew:0,pack:0,ship:0};
      colorMap[colorKey][e.st]+=e.val;
    });
    // ── Add scan events (barcode scans) as additional sewing source ─────────
    const scanRows = (db?.scanEvents||[]).filter(s=>s.style===styleRef);
    const scanSew  = scanRows.reduce((a,s)=>a+(parseInt(s.pieces)||0),0);
    const scanSmv  = scanRows.reduce((a,s)=>a+(parseFloat(s.smv_total)||0),0);
    // Merge scan data into colorMap
    scanRows.forEach(s=>{
      const ck=s.color||'(Unspecified)';
      if(!colorMap[ck]) colorMap[ck]={cut:0,sew:0,pack:0,ship:0};
      colorMap[ck].sew += parseInt(s.pieces)||0;
    });
    // Efficiency from scans: SMV earned / (actual hours × 60)
    // We use it separately — available in scanSmv
    const totalSew = Math.max(safeSew, scanSew);
    const efficiency = (scanSmv > 0 && totalSew > 0)
      ? Math.round((scanSmv / totalSew) * 100) : 0;
    return {cut,sew:totalSew,pack:safePack,ship,rawSew:sew,
            scanSew,scanSmv,efficiency,
            byColor:colorMap,warnings};
  };

  const getMergedPlan = (styleRef) => {
    const plan = plansList.find(p => p.style === styleRef);
    const style = stylesList.find(s => s.styleRef === styleRef);
    let combined = [];
    if (plan) combined = [...(plan.processes || [])];
    if (style && style.samples) style.samples.forEach(s => combined.push({ name: `Sample: ${s.type}`, planDate: s.date, actualDate: "", isSample: true, source: "Registry", qty: s.qty }));
    return combined.sort((a, b) => new Date(a.planDate) - new Date(b.planDate));
  };

  // ---- VISUAL SEARCH ----
  const runVisualSearch = async (imageSrc) => {
    setIsAnalyzing(true); setSearchImage(imageSrc);
    const searchFeats = await analyzeImageFeatures(imageSrc);
    if (!searchFeats) { setIsAnalyzing(false); return; }
    const scored = await Promise.all(stylesList.map(async (s) => {
      if (!s.img) return { ...s, matchScore: 0 };
      const sFeats = await analyzeImageFeatures(s.img);
      return { ...s, matchScore: calculateSimilarity(searchFeats, sFeats) };
    }));
    setScoredStyles(scored.sort((a, b) => b.matchScore - a.matchScore));
    setIsAnalyzing(false);
  };

  const handleSearchUpload = (e) => {
    const file = e.target.files[0];
    if (file) { const r = new FileReader(); r.onload = (f) => runVisualSearch(f.target.result); r.readAsDataURL(file); }
  };

  const clearSearch = () => { setSearchImage(null); setScoredStyles([]); };

  // ---- SMART UPLOAD WITH REAL AI ----
  const handleSmartUpload = async (e, type, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    const base64 = await fileToBase64(file);
    if (fieldName) setForm(prev => ({ ...prev, [fieldName]: base64 }));
    else if (type === 'PO') setInputs(prev => ({ ...prev, poFile: { url: base64, name: file.name, ts: new Date().toLocaleString() } }));

    if (window.confirm(`Run AI Extraction on this ${type}?`)) {
      notify(`Extracting data from ${type} via AI...`, 'info');
      try {
        const data = await extractDocumentData(file, type);
        if (type === 'PO') {
          const poData = data.po_details || [];
          const hasPerSizeRows = poData.length > 0 && poData.some(po => (po.size||po.Size) && !(po.size_breakdown||po.sizeBreakdown));
          let newPOs;
          if (hasPerSizeRows) {
            const grouped = {}; const topPoNo=data.po_no||data.po_number||data['PO#']||''; const topDate=data.ex_fty_date||data.po_date||''; const topDest=data.destination||'';
            poData.forEach(po => {
              const poNo=po.po_no||po.po_number||po['PO#']||po['PO']||topPoNo||'-'; const color=po.color||po.colour||po.Color||po.Colour||'Mix'; const key=`${poNo}||${color}`;
              if(!grouped[key]) grouped[key]={po_no:poNo,color,date:po.ex_fty_date||po.po_date||topDate||'',fob:po.base_price||po.fob_price||po.fob||data.fob||'',dest:po.destination||topDest||'',sizes:[]};
              const sz=(po.size||po.Size||'').toString().trim(); const qty=parseFloat(po.qty||po.quantity||po.Qty||0);
              if(sz&&qty>0) grouped[key].sizes.push({size:sz,qty});
            });
            newPOs=Object.values(grouped).map((g,idx)=>({id:Date.now()+idx,num:g.po_no,col:g.color,qty:g.sizes.reduce((a,s)=>a+s.qty,0),date:g.date,fob:String(g.fob),dest:g.dest,sizeBreakdown:g.sizes.map(s=>`${s.size}:${s.qty}`).join(','),file:{url:base64,name:file.name}}));
          } else {
            newPOs=poData.map((po,idx)=>({id:Date.now()+idx,num:po.po_no||po.po_number||po['PO#']||po['PO']||"-",col:po.color||po.colour||po.Color||"Mix",qty:parseFloat(po.qty||po.po_qty||po.total_qty||0),date:po.ex_fty_date||"",fob:po.fob_price||po.base_price||po.fob||"",dest:po.destination||"",sizeBreakdown:po.size_breakdown||po.sizeBreakdown||"",file:{url:base64,name:file.name}}));
          }
          // Show review modal instead of saving directly — prevents reference-doc contamination
          setPendingExtractedPOs(newPOs);
          notify(`Extraction complete — review and correct PO data before saving.`, 'info');
        } else if (type === 'TECHPACK') {
          const bomData = data.bom || [];
          const mappedBOM = bomData.map(b => ({
            category:        b.category || b.section || 'General',
            item_name:       b.item_name || b.item || b.name || b.code || b.package_code || '',
            description:     b.description || b.material || b.spec || b.details || b.remarks || '',
            supplier:        b.supplier || b.vendor || '',
            reference:       b.reference || b.ref || b.item_code || b.mill_ref || '',
            consumption:     b.consumption || b.total_qty || '',
            placement:       b.placement || b.location || '',
            color_breakdown: b.color_breakdown || b.color || b.colour || '',
            size_variation:  b.size_variation || b.size_breakdown || '',
            comments:        b.comments || b.notes || b.remarks || '',
          }));
          setForm(prev => {
            const existing = prev.bom || [];
            const merged = [...existing];
            mappedBOM.forEach(item => {
              const isDupe = merged.some(e =>
                (e.item_name || '').toLowerCase() === (item.item_name || '').toLowerCase() &&
                (e.category  || '').toLowerCase() === (item.category  || '').toLowerCase()
              );
              if (!isDupe) merged.push(item);
            });
            return { ...prev, bom: merged };
          });
          const sects = [...new Set(mappedBOM.map(b => b.category).filter(Boolean))].join(', ');
          notify(`BoM: added ${mappedBOM.length} items${sects ? ' (' + sects + ')' : ''}. Total accumulated: ${(window._lastBomCount || 0)} items. Upload next page to keep adding.`, 'success');
        } else if (type === 'SPEC') {
          setForm(prev => ({ ...prev, measurements: data }));
          notify("Graded measurements extracted!", 'success');
        } else if (type === 'OB_SUPPLIER') {
          setSupplierOBs(prev => [...prev, data]);
          notify(`Supplier OB extracted: ${data.supplier_name || 'Unknown'}`, 'success');
        }
        setNotification(null);
      } catch (err) {
        notify(`Extraction failed: ${err.message}`, 'error');
      }
    }
  };

  // ---- PO MANAGEMENT ----
  const addPO = () => {
    if (!inputs.poN) return alert("PO Number is required.");
    // Duplicate check — same PO# + color combination
    const isDup = pos.some(p => p.num === inputs.poN && (p.col||'') === (inputs.poC||''));
    if (isDup) return alert(`PO "${inputs.poN}" with color "${inputs.poC||'—'}" already exists. Edit the existing PO instead.`);
    const newPO = { id: Date.now(), num: inputs.poN, col: inputs.poC || "", qty: parseFloat(inputs.poQ) || 0, date: inputs.poD || "", fob: inputs.poFob || "", dest: inputs.poDest || "", sizeBreakdown: inputs.poSB || "" };
    setPos(prev => {
      const updated = [...prev, newPO];
      if (form.styleRef) savePOSizeTable(form.styleRef, updated);
      return updated;
    });
    setInputs({ poN: "", poC: "", poQ: "", poD: "", poFob: "", poDest: "", poSB: "" });
  };

  const deletePO = (idx) => setPos(prev => prev.filter((_, i) => i !== idx));
  const editPOField = (idx, field, val) => setPos(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p));

  const addSample = () => {
    if (!sampIn.type) return alert("Sample Type is required.");
    setSamples(prev => [...prev, { id: Date.now(), type: sampIn.type, qty: sampIn.qty || "", date: sampIn.date || "" }]);
    setSampIn({ type: "", qty: "", date: "" });
  };

  // ---- EDIT / SAVE ----
  const editStyle = (s) => {
    // Rebuild pos[] from po_size_table if style.pos is empty
    // This happens when POs were extracted via AI (landing in po_size_table)
    // but never written back to style.pos[]
    let resolvedPos = Array.isArray(s.pos) && s.pos.length > 0 ? s.pos : [];
    if (resolvedPos.length === 0) {
      const styleRef = s.styleRef || s.name;
      const dbPONums = [...new Set(
        (db.poSizeTable || []).filter(r => r.style_ref === styleRef).map(r => r.po_no)
      )];
      if (dbPONums.length > 0) {
        resolvedPos = dbPONums.map(pn => {
          const rows = (db.poSizeTable || []).filter(r => r.style_ref === styleRef && r.po_no === pn);
          const totalQty = rows.reduce((a, r) => a + (parseInt(r.qty) || 0), 0);
          const colors   = [...new Set(rows.map(r => r.color).filter(Boolean))];
          // Rebuild sizeBreakdown string from rows: "S:100,M:200,L:150"
          const sizeStr  = rows.filter(r => r.size && r.size !== 'ALL')
                               .map(r => `${r.size}:${r.qty}`)
                               .join(',');
          return {
            id:            Date.now() + Math.random(),
            num:           pn,
            col:           colors.join('/'),
            qty:           totalQty,
            date:          rows[0]?.ex_fty_date || '',
            fob:           rows[0]?.fob || '',
            dest:          rows[0]?.destination || '',
            sizeBreakdown: sizeStr,
          };
        });
        // Immediately persist back to style so it's consistent from now on
        // (fire and forget — don't block the edit UI)
        setTimeout(async () => {
          const merged = { ...s, pos: resolvedPos, totalQty: resolvedPos.reduce((a,p)=>a+(parseFloat(p.qty)||0),0) };
          await dbSaveStyle(merged);
          await savePOSizeTable(s.styleRef || s.name, resolvedPos);
        }, 500);
      }
    }
    setForm(s); setPos(resolvedPos);
    setSamples(Array.isArray(s.samples) ? s.samples : []);
    setObEff(s.obSettings?.eff || 75); setObHours(s.obSettings?.hours || 8);
    setIsEditing(s.id); setView("CREATE");
  };

  // ---- DELETE STYLE (archive if has data, hard-delete if empty) ----
  const handleDeleteStyle = async (s) => {
    const hasPOs       = (s.pos || []).length > 0 ||
                         (db.poSizeTable||[]).some(r => r.style_ref === s.styleRef);
    const hasProduction= (db.productionLogs || []).some(l => l.style === s.styleRef);
    const hasCosting   = (db.costings || []).some(co => co.styleId === s.id);
    const hasData      = hasPOs || hasProduction || hasCosting;

    if (hasData) {
      const dataDesc = [
        hasPOs        && `${(s.pos||[]).length || 'PO'} PO record(s)`,
        hasProduction && 'Production logs',
        hasCosting    && 'Costing sheet',
      ].filter(Boolean).join(', ');
      const choice = window.confirm(
        `"${s.styleRef}" has linked data:
  ${dataDesc}

` +
        `OK → ARCHIVE (hidden from gallery, all data preserved)
` +
        `Cancel → Do nothing (safe option)`
      );
      if (!choice) return;
      const archived = { ...s, status: 'Archived', archivedOn: new Date().toLocaleDateString() };
      setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(x => x.id === s.id ? archived : x) }));
      try {
        await dbSaveStyle(archived);
        notify(`"${s.styleRef}" archived successfully. Filter by Status → Archived to view it.`, 'info');
      } catch(e) { notify('Archive failed: ' + e.message, 'error'); }
      return;
    }
    if (!window.confirm(
      `Permanently delete "${s.styleRef}"?

This style has no linked POs, production, or costing data.
This action cannot be undone.`
    )) return;
    setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.filter(x => x.id !== s.id) }));
    try {
      await dbDeleteStyle(s.id);
      notify(`"${s.styleRef}" deleted permanently.`, 'success');
    } catch(e) { notify('Delete failed: ' + e.message, 'error'); }
  };

  // ---- CLONE STYLE ----
  const cloneStyle = (s) => {
    const clonedRef = `${s.styleRef}_COPY`;
    const cloned = {
      ...safeClone(s),
      styleRef: clonedRef,
      name: clonedRef,
      id: null,
      totalQty: 0,
      pos: [],
      samples: [],
      files: [],
      status: 'Development',
    };
    setForm(cloned);
    setPos([]);
    setSamples([]);
    setObEff(cloned.obSettings?.eff || 75);
    setObHours(cloned.obSettings?.hours || 8);
    setIsEditing(false);
    setView("CREATE");
    notify(`Cloned from "${s.styleRef}" — edit details and save as new style.`, 'info');
  };

  const handleFileUpload = async (e, fileType = 'OTHER') => {
    const file = e.target.files[0];
    if (file) {
      if (!targetStyle?.id) { notify('Error: open a style passport first', 'error'); return; }
      notify('Uploading ' + file.name + '...', 'info');
      try {
        const fileEntry = await uploadFileToServer(file, fileType, targetStyle.id);
        const updatedStyle = { ...targetStyle, files: [...(targetStyle.files || []), fileEntry] };
        setTargetStyle(updatedStyle);
        setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updatedStyle.id ? updatedStyle : s) }));
        notify(file.name + ' saved ✓', 'success');
      } catch(uploadErr) {
        console.error('File save error:', uploadErr);
        notify('Failed: ' + (uploadErr.message || 'check console F12'), 'error');
      }
      if (e.target) e.target.value = '';
    }
  };
  const handlePassportImageReplace = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      const updated = { ...targetStyle, img: base64 };
      // Update state immediately so image shows right away
      setTargetStyle(updated);
      setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updated.id ? updated : s) }));
      // Persist to DB
      await dbSaveStyle(updated);
      setShowImgUpload(false);
      notify('Image saved ✓', 'success');
    } catch(err) {
      notify('Image saved locally (DB sync failed: ' + (err.message||'check save_universal.php') + ')', 'info');
    }
    // Reset file input so same file can be re-selected
    if (e.target) e.target.value = '';
  };

  const deleteFile = async (id) => {
    if (view === 'PASSPORT') {
      const updatedStyle = { ...targetStyle, files: (targetStyle.files||[]).filter(f => f.id !== id) };
      setTargetStyle(updatedStyle);
      setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updatedStyle.id ? updatedStyle : s) }));
      dbSaveStyle(updatedStyle);
    } else { setForm(prev => ({ ...prev, files: prev.files.filter(f => f.id !== id) })); }
  };

  const resetForm = () => {
    setForm({ brand: "", styleRef: "", buyer: "", season: "", fabric: "", product: "", content: "", construction: "", sizeRange: "", img: null, tech: null, spec: null, status: "Development", measurements: null, bom: null, files: [], options: [], obSettings: { eff: 75, hours: 8 }, allocatedSuppliers: [] });
    setPos([]); setSamples([]); setIsEditing(false);
  };

  const saveStyle = async () => {
    if (isSaving) return;
    if (!form.styleRef || !form.brand) return alert("Style Ref and Brand are mandatory.");
    setIsSaving(true);
    setTimeout(async () => {
      try {
        const calculatedQty = pos.reduce((a, b) => a + (parseFloat(b.qty) || 0), 0);
        const existingWS = isEditing ? (db.mgmtStyles.find(x => x.id === isEditing)?.workstationData || null) : null;
        // id=0 for new styles → PHP does INSERT and returns real auto-increment id
        // id=isEditing for existing → PHP does UPDATE
        const entry = {
          ...form, pos, samples, files: form.files || [], options: form.options || [],
          id: isEditing || 0,
          totalQty: calculatedQty || parseFloat(form.totalQty) || 0,
          workstationData: existingWS, obSettings: { eff: obEff, hours: obHours }
        };
        const dbId = await dbSaveStyle(entry);
        const savedEntry = { ...entry, id: dbId || entry.id };
        // Always keep po_size_table in sync with pos[]
        if (pos.length > 0 && savedEntry.styleRef) {
          await savePOSizeTable(savedEntry.styleRef, pos);
        }
        setDb(prev => ({
          ...prev,
          mgmtStyles: isEditing
            ? prev.mgmtStyles.map(x => x.id === isEditing ? savedEntry : x)
            : [...(prev.mgmtStyles || []), savedEntry]
        }));
        notify(isEditing ? "Style Updated!" : "Style Created!", 'success');
        setIsSaving(false); resetForm(); setView("GALLERY");
      } catch (error) {
        console.error("Save Error:", error);
        notify("Save failed: " + (error.message || 'Check console'), 'error');
        setIsSaving(false);
      }
    }, 100);
  };

  // ══════════════════════════════════════════════════════
  // DB HELPERS — direct API calls, no prop dependency
  // ══════════════════════════════════════════════════════
  // ── Upload file — stores base64 in style.files[] via save_universal ────────
  const uploadFileToServer = async (file, fileType, styleId) => {
    const base64 = await fileToBase64(file);
    const fileEntry = {
      id:     'F' + Date.now(),
      name:   file.name,
      type:   fileType,
      mime:   file.type || 'application/octet-stream',
      size:   Math.round(file.size / 1024) + ' KB',
      date:   new Date().toLocaleDateString('en-GB'),
      source: 'MANUAL',
      data:   base64,   // full base64 data URL — stored in DB with style
    };
    // Save to style via save_universal (confirmed working)
    const currentStyle = db.mgmtStyles?.find(s => s.id === styleId) || targetStyle;
    const updatedFiles = [...(currentStyle.files || []).filter(f => f.id !== fileEntry.id), fileEntry];
    const updatedStyle = { ...currentStyle, files: updatedFiles };
    const res = await fetch(`${API_BASE}/save_universal.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category: 'style', data: updatedStyle })
    });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'Save failed');
    return fileEntry;  // return entry so UI can update immediately
  };

  // ── Delete file — removes from style.files[] and saves ──────────────────
  const deleteFileFromServer = async (fileId, styleId) => {
    const currentStyle = db.mgmtStyles?.find(s => s.id === styleId) || targetStyle;
    const updatedFiles = (currentStyle.files || []).filter(f => f.id !== fileId);
    const updatedStyle = { ...currentStyle, files: updatedFiles };
    await fetch(`${API_BASE}/save_universal.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category: 'style', data: updatedStyle })
    });
    return { success: true };
  };

  // ── loadStyleFiles not needed — files come from get_all_data with style ──
  const loadStyleFiles = React.useCallback(async (styleId) => {
    // Files stored in style.files[] — already loaded via get_all_data
    // This is a no-op kept for compatibility
  }, []);

  const dbSaveStyle = async (entry) => {
    const payload = { ...entry };
    if (!payload.id || payload.id > 2000000000) payload.id = 0;
    const res = await fetch(`${API_BASE}/save_universal.php`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ category: 'style', data: payload })
    });
    const json = await res.json();
    if (!json.success) throw new Error(json.error || 'DB save failed');
    return json.id;
  };

  const dbDeleteStyle = async (id) => {
    try {
      await fetch(`${API_BASE}/save_universal.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'style', data: { id, _deleted: true } })
      });
    } catch (e) { console.error('Delete style error:', e); }
  };

  // ---- PLANNING MODAL ----
  const savePlanUpdate = async (updatedProc) => {
    if (!editingProcess) return;
    const plans = [...(db.stylePlans || [])];
    const targetPlan = plans.find(p => p.id === editingProcess.planId);
    if (targetPlan) {
      targetPlan.processes[editingProcess.index] = updatedProc;
      setDb(prev => ({ ...prev, stylePlans: plans }));
      notify("Process Updated!", 'success');
    }
    setEditingProcess(null);
  };

  const PlanningModal = () => {
    if (!editingProcess) return null;
    const [localData, setLocalData] = useState({ ...editingProcess.process });
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
        <div style={{ background: 'white', width: '420px', padding: '25px', borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>{localData.name}</h3>
            <button onClick={() => setEditingProcess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.8rem', color: '#676879', display: 'block', marginBottom: '4px' }}>Status</label>
            <select style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e6e9ef' }} value={localData.approvalStatus} onChange={e => setLocalData({ ...localData, approvalStatus: e.target.value })}>
              <option>Pending</option><option>Submitted</option><option>Approved</option><option>Rejected</option>
            </select>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.8rem', color: '#676879', display: 'block', marginBottom: '4px' }}>Plan Date</label>
            <input type="date" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e6e9ef' }} value={localData.planDate || ''} onChange={e => setLocalData({ ...localData, planDate: e.target.value })} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '0.8rem', color: '#676879', display: 'block', marginBottom: '4px' }}>Actual Date</label>
            <input type="date" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e6e9ef' }} value={localData.actualDate || ''} onChange={e => setLocalData({ ...localData, actualDate: e.target.value })} />
          </div>
          <button onClick={() => savePlanUpdate(localData)} style={{ width: '100%', padding: '10px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>SAVE UPDATE</button>
        </div>
      </div>
    );
  };

  // ---- SUPPLIER OB UPLOAD IN PASSPORT ----
  const handleOBUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setObLoading(true);
    try {
      const data = await extractDocumentData(file, 'OB_SUPPLIER');
      if (!data.supplier_name) data.supplier_name = file.name.replace(/\.[^.]+$/, '');
      // Save to targetStyle
      const updatedOBs = [...(targetStyle.supplierOBs || []), data];
      const updated = { ...targetStyle, supplierOBs: updatedOBs };
      setTargetStyle(updated);
      setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updated.id ? updated : s) }));
      await dbSaveStyle(updated);
      setSupplierOBs(updatedOBs);
      notify(`OB extracted for ${data.supplier_name}`, 'success');
    } catch (err) {
      notify(`OB extraction failed: ${err.message}`, 'error');
    }
    setObLoading(false);
    e.target.value = '';
  };

  // ---- AI PRICE PREDICTION & TRAINING (PythonAnywhere) ----
  const handlePredict = async () => {
    try {
      const response = await fetch(`${AI_BASE}/predict`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: targetStyle.product || 'Shirt',
          gsm: 180,
          panels: 12,
          smv: targetStyle.workstationData?.smv || 15
        })
      });
      const result = await response.json();
      if (result.success) { setPredictedFob(result.predicted_fob); }
    } catch (e) { console.error("AI Predict Error:", e); }
  };

  const handleForceTrain = async () => {
    setObLoading(true);
    try {
      const response = await fetch(`${AI_BASE}/train`, { method: 'GET' });
      const result = await response.json();
      if (result.success) { alert("BRAIN UPDATED: AI has successfully learned from your latest database styles."); }
      else { alert("AI Error: " + (result.error || "Check PythonAnywhere logs")); }
    } catch (e) { alert("Restoration Failed: The brain is not responding."); }
    finally { setObLoading(false); }
  };

  const removeSupplierOB = (idx) => {
    const updated = { ...targetStyle, supplierOBs: (targetStyle.supplierOBs || []).filter((_, i) => i !== idx) };
    setTargetStyle(updated);
    setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updated.id ? updated : s) }));
    dbSaveStyle(updated);
  };

  // Passport document AI upload
  const handlePassportDocUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    notify(`Extracting ${docType} via AI...`, 'info');
    // ── Save original file to server before extraction ─────────────────────
    try {
      const result = await uploadFileToServer(file, docType, targetStyle.id);
      const fEntry = { id:result.fileId, name:file.name, url:result.url,
        type:docType, size:result.size, date:new Date().toLocaleDateString(), source:'AI_UPLOAD' };
      const withF = { ...targetStyle, files:[...(targetStyle.files||[]), fEntry] };
      setTargetStyle(withF);
      setDb(prev=>({...prev,mgmtStyles:prev.mgmtStyles.map(s=>s.id===withF.id?withF:s)}));
      // File is on server — no need to dbSaveStyle just for the file
    } catch(uploadErr) {
      // Fallback: store as base64 in style if server upload fails
      console.warn('Server upload failed, using base64 fallback:', uploadErr.message);
    }
    try {
      const data = await extractDocumentData(file, docType);
      let updated = { ...targetStyle };
      if (docType === 'PO') {
        const newPOs = (data.po_details || []).map((po, idx) => ({
          id: Date.now() + idx, num: po.po_no || "-", col: po.color || "Mix",
          qty: parseFloat(po.qty || 0), date: po.ex_fty_date || "", fob: po.fob_price || "", dest: po.destination || "",
          sizeBreakdown: po.size_breakdown || po.sizeBreakdown || ""
        }));
        updated.pos = [...(updated.pos || []), ...newPOs];
        // Save size breakdown to DB
        if (updated.styleRef) {
          await savePOSizeTable(updated.styleRef, updated.pos);
        }
      } else if (docType === 'TECHPACK') {
        const newItems = (data.bom || []).map(b => ({
          category:        b.category || b.section || 'General',
          item_name:       b.item_name || b.item || b.name || b.code || b.package_code || '',
          description:     b.description || b.material || b.spec || b.details || b.remarks || '',
          supplier:        b.supplier || b.vendor || '',
          reference:       b.reference || b.ref || b.item_code || b.mill_ref || '',
          consumption:     b.consumption || b.total_qty || '',
          placement:       b.placement || b.location || '',
          color_breakdown: b.color_breakdown || b.color || b.colour || '',
          size_variation:  b.size_variation || b.size_breakdown || '',
          comments:        b.comments || b.notes || b.remarks || '',
        }));
        const existingBom = updated.bom || [];
        const mergedBom = [...existingBom];
        newItems.forEach(item => {
          const isDupe = mergedBom.some(e =>
            (e.item_name || '').toLowerCase() === (item.item_name || '').toLowerCase() &&
            (e.category  || '').toLowerCase() === (item.category  || '').toLowerCase()
          );
          if (!isDupe) mergedBom.push(item);
        });
        updated.bom = mergedBom;
      } else if (docType === 'SPEC') {
        updated.measurements = data;
      }
      setTargetStyle(updated);
      setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updated.id ? updated : s) }));
      await dbSaveStyle(updated);
      notify(`${docType} data extracted and saved!`, 'success');
    } catch (err) {
      notify(`Extraction failed: ${err.message}`, 'error');
    }
    e.target.value = '';
  };

  // Passport BOM/Spec inline save
  const savePassportBOM = async (newBom) => {
    const updated = { ...targetStyle, bom: newBom };
    setTargetStyle(updated);
    setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updated.id ? updated : s) }));
    await dbSaveStyle(updated);
    notify('BOM saved!', 'success');
  };

  const savePassportSpec = async (newSpec) => {
    const updated = { ...targetStyle, measurements: newSpec };
    setTargetStyle(updated);
    setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updated.id ? updated : s) }));
    await dbSaveStyle(updated);
    notify('Spec saved!', 'success');
  };

  const savePassportPOs = async (newPos) => {
    const updated = { ...targetStyle, pos: newPos };
    setTargetStyle(updated);
    setDb(prev => ({ ...prev, mgmtStyles: prev.mgmtStyles.map(s => s.id === updated.id ? updated : s) }));
    await dbSaveStyle(updated);
    notify('PO data saved!', 'success');
  };

  // ---- STUDIO VIEW ----
  if (showVisualStudio) return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2147483647, background: 'white', display: 'flex', flexDirection: 'column', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <div style={{ padding:'10px 16px', background:'#1e1f2e', borderBottom:'1px solid #323338',
        display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
        <span style={{ fontWeight:'bold', color:'white', fontSize:'0.9rem', letterSpacing:'1px' }}>
          ✏ 3D WORKSTATION STUDIO
        </span>
        <div style={{display:'flex',gap:'8px'}}>
          <button onClick={async () => {
            // Save workstation data back to this style in DB
            try {
              const currentStyle = db.mgmtStyles?.find(s => s.id === targetStyle.id) || targetStyle;
              const withWS = { ...currentStyle };
              const updated = { ...targetStyle, workstationData: withWS.workstationData };
              setTargetStyle(updated);
              await dbSaveStyle(updated);
              notify('Workstation saved to style ✓', 'success');
            } catch(err) {
              notify('Save failed: ' + (err.message||'check save_universal.php'), 'error');
            }
          }} style={{ padding:'7px 16px', background:'#00a65e', color:'white', border:'none',
            borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'0.82rem' }}>
            💾 Save to Style
          </button>
          <button onClick={() => setShowVisualStudio(false)}
            style={{ padding:'7px 16px', background:'#e2445c', color:'white', border:'none',
              borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'0.82rem' }}>
            ✕ Close
          </button>
        </div>
      </div>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}><Workstation db={db} setDb={setDb} /></div>
    </div>
  );

  // ==========================================
  // VIEW D: DASHBOARD (FLIGHT BOARD)
  // ==========================================
  if (showDashboard) return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ background: '#1f1f1f', padding: '6px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <span style={{ color: '#93c5fd', fontWeight: 'bold', fontSize: '0.82rem', letterSpacing: '1px' }}>✈ STYLE STATUS BOARD</span>
        <button onClick={() => setShowDashboard(false)} style={{ padding:'5px 12px', background:'transparent', border:`1px solid ${T.border}`, borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', color:T.textSub }}>← Back to Gallery</button>
        <button onClick={toggleTheme} style={{ padding:'5px 12px', background: dark?'#1e3a8a':'#323338', border:'none', borderRadius:'6px', color:'#fff', cursor:'pointer', fontSize:'0.72rem', fontWeight:'bold' }}>
          {dark ? '☀ Light Mode' : '🌙 Dark Mode'}
        </button>
      </div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <FlightBoardDashboard
          db={db}
          getCostSummary={getCostSummary}
          getProdSummary={getProdSummary}
          format={format}
          quotations={quotations}
          productionLogs={productionLogs}
          plansList={plansList}
          dark={dark}
          T={T}
          toggleTheme={toggleTheme}
        />
      </div>
    </div>
  );

  // ==========================================
  // VIEW 1: GALLERY
  // ==========================================
  if (view === "GALLERY") {
    const sourceList = searchImage ? scoredStyles : stylesList;
    let filtered = sourceList.filter(s =>
      (filters.brand === "" || String(s.brand || "").toLowerCase().includes(filters.brand.toLowerCase())) &&
      (filters.style === "" || String(s.styleRef || "").toLowerCase().includes(filters.style.toLowerCase())) &&
      (filters.season === "" || String(s.season || "").toLowerCase().includes(filters.season.toLowerCase())) &&
      (filters.status === "" || String(s.status || "").toLowerCase().includes(filters.status.toLowerCase())) &&
      // Hide Archived unless user explicitly filters for them
      (filters.status.toLowerCase() === 'archived' || (s.status || '').toLowerCase() !== 'archived')
    );
    if (searchImage) filtered = filtered.filter(s => s.matchScore > 50);

    return (
      <div style={containerStyle}>
        <Toast message={notification} type={notifType} />
        {/* Gallery Header */}
        <div style={{ background: '#1f1f1f', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexShrink: 0, minHeight: '52px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flex: 1, overflowX: 'auto' }}>
            <span style={{ fontWeight: '800', color: 'white', fontSize: '0.9rem', letterSpacing: '2px', whiteSpace: 'nowrap', marginRight: '6px' }}>STYLE GALLERY</span>
            <select onChange={e => setFilters({ ...filters, brand: e.target.value })} style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #323338', background: '#323338', color: 'white', fontSize: '0.78rem', minWidth: '110px' }}>
              <option value="">All Brands</option>{brandsList.map(b => <option key={b.id}>{b.name}</option>)}
            </select>
            <select onChange={e => setFilters({ ...filters, season: e.target.value })} style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #323338', background: '#323338', color: 'white', fontSize: '0.78rem', minWidth: '100px' }}>
              <option value="">All Seasons</option>{seasonsList.map(b => <option key={b.id}>{b.name}</option>)}
            </select>
            <select onChange={e => setFilters({ ...filters, status: e.target.value })} style={{ padding: '5px 8px', borderRadius: '6px', border: '1px solid #323338', background: '#323338', color: 'white', fontSize: '0.78rem', minWidth: '100px' }}>
              <option value="">All Status</option><option>Development</option><option>Production</option><option>Shipped</option><option>Hold</option><option>Archived</option>
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#323338', border: '1px solid #323338', borderRadius: '6px', paddingRight: '6px' }}>
              {searchImage && (<div style={{ display: 'flex', alignItems: 'center' }}><img src={searchImage} style={{ width: '24px', height: '24px', objectFit: 'cover' }} alt="" /><span onClick={clearSearch} style={{ cursor: 'pointer', color: '#f87171', padding: '0 4px' }}>✕</span></div>)}
              <input type="text" placeholder="Search styles..." value={filters.style} onChange={e => setFilters({ ...filters, style: e.target.value })} style={{ border: 'none', background: 'transparent', outline: 'none', padding: '5px 8px', width: '160px', color: 'white', fontSize: '0.78rem' }} />
              <label style={{ cursor: 'pointer', color: '#60a5fa' }} title="Visual Image Search">
                <input type="file" hidden onChange={handleSearchUpload} accept="image/*" /><Upload size={13} />
              </label>
            </div>
            {isAnalyzing && <span style={{ fontSize: '0.72rem', color: '#60a5fa', whiteSpace: 'nowrap' }}>Analyzing...</span>}
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={handleForceTrain}
              disabled={obLoading}
              style={{ padding: '7px 16px', background: '#1f1f1f', color: '#38bdf8', border: '1px solid #38bdf8', borderRadius: '5px', cursor: obLoading ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
            >
              {obLoading ? "TRAINING..." : "FORCE TRAIN AI"}
            </button>
            <button onClick={() => setShowDashboard(true)} style={{ padding: '7px 14px', background: '#0d1b2a', color: '#93c5fd', border: '1px solid #1e40af', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '5px' }}>✈ STATUS BOARD</button>
            <button onClick={() => { resetForm(); setView("CREATE"); }} style={{ padding: '7px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>+ NEW STYLE</button>
          </div>
        </div>

        <div id="main-scroll-container" style={{ ...scrollableContentStyle, background: '#f5f6f8' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
            <div style={{ fontSize: '0.78rem', color: '#676879' }}>{filtered.length} style{filtered.length !== 1 ? 's' : ''} found</div>
            <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.78rem' }}>
              <span style={{ color:'#676879' }}>Display Currency:</span>
              <select value={localCurrencyName || activeCurrencyName}
                onChange={e => setDisplayCurrency(e.target.value)}
                style={{ padding:'4px 8px', border:'1px solid #c5c7d4', borderRadius:'6px', fontSize:'0.78rem', color:'#323338', background:'#fff', cursor:'pointer' }}>
                {activeCurrencyList.length > 0
                  ? activeCurrencyList.map(c => <option key={c.name} value={c.name}>{c.name} (rate: {c.rate})</option>)
                  : <option value="USD">USD</option>}
              </select>
              {activeCurrencyName !== baseCurrency?.name && (
                <span style={{ fontSize:'0.7rem', color:'#00a65e', fontWeight:'bold' }}>
                  ×{conversionFactor.toFixed(4)} from {baseCurrency?.name}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' }}>
            {filtered.map(s => {
              if (!s || !s.id) return null;
              const cost = getCostSummary(s.id);
              const prod = getProdSummary(s.styleRef);
              const supplier = (() => {
                const q = quotations.find(q => q.name === s.styleRef && q.status === 'Selected')?.supplier;
                if (q) return q;
                const al = (db.allocations || []).find(l => l.style === s.styleRef);
                if (al) {
                  const d = al.factory || al.factoryName || al.supplierName || '';
                  if (d) return d;
                  const fId = al.factoryId || al.factory_id || '';
                  if (fId) { const f = (db.factories||[]).find(x=>String(x.id)===String(fId)); if(f?.name) return f.name; }
                }
                const alLine = (s.allocatedLines||[]).find(l=>l.factory||l.factoryName||l.factoryId);
                if (alLine) {
                  const dn = alLine.factory||alLine.factoryName||''; if(dn) return dn;
                  const fId2=alLine.factoryId||alLine.factory_id||''; if(fId2){const f2=(db.factories||[]).find(x=>String(x.id)===String(fId2));if(f2?.name)return f2.name;}
                }
                return "—";
              })();
              const exFty = s.pos?.length > 0 ? [...s.pos].sort((a, b) => new Date(a.date) - new Date(b.date))[0].date : "TBD";
              // SMV: workstation > workstationData.smv > cost.smv > manualSmv
              const _wsAggSmvCard = (s.workstationData?.aggregatedOps||[]).reduce((a,b)=>a+(parseFloat(b.smv)||0),0);
              const smv = _wsAggSmvCard || parseFloat(s.workstationData?.smv) || parseFloat(cost?.smv) || parseFloat(s.manualSmv) || 0;

              return (
                <div key={s.id} style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', cursor: 'pointer', border: '1px solid #e6e9ef', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', position: 'relative', transition: 'box-shadow 0.2s, transform 0.2s' }}
                  onClick={() => { setTargetStyle(s); setView("PASSPORT"); loadStyleFiles(s.id); setPassportTab('overview'); }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {/* Image Area */}
                  <div style={{ height: '200px', background: '#f5f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', borderBottom: '1px solid #f0f1f4', overflow: 'hidden' }}>
                    {s.workstationData?.items?.length > 0
                      ? <WorkstationView data={s.workstationData} />
                      : (s.img ? (
                          <>
                            <img src={s.img} style={{
                                maxWidth:'100%', maxHeight:'100%',
                                width:'auto', height:'auto',
                                objectFit:'contain',
                                display:'block', margin:'auto',
                                padding:'4px'
                              }} alt="Style" />
                                                      </>
                        ) : (
                          <div style={{ color: '#cbd5e1', fontSize: '0.8rem', textAlign: 'center' }}><ImageIcon size={32} color="#e6e9ef" /><br />NO IMAGE</div>
                        ))}
                    <span style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(255,255,255,0.95)', padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 'bold', border: '1px solid #e6e9ef', color: '#323338' }}>{s.status || "New"}</span>
                    {searchImage && s.matchScore > 0 && (
                      <div style={{ position: 'absolute', bottom: 8, left: 8, background: s.matchScore > 90 ? '#00a65e' : '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '8px', fontSize: '0.68rem', fontWeight: 'bold' }}>{s.matchScore.toFixed(0)}% match</div>
                    )}
                    {/* Hover overlay */}
                    <div className="sr-hover-overlay" style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.9)', color: 'white', padding: '14px', display: 'none', flexDirection: 'column', justifyContent: 'center', fontSize: '0.8rem' }}>
                      <div style={{ marginBottom: '6px' }}><span style={{ color: '#9699a6' }}>Supplier: </span><strong>{supplier}</strong></div>
                      <div style={{ marginBottom: '6px' }}><span style={{ color: '#9699a6' }}>FOB: </span><strong style={{ color: '#4ade80' }}>{cost ? format(cost.fob) : "—"}</strong></div>
                      <div style={{ marginBottom: '6px' }}><span style={{ color: '#9699a6' }}>SMV: </span><strong>{smv.toFixed(2)}</strong></div>
                      <div style={{ marginBottom: '6px' }}><span style={{ color: '#9699a6' }}>Ex-Fty: </span><strong>{exFty}</strong></div>
                      <div style={{ borderTop: '1px solid #323338', paddingTop: '8px', marginTop: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '0.72rem' }}>
                        <div><span style={{ color: '#9699a6' }}>Total: </span><strong>{(s.totalQty || 0).toLocaleString()}</strong></div>
                        <div><span style={{ color: '#9699a6' }}>Cut: </span><strong style={{ color: '#f59e0b' }}>{prod.cut}</strong></div>
                        <div><span style={{ color: '#9699a6' }}>Sew: </span><strong style={{ color: '#3b82f6' }}>{prod.sew}</strong></div>
                        <div><span style={{ color: '#9699a6' }}>Fin: </span><strong style={{ color: '#8b5cf6' }}>{prod.pack}</strong></div>
                        <div><span style={{ color: '#9699a6' }}>Ship: </span><strong style={{ color: '#4ade80' }}>{prod.ship}</strong></div>
                      </div>
                    </div>
                  </div>
                  {/* Card Info */}
                  <div style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#1f1f1f', fontSize: '0.95rem', letterSpacing: '0.5px' }}>{s.styleRef}</div>
                        <div style={{ fontSize: '0.75rem', color: '#676879', marginTop: '2px' }}>{s.brand} · {s.season}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#00a65e' }}>{cost ? format(cost.fob) : "—"}</div>
                        <div style={{ fontSize: '0.68rem', color: '#9699a6' }}>Qty: {(s.totalQty || 0).toLocaleString()}</div>
                      </div>
                    </div>
                    <div style={{ marginTop: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '0.7rem', color: '#676879' }}>SMV: <strong>{smv > 0 ? smv.toFixed(2) : '—'}</strong></div>
                      {supplier !== '—' && <div style={{ fontSize: '0.68rem', color: '#0073ea', fontWeight: 'bold' }}>📦 {supplier}</div>}
                    </div>
                    <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {s.buyer && <span style={{ background: '#f0f1f4', color: '#676879', padding: '1px 6px', borderRadius: '6px', fontSize: '0.68rem' }}>{s.buyer}</span>}
                      {s.fabric && <span style={{ background: '#f0f1f4', color: '#676879', padding: '1px 6px', borderRadius: '6px', fontSize: '0.68rem' }}>{s.fabric}</span>}
                    </div>
                  </div>
                  {/* Action Row */}
                  <div style={{ padding: '6px 12px 10px', display: 'flex', gap: '6px', borderTop: '1px solid #f5f6f8' }}>
                    <button onClick={e => { e.stopPropagation(); editStyle(s); }} style={{ flex: 1, padding: '4px', fontSize: '0.72rem', background: '#f0f1f4', border: '1px solid #e6e9ef', borderRadius: '6px', cursor: 'pointer', color: '#323338' }}>Edit</button>
                    <button onClick={e => { e.stopPropagation(); cloneStyle(s); }} style={{ flex: 1, padding: '4px', fontSize: '0.72rem', background: '#e8f0ff', border: '1px solid #a8d1ff', borderRadius: '6px', cursor: 'pointer', color: '#1e40af' }}>Clone</button>
                    <button onClick={e => { e.stopPropagation(); handleDeleteStyle(s); }} style={{ padding: '4px 8px', fontSize: '0.72rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', color: '#e2445c' }}>Del</button>
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 20px', color: '#9699a6' }}>
                <Search size={40} color="#e6e9ef" style={{ marginBottom: '12px', display: 'block', margin: '0 auto 12px' }} />
                No styles found. {filters.style || filters.brand ? "Try adjusting filters." : "Create your first style."}
              </div>
            )}
          </div>
          <div style={{ height: '80px' }} />
        </div>
        <NavigationArrows />
        <style>{`.sr-hover-overlay { display: none !important; } div:hover > div > .sr-hover-overlay { display: flex !important; }`}</style>
      </div>
    );
  }

  // ==========================================
  // PO EXTRACTION REVIEW MODAL
  // ==========================================
  const POExtractionReviewModal = () => {
    const [reviewPOs, setReviewPOs] = React.useState(()=>(pendingExtractedPOs||[]).map(po=>({...po})));
    if (!pendingExtractedPOs) return null;
    const upd=(idx,field,val)=>setReviewPOs(prev=>prev.map((po,i)=>i===idx?{...po,[field]:val}:po));
    const confirmSave=()=>{setPos(prev=>{const updated=[...prev,...reviewPOs];if(form.styleRef)savePOSizeTable(form.styleRef,updated);return updated;});setPendingExtractedPOs(null);notify(`${reviewPOs.length} PO line(s) saved.`,'success');};
    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
        <div style={{background:'white',borderRadius:'12px',padding:'24px',maxWidth:'920px',width:'95vw',maxHeight:'88vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'}}>
          <div style={{marginBottom:'16px'}}>
            <div style={{fontWeight:'bold',fontSize:'1rem',color:'#323338'}}>📋 Review Extracted PO Data — Edit Before Saving</div>
            <div style={{fontSize:'0.8rem',color:'#676879',marginTop:'6px',lineHeight:'1.5'}}>AI read the data below from the document.<br/><span style={{color:'#c93652',fontWeight:'bold'}}>⚠ If you used a REFERENCE document</span> (e.g. Dennis Lingo PO for size structure), correct the PO #, Color to match the actual style's PO. Nothing saves until you click Confirm &amp; Save.</div>
          </div>
          {reviewPOs.map((po,idx)=>(
            <div key={idx} style={{border:'2px solid #a8d1ff',borderRadius:'8px',padding:'14px',marginBottom:'12px',background:'#f5f6f8'}}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(175px,1fr))',gap:'10px',marginBottom:'10px'}}>
                {[{label:'PO NUMBER *',field:'num',bold:true,bc:'#3b82f6',ph:'e.g. DL/INT/120426/BLU'},{label:'COLOR *',field:'col',bold:true,bc:'#3b82f6',ph:'e.g. Blue'},{label:'TOTAL QTY',field:'qty',type:'number',ph:'5000'},{label:'FOB',field:'fob',ph:'355.00'},{label:'EX-FACTORY DATE',field:'date',ph:'2026-06-30'},{label:'DESTINATION',field:'dest',ph:'Bangalore'}].map(({label,field,bold,bc,ph,type})=>(
                  <div key={field}>
                    <div style={{fontSize:'0.65rem',color:'#676879',marginBottom:'3px',textTransform:'uppercase'}}>{label}</div>
                    <input type={type||'text'} value={po[field]||''} placeholder={ph} onChange={e=>upd(idx,field,type==='number'?(parseFloat(e.target.value)||0):e.target.value)} style={{width:'100%',padding:'6px 8px',border:`2px solid ${bc||'#e6e9ef'}`,borderRadius:'5px',fontSize:'0.82rem',fontWeight:bold?'bold':'normal',boxSizing:'border-box'}}/>
                  </div>
                ))}
              </div>
              <div>
                <div style={{fontSize:'0.65rem',color:'#676879',marginBottom:'3px',textTransform:'uppercase'}}>SIZE BREAKDOWN (S:100,M:200,L:300)</div>
                <input value={po.sizeBreakdown||''} onChange={e=>upd(idx,'sizeBreakdown',e.target.value)} style={{width:'100%',padding:'6px 8px',border:'1px solid #e6e9ef',borderRadius:'5px',fontSize:'0.82rem',fontFamily:'monospace',boxSizing:'border-box'}}/>
                {po.sizeBreakdown&&<div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginTop:'6px'}}>{po.sizeBreakdown.split(',').filter(Boolean).map((s,i)=>{const[sz,q]=s.split(':');return sz?<span key={i} style={{background:'#e8f0ff',color:'#0062c4',padding:'2px 8px',borderRadius:'10px',fontSize:'0.72rem',fontWeight:'bold'}}>{sz.trim()}: {q?.trim()||'?'}</span>:null;})}</div>}
              </div>
              {reviewPOs.length>1&&<button onClick={()=>setReviewPOs(prev=>prev.filter((_,i)=>i!==idx))} style={{marginTop:'8px',background:'none',border:'1px solid #fca5a5',color:'#c93652',borderRadius:'4px',padding:'3px 10px',cursor:'pointer',fontSize:'0.72rem'}}>🗑 Remove row</button>}
            </div>
          ))}
          <div style={{display:'flex',gap:'10px',justifyContent:'flex-end',marginTop:'8px',paddingTop:'12px',borderTop:'1px solid #e6e9ef'}}>
            <button onClick={()=>setPendingExtractedPOs(null)} style={{padding:'8px 18px',background:'#f0f1f4',border:'1px solid #e6e9ef',borderRadius:'6px',cursor:'pointer',fontSize:'0.82rem',color:'#676879'}}>Discard</button>
            <button onClick={confirmSave} style={{padding:'8px 24px',background:'#00a65e',color:'white',border:'none',borderRadius:'6px',cursor:'pointer',fontWeight:'bold',fontSize:'0.82rem'}}>✓ Confirm &amp; Save to "{form.styleRef||'Style'}"</button>
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // VIEW 2: CREATE / EDIT
  // ==========================================
  if (view === "CREATE") {
    return (
      <div style={containerStyle}>
        <Toast message={notification} type={notifType} />
        <POExtractionReviewModal />
        <div style={{ background: '#1f1f1f', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '0.9rem', fontWeight: '800', letterSpacing: '1px' }}>{isEditing ? `EDIT: ${form.styleRef}` : "CREATE NEW STYLE"}</h3>
          <button type="button" onClick={() => setView("GALLERY")} style={{ padding: '6px 14px', background: '#323338', color: '#9699a6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>← CANCEL</button>
        </div>

        <div id="main-scroll-container" style={scrollableContentStyle}>
          {/* Basic Info Card */}
          <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '10px', padding: '18px', marginBottom: '16px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#676879', letterSpacing: '1px', marginBottom: '14px' }}>BASIC INFORMATION</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
              {[
                { label: 'Brand *', field: 'brand', type: 'select', options: brandsList },
                { label: 'Style Ref *', field: 'styleRef', type: 'text' },
                { label: 'Buyer', field: 'buyer', type: 'select', options: buyersList },
                { label: 'Season', field: 'season', type: 'select', options: seasonsList },
                { label: 'Fabric', field: 'fabric', type: 'select', options: fabricsList },
                { label: 'Product', field: 'product', type: 'select', options: productsList },
                { label: 'Status', field: 'status', type: 'select', options: [{ name: 'Development' }, { name: 'Production' }, { name: 'Shipped' }, { name: 'Hold' }, { name: 'Cancelled' }] },
                { label: 'Size Range', field: 'sizeRange', type: 'text' },
              ].map(({ label, field, type, options }) => (
                <div key={field} className="field">
                  <label style={{ fontSize: '0.72rem', color: '#676879', display: 'block', marginBottom: '4px' }}>{label}</label>
                  {type === 'select'
                    ? <select value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })} style={{ width: '100%', padding: '6px 8px', borderRadius: '5px', border: '1px solid #e6e9ef', fontSize: '0.82rem' }}>
                      <option value="">--Select--</option>
                      {/* If the style's saved value isn't in the masters list (e.g. 'ss25' not added to Seasons master yet),
                          show it as an amber option so it doesn't silently disappear from the dropdown */}
                      {form[field] && !(options||[]).some(o => o.name === form[field]) && (
                        <option key="__current__" value={form[field]} style={{ color:'#fdab3d' }}>{form[field]} ⚠ (add to masters)</option>
                      )}
                      {(options || []).map(b => <option key={b.id || b.name} value={b.name}>{b.name}</option>)}
                    </select>
                    : <input value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })} style={{ width: '100%', padding: '6px 8px', borderRadius: '5px', border: '1px solid #e6e9ef', fontSize: '0.82rem', boxSizing: 'border-box' }} />
                  }
                </div>
              ))}
              {/* Live Total Qty — computed from POs, or manual if no POs */}
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: pos.length > 0 ? '#f0fdf4' : '#f5f6f8', border: `1px solid ${pos.length > 0 ? '#86efac' : '#e6e9ef'}`, borderRadius: '6px', marginTop: '4px' }}>
                <span style={{ fontSize: '0.72rem', color: '#676879', fontWeight: 'bold' }}>TOTAL QTY:</span>
                {pos.length > 0 ? (
                  <>
                    <span style={{ fontSize: '1rem', fontWeight: 'bold', color: '#00a65e' }}>
                      {pos.reduce((a, b) => a + (parseFloat(b.qty) || 0), 0).toLocaleString()} pcs
                    </span>
                    <span style={{ fontSize: '0.7rem', color: '#676879' }}>← computed from {pos.length} PO(s) · locked</span>
                    {form.totalQty && parseFloat(form.totalQty) !== pos.reduce((a,b)=>a+(parseFloat(b.qty)||0),0) && (
                      <span style={{ fontSize: '0.7rem', color: '#f59e0b', marginLeft: '8px' }}>
                        ⚠ Previously stored {parseFloat(form.totalQty).toLocaleString()} — will update to PO sum on save
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <input type="number" value={form.totalQty || ''} onChange={e => setForm({...form, totalQty: e.target.value})}
                      placeholder="Enter total order qty"
                      style={{ width: '130px', padding: '4px 8px', border: '2px solid #f59e0b', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 'bold' }} />
                    <span style={{ fontSize: '0.7rem', color: '#f59e0b' }}>⚠ No POs added — enter manually or use AI Extract PO</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            {/* PO Matrix */}
            <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '10px', padding: '18px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#1e40af', letterSpacing: '1px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Package size={14} /> PO MATRIX
                <label style={{ marginLeft: 'auto', cursor: 'pointer', background: '#e8f0ff', border: '1px solid #a8d1ff', color: '#1e40af', padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem' }}>
                  <input type="file" hidden onChange={e => handleSmartUpload(e, 'PO', null)} /> AI Extract PO
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1.5fr auto', gap: '6px', marginBottom: '10px', alignItems: 'end' }}>
                {[['PO #', 'poN'], ['Color', 'poC'], ['Qty', 'poQ', 'number'], ['Date', 'poD', 'date'], ['FOB', 'poFob', 'number'], ['Dest', 'poDest']].map(([lbl, key, tp]) => (
                  <div key={key}>
                    <label style={{ fontSize: '0.68rem', color: '#676879', display: 'block', marginBottom: '2px' }}>{lbl}</label>
                    <input type={tp || 'text'} value={inputs[key] || ''} onChange={e => setInputs({ ...inputs, [key]: e.target.value })} style={{ width: '100%', padding: '5px 6px', borderRadius: '6px', border: '1px solid #e6e9ef', fontSize: '0.78rem', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize: '0.68rem', color: '#676879', display: 'block', marginBottom: '2px' }}>Size Breakdown</label>
                  <input type="text" placeholder="S:10,M:20,L:30" value={inputs.poSB || ''} onChange={e => setInputs({ ...inputs, poSB: e.target.value })} style={{ width: '100%', padding: '5px 6px', borderRadius: '6px', border: '1px solid #a8d1ff', fontSize: '0.78rem', boxSizing: 'border-box' }} title="Format: S:qty,M:qty,L:qty  (per color)" />
                </div>
                <button onClick={addPO} style={{ padding: '5px 10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', alignSelf: 'flex-end' }}>+</button>
              </div>
              <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #f0f1f4', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead><tr style={{ background: '#f5f6f8' }}>{['PO#', 'Color', 'Qty', 'Date', 'FOB', 'Dest', 'Sizes', ''].map(h => <th key={h} style={{ padding: '4px 6px', textAlign: 'left', borderBottom: '1px solid #e6e9ef', color: '#676879', fontSize: '0.7rem' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {pos.length === 0 && (() => {
                      const hasPOTable = (db.poSizeTable||[]).some(r=>r.style_ref===(form.styleRef||''));
                      return (
                        <tr><td colSpan={8} style={{ textAlign:'center', padding:'12px', color: hasPOTable ? '#f59e0b' : '#9699a6', fontSize:'0.78rem' }}>
                          {hasPOTable
                            ? '⚠ PO data exists in database but not loaded — click CANCEL then EDIT again to reload'
                            : 'No POs added — use AI Extract PO or enter manually below'}
                        </td></tr>
                      );
                    })()}
                    {pos.map((p, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f5f6f8' }}>
                        <td style={{ padding: '4px 6px' }}><strong>{p.num}</strong></td>
                        <td style={{ padding: '4px 6px' }}>{p.col}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right' }}>{p.qty?.toLocaleString()}</td>
                        <td style={{ padding: '4px 6px' }}>{p.date}</td>
                        <td style={{ padding: '4px 6px', color: '#00a65e' }}>{p.fob}</td>
                        <td style={{ padding: '4px 6px', fontSize: '0.7rem', color: '#676879' }}>{p.dest}</td>
                        <td style={{ padding: '4px 6px', fontSize: '0.68rem', color: '#7c3aed', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={typeof p.sizeBreakdown === 'string' ? p.sizeBreakdown : JSON.stringify(p.sizeBreakdown)}>{p.sizeBreakdown ? (typeof p.sizeBreakdown === 'string' ? p.sizeBreakdown : '✓') : <span style={{ color: '#e6e9ef' }}>—</span>}</td>
                        <td style={{ padding: '4px 6px' }}><button onClick={() => deletePO(i)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={11} color="#e2445c" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pos.length > 0 && <div style={{ padding: '6px 8px', background: '#f0fdf4', borderTop: '1px solid #e3faf0', fontSize: '0.75rem', fontWeight: 'bold', color: '#00a65e' }}>Total: {pos.reduce((a, b) => a + (parseFloat(b.qty) || 0), 0).toLocaleString()} pcs</div>}
              </div>
            </div>

            {/* Documents & AI */}
            <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '10px', padding: '18px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#7c3aed', letterSpacing: '1px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FileText size={14} /> DOCUMENTS & AI PARSING
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#676879', display: 'block', marginBottom: '4px' }}>Style Image</label>
                  <input type="file" accept="image/*" onChange={e => handleSmartUpload(e, 'IMG', 'img')} style={{ width: '100%', fontSize: '0.72rem' }} />
                  {form.img && <img src={form.img} style={{ width: '60px', height: '60px', marginTop: '6px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e6e9ef' }} alt="Preview" />}
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Techpack (BoM) – AI</label>
                  <input type="file" onChange={e => handleSmartUpload(e, 'TECHPACK', 'tech')} style={{ width: '100%', fontSize: '0.72rem' }} />
                  {form.bom && <div style={{ fontSize: '0.68rem', color: '#7c3aed', marginTop: '4px' }}><CheckCircle size={10} style={{ display: 'inline' }} /> {form.bom.length} BOM items extracted</div>}
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Spec Sheet – AI</label>
                  <input type="file" onChange={e => handleSmartUpload(e, 'SPEC', 'spec')} style={{ width: '100%', fontSize: '0.72rem' }} />
                  {form.measurements && <div style={{ fontSize: '0.68rem', color: '#7c3aed', marginTop: '4px' }}><CheckCircle size={10} style={{ display: 'inline' }} /> {form.measurements.rows?.length || 0} rows extracted</div>}
                </div>
              </div>

              {/* Samples */}
              <div style={{ borderTop: '1px solid #f0f1f4', paddingTop: '12px' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#676879', marginBottom: '8px' }}>SAMPLES</div>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', alignItems: 'flex-end' }}>
                  {[['Type', 'type'], ['Qty', 'qty'], ['Date', 'date', 'date']].map(([lbl, key, tp]) => (
                    <div key={key} style={{ flex: 1 }}>
                      <label style={{ fontSize: '0.68rem', color: '#676879', display: 'block', marginBottom: '2px' }}>{lbl}</label>
                      <input type={tp || 'text'} value={sampIn[key] || ''} onChange={e => setSampIn({ ...sampIn, [key]: e.target.value })} style={{ width: '100%', padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.75rem', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                  <button onClick={addSample} style={{ padding: '5px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>+</button>
                </div>
                <div style={{ maxHeight: '80px', overflowY: 'auto', fontSize: '0.75rem' }}>
                  {samples.map((s, i) => <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #f5f6f8' }}><span>{s.type}</span><span>{s.qty} pcs</span><span style={{ color: '#676879' }}>{s.date}</span></div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Extracted Data Preview */}
          {(form.measurements || form.bom) && (
            <div style={{ background: '#fafafa', border: '1px solid #e6e9ef', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '0.75rem', color: '#7c3aed', marginBottom: '10px' }}>EXTRACTED DATA PREVIEW</div>
              <div style={{ display: 'flex', gap: '20px' }}>
                {form.measurements?.rows && (
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '6px', color: '#323338' }}>SPEC SHEET ({form.measurements.rows.length} rows)</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ fontSize: '0.72rem', borderCollapse: 'collapse', width: '100%' }}>
                        <thead><tr style={{ background: '#f0f1f4' }}>{(form.measurements.headers || []).map((h, i) => <th key={i} style={{ padding: '3px 6px', border: '1px solid #e6e9ef', fontWeight: '600' }}>{h}</th>)}</tr></thead>
                        <tbody>{(form.measurements.rows || []).slice(0, 5).map((row, i) => <tr key={i}>{(form.measurements.headers || []).map((h, j) => <td key={j} style={{ padding: '2px 6px', border: '1px solid #f0f1f4', textAlign: j > 0 ? 'center' : 'left' }}>{row[h] || row.pom || '-'}</td>)}</tr>)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
                {form.bom && form.bom.length > 0 && (
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 'bold', marginBottom: '6px', color: '#6d28d9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>BOM — {form.bom.length} items · {[...new Set(form.bom.map(b => b.category).filter(Boolean))].join(', ')}</span>
                      <button onClick={() => setForm(p => ({...p, bom: null}))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9699a6', fontSize: '0.7rem' }}>✕ Clear</button>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: '180px', overflowY: 'auto', border: '1px solid #e9d5ff', borderRadius: '5px' }}>
                      <table style={{ fontSize: '0.7rem', borderCollapse: 'collapse', width: '100%' }}>
                        <thead>
                          <tr style={{ background: '#f3e8ff', position: 'sticky', top: 0 }}>
                            {['Section','Item / Code','Description / Material','Supplier','Ref','Qty','Placement','Colours','Comments'].map(h => (
                              <th key={h} style={{ padding: '4px 7px', border: '1px solid #e9d5ff', fontWeight: '700', textAlign: 'left', whiteSpace: 'nowrap', color: '#6d28d9', fontSize: '0.65rem' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {form.bom.map((b, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fdf4ff', borderBottom: '1px solid #f3e8ff' }}>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', fontWeight: '700', color: '#6d28d9', whiteSpace: 'nowrap', fontSize: '0.67rem' }}>{b.category || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', fontWeight: '700', color: '#323338' }}>{b.item_name || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', maxWidth: '160px', color: '#323338' }}>{b.description || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', color: '#676879' }}>{b.supplier || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', color: '#676879', fontSize: '0.65rem' }}>{b.reference || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff' }}>{b.consumption || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', maxWidth: '160px', color: '#323338' }}>{b.placement || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', color: '#7c3aed', fontSize: '0.65rem' }}>{b.color_breakdown || '—'}</td>
                              <td style={{ padding: '3px 7px', border: '1px solid #f3e8ff', color: '#676879', maxWidth: '140px', fontSize: '0.65rem' }}>{b.comments || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ fontSize: '0.67rem', color: '#9699a6', marginTop: '4px' }}>
                      Upload next page to add more rows — existing data is preserved.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ textAlign: 'right', marginBottom: '16px' }}>
            <button type="button" onClick={saveStyle} disabled={isSaving} style={{ padding: '12px 32px', background: isSaving ? '#9699a6' : '#00a65e', color: 'white', border: 'none', borderRadius: '8px', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '0.9rem', letterSpacing: '0.5px' }}>
              {isSaving ? "SAVING..." : (isEditing ? "UPDATE STYLE" : "SAVE STYLE")}
            </button>
          </div>
          <div style={{ height: '60px' }} />
        </div>
        <NavigationArrows />
      </div>
    );
  }

  // ==========================================
  // VIEW 3: PASSPORT
  // ==========================================
  if (view === "PASSPORT" && targetStyle) {
    // Augment targetStyle.pos from poSizeTable if style.pos is empty
    // This is the single fix that makes passport always show real PO data
    const _augmentedStyle = (() => {
      if ((targetStyle.pos || []).length > 0) return targetStyle;
      const styleRef = targetStyle.styleRef || targetStyle.name;
      const dbNums = [...new Set((db.poSizeTable || []).filter(r => r.style_ref === styleRef).map(r => r.po_no))];
      if (dbNums.length === 0) return targetStyle;
      const rebuiltPos = dbNums.map(pn => {
        const rows = (db.poSizeTable || []).filter(r => r.style_ref === styleRef && r.po_no === pn);
        const qty  = rows.reduce((a, r) => a + (parseInt(r.qty) || 0), 0);
        const cols = [...new Set(rows.map(r => r.color).filter(Boolean))].join('/');
        const szStr= rows.filter(r => r.size && r.size !== 'ALL').map(r => `${r.size}:${r.qty}`).join(',');
        return { num: pn, col: cols, qty, date: rows[0]?.ex_fty_date || '', fob: rows[0]?.fob || '', dest: rows[0]?.destination || '', sizeBreakdown: szStr };
      });
      const rebuiltTotal = rebuiltPos.reduce((a, p) => a + (parseFloat(p.qty) || 0), 0);
      return { ...targetStyle, pos: rebuiltPos, totalQty: targetStyle.totalQty || rebuiltTotal };
    })();
    // ts = augmented targetStyle (never redeclare targetStyle — causes TDZ error)
    const ts = _augmentedStyle;
    const cost = getCostSummary(ts.id);
    const prodSummary = getProdSummary(ts.styleRef);
    const ops = ts.workstationData?.aggregatedOps || [];
    const poNums = (ts.pos || []).map(p => p.num).filter(Boolean);

    // Best-match GANTT plan (prefer brand match + has processes + has dates)
    const _matchPlans = plansList.filter(p => p.style===ts.styleRef || p.styleRef===ts.styleRef || poNums.some(pn=>p.style===pn));
    const _score = (p) => ((p.processes?.length||0)*10) + (p.brand===ts.brand?50:0) + ((p.exFtyDate||p.sewingStart||p.cutDate||p.ex_fty_date||p.sewing_start)?5:0) + (p.brand?2:0);
    const activePlan = _matchPlans.length ? [..._matchPlans].sort((a,b)=>_score(b)-_score(a))[0] : null;
    const planProcesses = activePlan ? (activePlan.processes || []) : [];
    const planKeyDates = activePlan ? {
      exFtyDate:activePlan.exFtyDate||activePlan.ex_fty_date||activePlan.exFty||'',
      sewingStart:activePlan.sewingStart||activePlan.sewing_start||activePlan.sewStart||'',
      cutDate:activePlan.cutDate||activePlan.cut_date||activePlan.pcd||'',
      finEnd:activePlan.finEnd||activePlan.fin_end||activePlan.finishEnd||'',
    } : null;
    const hasPlanKeyDates = !!planKeyDates && Object.values(planKeyDates).some(v=>v);
    const mergedPlan = getMergedPlan(ts.styleRef);
    const linkedQuotations = quotations.filter(q => q.name === ts.styleRef);

    // T&A allocation records (production_logs type=ALLOCATION) — factory name from DB column after get_all_data fix
    const styleAllocs = (db.allocations||[]).filter(l => l.style===ts.styleRef || poNums.includes(l.style||'') || poNums.includes(l.po_no||l.po||''));

    // Factory name resolver: direct field → db.factories by ID → cross-ref by line name
    const resolveAllocFactory = (item) => {
      if (!item) return '';
      const d = item.factory||item.factoryName||item.supplierName||item.supplier||''; if(d) return d;
      const fId=item.factoryId||item.factory_id||item.supplierId||'';
      if(fId){const f=(db.factories||[]).find(x=>String(x.id)===String(fId));if(f?.name)return f.name;}
      const line=(item.name||item.line||item.lineName||'').toLowerCase();
      if(line){const sa=styleAllocs.filter(a=>(a.line||a.lineName||'').toLowerCase()===line).sort((a,b)=>(parseInt(b.id)||0)-(parseInt(a.id)||0))[0];if(sa){const sn=sa.factory||sa.factoryName||'';if(sn)return sn;const sId=sa.factoryId||sa.factory_id||'';if(sId){const f2=(db.factories||[]).find(x=>String(x.id)===String(sId));if(f2?.name)return f2.name;}}}
      return '';
    };

    // Factory allocation cards: ts.allocatedLines is the count/qty authority; styleAllocs enriches with factory name
    const factoryAllocCards = (ts.allocatedLines||[]).map((al,idx)=>{
      const lineName=al.name||al.line||al.lineName||'';
      const dbMatch=styleAllocs.filter(sa=>(sa.line||sa.lineName||'').toLowerCase()===lineName.toLowerCase()).sort((a,b)=>(parseInt(b.id)||0)-(parseInt(a.id)||0))[0]||null;
      return { resolvedFactory:resolveAllocFactory(al)||resolveAllocFactory(dbMatch), resolvedLine:lineName, resolvedQty:parseInt(al.plannedQty||al.planned_qty||al.quantity||al.qty||0), resolvedStart:al.startDate||al.start_date||dbMatch?.startDate||dbMatch?.start_date||'', resolvedEnd:al.endDate||al.end_date||dbMatch?.endDate||dbMatch?.end_date||'', resolvedPO:al.po_no||al.po||dbMatch?.po_no||dbMatch?.po||'' };
    });

    // Allocation-based planning (fallback when no GANTT plan)
    const allocProcesses = styleAllocs.slice().sort((a,b)=>(parseInt(b.id)||0)-(parseInt(a.id)||0))
      .filter((a,i,arr)=>{const k=`${resolveAllocFactory(a)}|${a.line||''}`.toLowerCase();return arr.findIndex(x=>`${resolveAllocFactory(x)}|${x.line||''}`.toLowerCase()===k)===i;})
      .map((a,idx)=>{const fn=resolveAllocFactory(a);const ln=a.line||a.lineName||'';return{name:fn||ln||`Line ${idx+1}`,subLabel:fn&&ln?ln:'',planDate:a.startDate||a.start_date||a.date||'',actualDate:a.endDate||a.end_date||'',approvalStatus:'Confirmed',qty:a.plannedQty||a.planned_qty||a.quantity||'',line:ln,po:a.po_no||a.po||'',fromAllocation:true};});
    const displayPlanProcesses = planProcesses.length>0 ? planProcesses : allocProcesses;
    const hasPlanningData = displayPlanProcesses.length>0;

    // Allocated suppliers: confirmed cards (clean) → sourcing quotes → style object
    const _suppFromCards  = factoryAllocCards.map(c=>c.resolvedFactory).filter(Boolean);
    const _suppFromQuotes = linkedQuotations.filter(q=>q.status==='Selected').map(q=>q.supplier).filter(Boolean);
    const _suppFromStyle  = ts.allocatedSuppliers || [];
    const passportAllocatedSuppliers = [...new Set([..._suppFromCards,..._suppFromQuotes,..._suppFromStyle])];
    const allocations = ts.allocatedLines || [];
    const obEffVal = ts.obSettings?.eff || 75;
    const obHoursVal = ts.obSettings?.hours || 8;
    const totalSMV = ops.reduce((a, b) => a + (parseFloat(b.smv) || 0), 0);
    const uniqueMachines = [...new Set(ops.map(o => o.machine))].length;
    const qcStaff = Math.ceil((ts.totalQty || 0) / 2000);
    const hourlyTarget = totalSMV > 0 ? Math.round((60 / totalSMV) * (obEffVal / 100)) : 0;
    const dailyTarget = hourlyTarget * obHoursVal;
    const exFty = (ts.pos && ts.pos.length > 0) ? [...ts.pos].sort((a, b) => new Date(a.date) - new Date(b.date))[0].date : "TBD";
    const storedOBs = ts.supplierOBs || [];
    const standardOps = ops; // workstation ops = standard OB

    // ── EFFECTIVE SMV: single source of truth for this style ──────────────
    // Priority: workstation aggregatedOps sum > workstationData.smv > cost.smv (manual costing entry) > style.manualSmv > 0
    const _wsSmv   = ops.length > 0 ? ops.reduce((a,b)=>a+(parseFloat(b.smv)||0),0) : 0;
    const _costSmv = parseFloat(cost?.smv) || 0;
    const _manSmv  = parseFloat(ts.manualSmv) || 0;
    const effectiveSMV = _wsSmv || parseFloat(ts.workstationData?.smv) || _costSmv || _manSmv || 0;
    const smvSource    = _wsSmv ? 'Workstation' : (ts.workstationData?.smv ? 'Workstation' : (_costSmv ? 'Costing' : (_manSmv ? 'Manual' : 'None')));

    // Previous season data
    const prevSeasonStyles = stylesList.filter(s => s.styleRef === ts.styleRef && s.id !== ts.id);
    const sameStyleAllSeasons = stylesList.filter(s => {
      const sRef = s.styleRef?.replace(/[\d]+$/, '').trim().toLowerCase();
      const tRef = ts.styleRef?.replace(/[\d]+$/, '').trim().toLowerCase();
      return sRef === tRef;
    });

    const passportTabs = [
      { key: 'overview', label: 'Overview' },
      { key: 'production', label: 'Production Follow-Up' },
      { key: 'pricing', label: 'Price Comparison' },
      { key: 'docs', label: 'Documents & BoM' },
      { key: 'ob', label: 'Operation Bulletin' },
      { key: 'history', label: 'Season History' },
    ];

    return (
      <div style={containerStyle}>
        <Toast message={notification} type={notifType} />
        <PlanningModal />

        {/* Passport Header */}
        <div style={{ background: '#1f1f1f', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={() => setView("GALLERY")} style={{ padding: '5px 10px', background: '#323338', color: '#9699a6', border: '1px solid #323338', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem' }}>← Gallery</button>
            <span style={{ fontWeight: '800', color: 'white', fontSize: '1rem', letterSpacing: '1px' }}>{ts.styleRef}</span>
            <span style={{ background: '#323338', color: '#9699a6', padding: '3px 10px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 'bold' }}>{ts.status}</span>
            <span style={{ color: '#676879', fontSize: '0.78rem' }}>{ts.brand} · {ts.season}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {ts.spec && <a href={ts.spec} download style={{ padding: '5px 10px', background: '#f59e0b', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 'bold' }}>Spec</a>}
            {ts.tech && <a href={ts.tech} download style={{ padding: '5px 10px', background: '#059669', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 'bold' }}>Techpack</a>}
            <button onClick={() => cloneStyle(ts)} style={{ padding: '5px 12px', background: '#1e40af', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem' }}>Clone</button>
            <button onClick={() => editStyle(ts)} style={{ padding: '5px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem' }}>Edit</button>
          </div>
        </div>

        {/* Passport Sub-Tabs */}
        <div style={{ background: '#323338', display: 'flex', borderBottom: '1px solid #323338', overflowX: 'auto', flexShrink: 0 }}>
          {passportTabs.map(t => (
            <button key={t.key} onClick={() => setPassportTab(t.key)} style={{ padding: '10px 16px', border: 'none', background: passportTab === t.key ? '#3b82f6' : 'transparent', color: passportTab === t.key ? 'white' : '#9699a6', cursor: 'pointer', fontSize: '0.78rem', fontWeight: passportTab === t.key ? 'bold' : 'normal', whiteSpace: 'nowrap', borderBottom: passportTab === t.key ? '2px solid #60a5fa' : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>

        <div id="main-scroll-container" style={{ ...scrollableContentStyle, background: '#f5f6f8' }}>

          {/* ---- TAB: OVERVIEW ---- */}
          {passportTab === 'overview' && (
            <div>
              {/* Top Row: Image + Basic Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', marginBottom: '16px' }}>
                {/* Style Image */}
                <div>
                  {/* ── STYLE IMAGE — premium card with centered image ──────── */}
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '3/4',
                    minHeight: '300px',
                    background: '#fafafa',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #f0f0f0',
                  }}>
                    {/* Image / Workstation / Placeholder */}
                    {/* Priority: uploaded image first, workstation as fallback */}
                    {ts.img
                      ? <img src={ts.img} alt="Style"
                          style={{ maxWidth:'100%', maxHeight:'100%', width:'auto', height:'auto',
                            objectFit:'contain', display:'block', margin:'auto', padding:'12px' }} />
                      : ts.workstationData?.items?.length > 0
                        ? <div style={{width:'100%',height:'100%'}}><WorkstationView data={ts.workstationData} /></div>
                        : <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                            gap:'10px', color:'#c5c7d4', padding:'24px' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                              <polyline points="21 15 16 10 5 21"/>
                            </svg>
                            <span style={{fontSize:'0.78rem',color:'#94a3b8',textAlign:'center'}}>
                              No image uploaded<br/>
                              <span style={{fontSize:'0.7rem',color:'#c5c7d4'}}>Click Edit to add</span>
                            </span>
                          </div>}

                    {/* Production stats overlay on hover */}
                    <div style={{ position:'absolute', inset:0,
                      background:'linear-gradient(to top, rgba(10,12,20,0.95) 0%, rgba(10,12,20,0.5) 60%, transparent 100%)',
                      opacity:0, transition:'opacity 0.25s', display:'flex', flexDirection:'column',
                      justifyContent:'flex-end', padding:'16px' }}
                      onMouseEnter={e=>e.currentTarget.style.opacity=1}
                      onMouseLeave={e=>e.currentTarget.style.opacity=0}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px'}}>
                        {[['Ordered',(ts.totalQty||0).toLocaleString(),'#94a3b8'],
                          ['Cut',prodSummary.cut,'#f59e0b'],
                          ['Sewn',prodSummary.sew,'#3b82f6'],
                          ['Finished',prodSummary.pack,'#10b981'],
                          ['Shipped',prodSummary.ship,'#8b5cf6'],
                          ['Ex-Fty',exFty,'#94a3b8']
                        ].map(([label,val,clr])=>(
                          <div key={label} style={{textAlign:'center'}}>
                            <div style={{fontSize:'0.6rem',color:'rgba(255,255,255,0.5)',textTransform:'uppercase',letterSpacing:'0.5px'}}>{label}</div>
                            <div style={{fontSize:'0.88rem',fontWeight:'700',color:clr,marginTop:'2px'}}>{val||'—'}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* AI FOB badge — top right corner, clean pill */}
                    {predictedFob && (
                      <div style={{ position:'absolute', top:'10px', right:'10px',
                        background:'rgba(10,12,20,0.85)', backdropFilter:'blur(8px)',
                        border:'1px solid rgba(56,189,248,0.4)', borderRadius:'20px',
                        padding:'4px 10px', display:'flex', alignItems:'center', gap:'5px' }}>
                        <span style={{fontSize:'0.58rem',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.5px'}}>AI FOB</span>
                        <span style={{fontSize:'0.88rem',fontWeight:'800',color:'#38bdf8'}}>{format(predictedFob)}</span>
                      </div>
                    )}
                  </div>

                  {/* ── ACTION BUTTONS — icon-only, clean ──────────────────────── */}
                  <div style={{ marginTop:'10px', display:'flex', gap:'6px' }}>
                    {/* Replace image */}
                    <label title="Replace Image" style={{
                      flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                      gap:'5px', padding:'8px', cursor:'pointer',
                      background:'#fff', border:'1px solid #e6e9ef', borderRadius:'8px',
                      color:'#676879', fontSize:'0.72rem', fontWeight:'600',
                      transition:'all 0.15s',
                    }}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor='#0073ea';e.currentTarget.style.color='#0073ea';}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='#e6e9ef';e.currentTarget.style.color='#676879';}}>
                      <input type="file" style={{display:'none'}} accept="image/*"
                        onChange={handlePassportImageReplace} />
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      Edit
                    </label>

                    {/* AI Prediction */}
                    <button title="AI FOB Prediction" onClick={handlePredict}
                      style={{
                        flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                        gap:'5px', padding:'8px', cursor:'pointer',
                        background:'#fff', border:'1px solid #e6e9ef', borderRadius:'8px',
                        color:'#676879', fontSize:'0.72rem', fontWeight:'600',
                        transition:'all 0.15s',
                      }}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor='#38bdf8';e.currentTarget.style.color='#38bdf8';}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='#e6e9ef';e.currentTarget.style.color='#676879';}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                      AI
                    </button>

                    {/* 3D Workstation */}
                    <button title="Open 3D Workstation" onClick={()=>setShowVisualStudio(true)}
                      style={{
                        flex:1, display:'flex', alignItems:'center', justifyContent:'center',
                        gap:'4px', padding:'8px', cursor:'pointer',
                        background:'#fff', border:'1px solid #e6e9ef', borderRadius:'8px',
                        color:'#676879', fontSize:'0.72rem', fontWeight:'700',
                        transition:'all 0.15s',
                      }}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor='#7c3aed';e.currentTarget.style.color='#7c3aed';}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='#e6e9ef';e.currentTarget.style.color='#676879';}}>
                      3D
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </button>
                  </div>
                  {/* Files */}
                  <div style={{ marginTop: '12px', background: 'white', border: '1px solid #e6e9ef', borderRadius: '10px', padding: '12px' }}>
                    {/* ── FILES: categorised, with type badges ────────── */}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                      <div style={{fontSize:'0.75rem',fontWeight:'bold',color:'#676879'}}>FILES & DOCUMENTS</div>
                      <span style={{fontSize:'0.7rem',color:'#94a3b8'}}>{(ts.files||[]).length} file{(ts.files||[]).length!==1?'s':''}</span>
                    </div>
                    {/* Upload buttons by category */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'4px',marginBottom:'10px'}}>
                      {[['TECHPACK','📐','#7c3aed'],['PO','📋','#0073ea'],['SPEC','📄','#00a65e'],['OTHER','📎','#676879']].map(([type,icon,clr])=>(
                        <label key={type} style={{display:'flex',alignItems:'center',gap:'4px',
                          padding:'5px 8px',background:'#f8f9fc',border:'1px dashed #e6e9ef',
                          borderRadius:'6px',cursor:'pointer',fontSize:'0.68rem',color:clr,fontWeight:'600'}}>
                          <input type="file" style={{display:'none'}} onChange={e=>handleFileUpload(e,type)}/>
                          {icon} {type}
                        </label>
                      ))}
                    </div>
                    {/* Files list grouped by type */}
                    {(ts.files||[]).length===0
                      ? <div style={{textAlign:'center',padding:'12px',color:'#94a3b8',fontSize:'0.72rem',background:'#f8f9fc',borderRadius:'6px'}}>
                          No files uploaded yet.<br/>Upload Techpack, PO, Spec or any document above.
                        </div>
                      : (() => {
                          const order = ['TECHPACK','PO','SPEC','OTHER'];
                          const typeColors = {TECHPACK:'#7c3aed',PO:'#0073ea',SPEC:'#00a65e',OTHER:'#676879'};
                          const grouped = {};
                          (ts.files||[]).forEach(f=>{const t=f.type||'OTHER'; if(!grouped[t])grouped[t]=[]; grouped[t].push(f);});
                          return order.filter(t=>grouped[t]).map(t=>(
                            <div key={t} style={{marginBottom:'8px'}}>
                              <div style={{fontSize:'0.65rem',fontWeight:'700',color:typeColors[t],
                                textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:'4px',
                                display:'flex',alignItems:'center',gap:'4px'}}>
                                <span style={{width:'6px',height:'6px',borderRadius:'50%',background:typeColors[t],display:'inline-block'}}/>
                                {t} ({grouped[t].length})
                              </div>
                              {grouped[t].map(f=>(
                                <div key={f.id} style={{display:'flex',justifyContent:'space-between',
                                  alignItems:'center',padding:'5px 8px',background:'#fff',
                                  border:'1px solid #f0f1f4',borderRadius:'6px',marginBottom:'3px'}}>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:'0.72rem',fontWeight:'600',color:'#323338',
                                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                                      title={f.name}>{f.name}</div>
                                    <div style={{fontSize:'0.62rem',color:'#94a3b8',marginTop:'1px'}}>
                                      {f.date}{f.size?' · '+f.size:''}{f.source==='AI_UPLOAD'?' · AI extracted':''}
                                    </div>
                                  </div>
                                  <div style={{display:'flex',gap:'6px',marginLeft:'6px',flexShrink:0}}>
                                    <a href={f.data||f.url||'#'} download={f.name} title="Download"
                                      style={{color:'#0073ea',lineHeight:1}}>
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                                      </svg>
                                    </a>
                                    <button onClick={()=>deleteFile(f.id)}
                                      style={{background:'none',border:'none',cursor:'pointer',color:'#e2445c',lineHeight:1,padding:0}}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ));
                        })()
                    }
                    {(ts.files || []).map(f => (
                      <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid #f5f6f8', fontSize: '0.75rem' }}>
                        <span style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#323338' }}>{f.name}</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <a href={f.url} download={f.name}><Download size={13} color="#3b82f6" /></a>
                          <button onClick={() => deleteFile(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><Trash2 size={13} color="#e2445c" /></button>
                        </div>
                      </div>
                    ))}
                    {(ts.files || []).length === 0 && <div style={{ color: '#9699a6', fontSize: '0.72rem', textAlign: 'center', padding: '8px' }}>No files uploaded</div>}
                  </div>
                </div>

                {/* Right: Style Details + Costing */}
                <div>
                  {/* Style Info Grid */}
                  <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '10px', padding: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                      {[['Brand', ts.brand], ['Season', ts.season], ['Fabric', ts.fabric], ['Total Qty', (ts.totalQty || 0).toLocaleString()], ['Buyer', ts.buyer], ['Product', ts.product], ['Status', ts.status], ['Size Range', ts.sizeRange], ['Ex-Factory', exFty]].map(([l, v]) => (
                        <div key={l}>
                          <div style={{ fontSize: '0.68rem', color: '#9699a6', letterSpacing: '0.5px', marginBottom: '2px' }}>{l.toUpperCase()}</div>
                          <div style={{ fontSize: '0.88rem', fontWeight: '600', color: '#323338' }}>{v || '—'}</div>
                        </div>
                      ))}
                    </div>
                    {/* Allocated Suppliers — from confirmed T&A allocations + sourcing quotes + style object */}
                    {passportAllocatedSuppliers.length > 0 && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f1f4' }}>
                        <div style={{ fontSize: '0.68rem', color: '#9699a6', marginBottom: '6px' }}>ALLOCATED SUPPLIERS</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {passportAllocatedSuppliers.map((s, i) => <span key={i} style={{ background: '#e8f0ff', color: '#0073ea', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 'bold' }}>{s}</span>)}
                        </div>
                      </div>
                    )}
                    {/* Previous Seasons History inline in Overview */}
                    {sameStyleAllSeasons.length > 1 && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f1f4' }}>
                        <div style={{ fontSize: '0.68rem', color: '#9699a6', marginBottom: '8px' }}>PREVIOUS SEASONS — PLACED HISTORY</div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {sameStyleAllSeasons.filter(s => s.id !== ts.id).map((s, i) => {
                            const sCost = getCostSummary(s.id);
                            const sQuote = quotations.find(q => q.name === s.styleRef && q.status === 'Selected');
                            const sPlaced = (s.pos || []).reduce((a, b) => a + (parseFloat(b.qty) || 0), 0);
                            return (
                              <div key={i} style={{ background: '#f5f6f8', border: '1px solid #e6e9ef', borderRadius: '6px', padding: '8px 10px', minWidth: '140px' }}>
                                <div style={{ fontWeight: 'bold', fontSize: '0.78rem', color: '#323338' }}>{s.season}</div>
                                <div style={{ fontSize: '0.72rem', color: '#676879', marginTop: '2px' }}>Qty: <strong>{(s.totalQty || 0).toLocaleString()}</strong></div>
                                {sPlaced > 0 && <div style={{ fontSize: '0.72rem', color: '#0073ea' }}>Placed: <strong>{sPlaced.toLocaleString()}</strong></div>}
                                {sCost && <div style={{ fontSize: '0.72rem', color: '#00a65e', fontWeight: 'bold' }}>FOB: {format(sCost.fob)}</div>}
                                {sQuote && <div style={{ fontSize: '0.68rem', color: '#7c3aed', marginTop: '2px' }}>✓ {sQuote.supplier}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Costing Breakdown */}
                  <div style={{ background: 'white', border: '1px solid #e3faf0', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#166534', letterSpacing: '0.5px' }}>COSTING BREAKDOWN</div>
                      <button onClick={() => {
                        // Save target so Costing tab can read it on mount
                        try { sessionStorage.setItem('costingTargetStyleId',  String(ts.id)); } catch{}
                        try { sessionStorage.setItem('costingTargetStyleRef', ts.styleRef||''); } catch{}
                        try { sessionStorage.setItem('costingTargetBlock',    ''); } catch{}

                        // ── METHOD 1: Direct DOM click on the COSTING tab (most reliable) ─
                        let clicked = false;
                        const allEls = Array.from(document.querySelectorAll('button, [role="tab"], li, a, span'));
                        for (const el of allEls) {
                          const txt = (el.innerText || el.textContent || '').trim();
                          if (/^costing$/i.test(txt) || /^cost\s*sheet$/i.test(txt)) {
                            el.click();
                            clicked = true;
                            // Give tab time to mount, then call loadCosting
                            setTimeout(() => {
                              if (typeof loadCosting === 'function') loadCosting(ts.id);
                              window.dispatchEvent(new CustomEvent('costingStyleLoaded',{detail:{styleId:ts.id,styleRef:ts.styleRef}}));
                            }, 150);
                            break;
                          }
                        }

                        // ── METHOD 2: React prop (if parent wired it) ─────────────────────
                        if (!clicked) {
                          if (typeof setActiveTab === 'function') {
                            ['COSTING','Costing','costing','Cost Sheet','COST'].forEach(t=>{ try{setActiveTab(t);}catch{} });
                          }
                          if (typeof loadCosting === 'function') {
                            setTimeout(()=>loadCosting(ts.id), 100);
                          }
                        }

                        // ── METHOD 3: Custom events (App.js listener) ─────────────────────
                        ['openCosting','loadStyleCosting','open-costing','nirvikalp_navigate'].forEach(ev => {
                          try {
                            window.dispatchEvent(new CustomEvent(ev,{detail:{styleId:ts.id,styleRef:ts.styleRef,tab:'COSTING',module:'MERCHANDISING'}}));
                          } catch{}
                        });
                      }} style={{ padding:'5px 12px', background:'#f0fdf4', border:'1px solid #86efac', color:'#00a65e', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:'bold' }}>
                        📊 Open Full Costing →
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
                      {[
                        { label: 'MATERIAL', val: cost?.m, block: 'MATERIALS', color: '#d0e4ff', border: '#a8d1ff', text: '#1e40af' },
                        { label: 'TRIMS', val: cost?.t, block: 'TRIMS', color: '#fce7f3', border: '#fbcfe8', text: '#9d174d' },
                        { label: 'V.A.', val: cost?.v, block: 'VAS', color: '#fef9c3', border: '#fef08a', text: '#854d0e' },
                        { label: 'FINISH', val: cost?.f, block: 'FINISHING', color: '#e0e7ff', border: '#c7d2fe', text: '#3730a3' },
                        { label: 'C.M.', val: cost?.cm, block: 'CM', color: '#f0fdf4', border: '#b3f5d6', text: '#166534' },
                        { label: 'FOB TOTAL', val: cost?.fob, block: null, color: '#00a65e', border: '#00a65e', text: 'white', bold: true },
                      ].map(({ label, val, block, color, border, text, bold }) => (
                        <div key={label} onClick={() => {
                          try {
                            if (!block) return;
                            sessionStorage.setItem('costingTargetStyleId', String(ts.id));
                            sessionStorage.setItem('costingTargetStyleRef', ts.styleRef||'');
                            sessionStorage.setItem('costingTargetBlock', block);
                            try { sessionStorage.setItem('costingTargetStyleId', String(ts.id)); } catch{}
                            try { sessionStorage.setItem('costingTargetBlock',   block||''); } catch{}
                            // DOM click COSTING tab
                            const els2 = Array.from(document.querySelectorAll('button,[role="tab"],li,a,span'));
                            let hit = false;
                            for (const el of els2) {
                              if (/^costing$/i.test((el.innerText||el.textContent||'').trim())) {
                                el.click(); hit = true;
                                setTimeout(()=>{ if(typeof loadCosting==='function') loadCosting(ts.id); if(typeof setActiveBlock==='function') setActiveBlock(block); }, 150);
                                break;
                              }
                            }
                            if (!hit) {
                              if(typeof loadCosting==='function') loadCosting(ts.id);
                              if(typeof setActiveTab==='function') ['COSTING','Costing','costing'].forEach(t=>{try{setActiveTab(t);}catch{}});
                              if(typeof setActiveBlock==='function') setTimeout(()=>setActiveBlock(block),100);
                            }
                            ['openCosting','loadStyleCosting'].forEach(ev=>window.dispatchEvent(new CustomEvent(ev,{detail:{styleId:ts.id,styleRef:ts.styleRef,block}})));
                          } catch(e){console.error('[CostingBlock]',e);}
                        }}
                          style={{ background: color, border: `1px solid ${border}`, borderRadius: '6px', padding: '10px 6px', textAlign: 'center', cursor: block ? 'pointer' : 'default', transition: 'transform 0.15s' }}
                          onMouseEnter={e => block && (e.currentTarget.style.transform = 'scale(1.04)')}
                          onMouseLeave={e => block && (e.currentTarget.style.transform = 'scale(1)')}>
                          <div style={{ fontSize: '0.62rem', color: bold ? 'rgba(255,255,255,0.8)' : '#676879', marginBottom: '3px' }}>{label}</div>
                          <div style={{ fontWeight: bold ? '800' : 'bold', color: text, fontSize: bold ? '0.9rem' : '0.82rem' }}>
                            {val !== undefined
                              ? bold
                                ? format(val)   // FOB TOTAL: full currency label
                                : `${(parseFloat(val)||0).toFixed(2)}`   // sub-items: no label (implied same currency)
                              : '—'}
                          </div>
                          {bold && <div style={{fontSize:'0.58rem',color:'rgba(255,255,255,0.7)',marginTop:'2px'}}>{activeCurrencyName}</div>}
                        </div>
                      ))}
                    </div>
                    {/* SMV — always visible, with source label and manual entry fallback */}
                    <div style={{ marginTop: '10px', background: '#f5f6f8', borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div>
                          <div style={{ fontSize: '0.62rem', color: '#9699a6', textTransform: 'uppercase', fontWeight: '700', marginBottom: '2px' }}>SMV (Standard Minute Value)</div>
                          {/* SMV comparison — Workstation vs Costing vs Live */}
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'6px',marginTop:'4px'}}>
                            {/* Workstation SMV */}
                            <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'6px',padding:'6px 8px',textAlign:'center'}}>
                              <div style={{fontSize:'0.6rem',color:'#15803d',fontWeight:'700',textTransform:'uppercase',marginBottom:'2px'}}>Workstation</div>
                              <div style={{fontSize:'1rem',fontWeight:'900',color:'#15803d'}}>
                                {(_wsSmv||parseFloat(ts.workstationData?.smv)||0)>0 ? (_wsSmv||parseFloat(ts.workstationData?.smv)).toFixed(2) : '—'}
                              </div>
                              <div style={{fontSize:'0.6rem',color:'#15803d'}}>min/pc</div>
                            </div>
                            {/* Costing SMV */}
                            <div style={{background:'#faf5ff',border:'1px solid #e9d5ff',borderRadius:'6px',padding:'6px 8px',textAlign:'center'}}>
                              <div style={{fontSize:'0.6rem',color:'#7c3aed',fontWeight:'700',textTransform:'uppercase',marginBottom:'2px'}}>Costing</div>
                              <div style={{fontSize:'1rem',fontWeight:'900',color:'#7c3aed'}}>
                                {_costSmv>0 ? _costSmv.toFixed(2) : '—'}
                              </div>
                              <div style={{fontSize:'0.6rem',color:'#7c3aed'}}>min/pc</div>
                            </div>
                            {/* Approved OB SMV */}
                            <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:'6px',padding:'6px 8px',textAlign:'center'}}>
                              <div style={{fontSize:'0.6rem',color:'#c2410c',fontWeight:'700',textTransform:'uppercase',marginBottom:'2px'}}>Approved OB</div>
                              <div style={{fontSize:'1rem',fontWeight:'900',color:'#c2410c'}}>
                                {(() => {
                                  const obSmv = (ts.workstationData?.aggregatedOps||[]).reduce((s,o)=>s+(parseFloat(o.smv)||0),0);
                                  return obSmv>0 ? obSmv.toFixed(2) : '—';
                                })()}
                              </div>
                              <div style={{fontSize:'0.6rem',color:'#c2410c'}}>min/pc</div>
                            </div>
                            {/* Live Efficiency */}
                            <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'6px',padding:'6px 8px',textAlign:'center'}}>
                              <div style={{fontSize:'0.6rem',color:'#1d4ed8',fontWeight:'700',textTransform:'uppercase',marginBottom:'2px'}}>Live Eff %</div>
                              <div style={{fontSize:'1rem',fontWeight:'900',color:
                                (() => { const e = prodSummary?.efficiency || 0; return e>=85?'#15803d':e>=70?'#d97706':'#e2445c'; })()}}>
                                {prodSummary?.efficiency > 0 ? Math.round(prodSummary.efficiency)+'%' : '—'}
                              </div>
                              <div style={{fontSize:'0.6rem',color:'#1d4ed8'}}>from production</div>
                            </div>
                          </div>
                          {/* Efficiency row */}
                          <div style={{marginTop:'6px'}}>
                            <div style={{fontSize:'0.6rem',color:'#64748b',fontWeight:'600',marginBottom:'4px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Efficiency assumed</div>
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:'6px'}}>
                              <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:'4px',padding:'4px 6px',textAlign:'center'}}>
                                <div style={{fontSize:'0.72rem',fontWeight:'700',color:'#15803d'}}>
                                  {ts.workstationData?.efficiency > 0 ? ts.workstationData.efficiency+'%' : '75%'}
                                </div>
                                <div style={{fontSize:'0.58rem',color:'#15803d'}}>WS OB</div>
                              </div>
                              <div style={{background:'#faf5ff',border:'1px solid #e9d5ff',borderRadius:'4px',padding:'4px 6px',textAlign:'center'}}>
                                <div style={{fontSize:'0.72rem',fontWeight:'700',color:'#7c3aed'}}>
                                  {(() => {
                                    const cst = (db.costings||[]).find(c=>c.styleId===ts.id);
                                    return cst?.efficiency ? cst.efficiency+'%' : cst?.eff ? cst.eff+'%' : '—';
                                  })()}
                                </div>
                                <div style={{fontSize:'0.58rem',color:'#7c3aed'}}>Costing</div>
                              </div>
                              <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:'4px',padding:'4px 6px',textAlign:'center'}}>
                                <div style={{fontSize:'0.72rem',fontWeight:'700',color:'#c2410c'}}>
                                  {(() => {
                                    const suppOB = (ts.supplierOBs||ts.workstationData?.supplierOBs||[])[0];
                                    return suppOB?.efficiency ? suppOB.efficiency+'%' : '—';
                                  })()}
                                </div>
                                <div style={{fontSize:'0.58rem',color:'#c2410c'}}>Supplier OB</div>
                              </div>
                              <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'4px',padding:'4px 6px',textAlign:'center'}}>
                                <div style={{fontSize:'0.72rem',fontWeight:'700',color:
                                  (() => { const e=prodSummary?.efficiency||0; return e>=85?'#15803d':e>=70?'#d97706':'#e2445c'; })()}}>
                                  {prodSummary?.efficiency > 0 ? Math.round(prodSummary.efficiency)+'%' : '—'}
                                </div>
                                <div style={{fontSize:'0.58rem',color:'#1d4ed8'}}>Live</div>
                              </div>
                            </div>
                          </div>
                          {effectiveSMV <= 0 && (
                            <div style={{fontSize:'0.75rem',color:'#e2445c',marginTop:'4px'}}>No SMV set — complete Workstation OB or enter manually</div>
                          )}
                        </div>
                        {/* SMV History from costings */}
                        {(db.costings||[]).filter(c=>c.styleId===ts.id&&c.smv>0).length > 0 && (
                          <div style={{ borderLeft: '1px solid #e6e9ef', paddingLeft: '12px' }}>
                            <div style={{ fontSize: '0.62rem', color: '#9699a6', fontWeight: '700', marginBottom: '3px', textTransform: 'uppercase' }}>SMV History</div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {(db.costings||[]).filter(co=>co.styleId===ts.id&&co.smv>0).slice(-5).map((co,i)=>(
                                <span key={i} style={{ background: 'white', border: '1px solid #e6e9ef', padding: '2px 8px', borderRadius: '8px', fontSize: '0.7rem', color: '#676879' }}>
                                  {parseFloat(co.smv).toFixed(2)}{co.season?' ('+co.season+')':''}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Manual SMV entry — only when workstation has no SMV */}
                      {!_wsSmv && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Enter SMV manually"
                            defaultValue={_manSmv||_costSmv||''}
                            id="manualSmvInput"
                            style={{ padding: '6px 10px', border: '2px solid #f59e0b', borderRadius: '6px', width: '160px', fontSize: '0.82rem', fontWeight: '600' }}
                          />
                          <button onClick={async () => {
                            const val = parseFloat(document.getElementById('manualSmvInput')?.value)||0;
                            if(!val){ alert('Enter a valid SMV value'); return; }
                            const updated = { ...ts, manualSmv: val };
                            const res = await saveToDB('style', updated);
                            if(res?.id||res?.success){
                              setDb(prev=>({...prev, mgmtStyles: prev.mgmtStyles.map(s=>s.id===ts.id?{...s,manualSmv:val}:s)}));
                              setTargetStyle({...ts, manualSmv: val});
                              setNotification('SMV saved to style passport');
                              setTimeout(()=>setNotification(''),2500);
                            }
                          }} style={{ padding: '6px 14px', background: '#f59e0b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '0.78rem', color: 'white' }}>
                            Save SMV
                          </button>
                        </div>
                      )}
                    </div>
                    {cost && (
                      <div style={{ marginTop: '6px', fontSize: '0.72rem', color: '#676879', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <span>Status: <strong style={{ color: cost.status === 'Approved' ? '#00a65e' : '#f59e0b' }}>{cost.status}</strong></span>
                      </div>
                    )}
                    {!cost && effectiveSMV === 0 && <div style={{ textAlign: 'center', color: '#9699a6', fontSize: '0.8rem', padding: '6px 0' }}>No costing data — complete Workstation OB or enter SMV manually above.</div>}
                  </div>

                  {/* PO Summary */}
                  <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '10px', padding: '14px', marginTop: '12px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#323338', marginBottom: '10px' }}>PO DETAILS</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                        <thead><tr style={{ background: '#f5f6f8' }}>{['PO #', 'Color', 'Qty', 'FOB', 'Ex-Factory', 'Destination'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #e6e9ef', color: '#676879', fontSize: '0.7rem' }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {(() => {
                            // ── SINGLE SOURCE OF TRUTH: db.poSummary from style_po master table ──
                            // Aggregated by style_ref + po_no + color. Falls back to style.pos[]
                            // if migration hasn't run yet.
                            const masterRows = (db.poSummary || []).filter(r => r.style_ref === ts.styleRef);
                            const displayPOs = masterRows.length > 0
                              ? masterRows.map(r => ({
                                  num: r.po_no, col: r.color,
                                  qty: parseInt(r.total_qty || 0),
                                  fob: r.fob, date: r.ex_fty_date || '',
                                  dest: r.destination || '',
                                  sizeBreakdown: r.size_breakdown || '',
                                }))
                              : (() => {
                                  // Legacy fallback: style.pos[] + po_size_table
                                  const directPOs = ts.pos || [];
                                  if (directPOs.length > 0) return directPOs;
                                  const dbPONums = [...new Set((db.poSizeTable||[]).filter(r=>r.style_ref===ts.styleRef).map(r=>r.po_no))];
                                  return dbPONums.map(pn => {
                                    const rows = (db.poSizeTable||[]).filter(r=>r.style_ref===ts.styleRef&&r.po_no===pn);
                                    const qty = rows.reduce((a,r)=>a+(parseInt(r.qty)||0),0);
                                    const fst = rows[0]||{};
                                    const szStr = rows.filter(r=>r.size&&r.size!=='ALL').map(r=>`${r.size}:${r.qty}`).join(',');
                                    return { num:pn, col:[...new Set(rows.map(r=>r.color).filter(Boolean))].join(', '), qty, fob:fst.fob||'', date:fst.ex_fty_date||'', dest:fst.destination||'', sizeBreakdown:szStr };
                                  });
                                })();
                            if (displayPOs.length === 0) return <tr><td colSpan={6} style={{ textAlign:'center', padding:'12px', color:'#9699a6' }}>No POs linked — add POs in Edit mode or upload a PO document</td></tr>;
                            return displayPOs.map((p,i) => (
                              <tr key={i} style={{ borderBottom:'1px solid #f5f6f8' }}>
                                <td style={{ padding:'5px 8px', fontWeight:'bold' }}>{p.num}</td>
                                <td style={{ padding:'5px 8px' }}>{p.col}</td>
                                <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:'bold' }}>{(parseInt(p.qty)||0).toLocaleString()}</td>
                                <td style={{ padding:'5px 8px', color:'#00a65e', fontWeight:'bold' }}>{p.fob||'—'}</td>
                                <td style={{ padding:'5px 8px' }}>{p.date||'TBD'}</td>
                                <td style={{ padding:'5px 8px', color:'#676879', fontSize:'0.72rem' }}>{p.dest||'—'}</td>
                                <td style={{ padding:'5px 8px', fontSize:'0.7rem', color:'#475569' }}>
                                  {(() => {
                                    const poNum = (p.num||p.po||'').split(' ')[0].replace(/[·•].*/,'');
                                    const rows = (db.poSizeTable||[]).filter(function(r){
                                      return r.style_ref===ts.styleRef && r.po_no===poNum && r.color===p.col;
                                    });
                                    if (rows.length > 0) return rows.map(function(r){return r.size+(r.qty?' ('+r.qty+')':'');}).join(' · ');
                                    var sb = p.sizes||p.sizeBreakdown||p.size_breakdown||'';
                                    return typeof sb==='string'?sb:'—';
                                  })()}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    </div>
                    {(() => {
                      const directPOs = ts.pos || [];
                      const dbPONums = [...new Set((db.poSizeTable||[]).filter(r=>r.style_ref===ts.styleRef).map(r=>r.po_no))];
                      const total = directPOs.length > 0
                        ? directPOs.reduce((a,b)=>a+(parseFloat(b.qty)||0),0)
                        : (db.poSizeTable||[]).filter(r=>r.style_ref===ts.styleRef).reduce((a,r)=>a+(parseInt(r.qty)||0),0);
                      return total > 0 ? (
                        <div style={{ marginTop:'8px', fontSize:'0.75rem', color:'#00a65e', fontWeight:'bold', textAlign:'right' }}>
                          Total: {total.toLocaleString()} pcs
                          {directPOs.length === 0 && dbPONums.length > 0 && <span style={{color:'#9699a6', fontWeight:'normal', marginLeft:'6px'}}>(from PO size table)</span>}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>

              {/* Planning Timeline */}
              <Section title="PLANNING TIMELINE" icon={<Calendar size={14} />} color="#0369a1" bg="#f0f9ff" defaultOpen={true}
                badge={planProcesses.length>0?`${planProcesses.length} GANTT processes`:hasPlanKeyDates?'Plan — key dates set':allocProcesses.length>0?`${allocProcesses.length} Line Allocation(s)`:'No Plan'}>
                {/* CASE 1: GANTT processes */}
                {planProcesses.length > 0 && (
                  <div>
                    <div style={{ display:'flex', gap:'8px', overflowX:'auto', paddingBottom:'8px', marginBottom:'12px' }}>
                      {planProcesses.map((p,i)=>{const isLate=p.actualDate&&p.planDate&&new Date(p.actualDate)>new Date(p.planDate);const isDone=p.approvalStatus==='Approved';return(
                        <div key={i} onClick={()=>activePlan&&setEditingProcess({planId:activePlan.id,process:p,index:i})} style={{minWidth:'140px',flexShrink:0,background:'white',borderRadius:'8px',padding:'10px',textAlign:'center',cursor:'pointer',border:`2px solid ${isDone?'#00a65e':isLate?'#e2445c':'#93c5fd'}`,position:'relative',transition:'transform 0.15s'}} onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'} onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
                          {i>0&&<div style={{position:'absolute',left:'-12px',top:'50%',transform:'translateY(-50%)',color:'#9699a6',fontSize:'0.7rem'}}>→</div>}
                          <div style={{fontWeight:'bold',fontSize:'0.75rem',marginBottom:'4px',color:'#323338'}}>{p.name}</div>
                          <span style={{background:isDone?'#e3faf0':isLate?'#fde8eb':'#d0e4ff',color:isDone?'#00a65e':isLate?'#e2445c':'#1e40af',padding:'2px 6px',borderRadius:'8px',fontSize:'0.65rem',fontWeight:'bold'}}>{p.approvalStatus||'Pending'}</span>
                          <div style={{fontSize:'0.65rem',color:'#676879',marginTop:'4px'}}>Plan: {p.planDate||'—'}</div>
                          {p.actualDate&&<div style={{fontSize:'0.65rem',color:isLate?'#e2445c':'#00a65e'}}>Actual: {p.actualDate}</div>}
                        </div>);
                      })}
                    </div>
                    <div style={{fontSize:'0.72rem',color:'#676879'}}>Click any process block to update status and dates.</div>
                  </div>
                )}
                {/* CASE 2: GANTT plan with key dates but no processes */}
                {planProcesses.length===0 && hasPlanKeyDates && (
                  <div>
                    <div style={{fontSize:'0.72rem',color:'#0369a1',background:'#e0f2fe',padding:'7px 12px',borderRadius:'6px',marginBottom:'14px'}}>📋 T&amp;A GANTT plan found — key dates set. Add process milestones in T&amp;A → Planning → Generator.</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:'10px'}}>
                      {[{label:'Plan Cut Date',val:planKeyDates.cutDate},{label:'Sewing Start',val:planKeyDates.sewingStart},{label:'Finish End',val:planKeyDates.finEnd},{label:'Ex-Factory',val:planKeyDates.exFtyDate}].filter(d=>d.val).map(d=>(
                        <div key={d.label} style={{background:'white',border:'1px solid #bae6fd',borderRadius:'8px',padding:'10px',textAlign:'center'}}>
                          <div style={{fontSize:'0.6rem',color:'#676879',textTransform:'uppercase',marginBottom:'4px'}}>{d.label}</div>
                          <div style={{fontWeight:'bold',fontSize:'0.88rem',color:'#0369a1'}}>{d.val}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* CASE 3: No GANTT plan — show T&A allocation lines */}
                {planProcesses.length===0 && !hasPlanKeyDates && allocProcesses.length>0 && (
                  <div>
                    <div style={{fontSize:'0.72rem',color:'#0369a1',background:'#e0f2fe',padding:'7px 12px',borderRadius:'6px',marginBottom:'12px'}}>📋 Showing <strong>T&amp;A Line Allocations</strong> — no GANTT timeline created yet. Create one via T&amp;A → Planning → Generator.</div>
                    <div style={{display:'flex',gap:'8px',overflowX:'auto',paddingBottom:'8px'}}>
                      {allocProcesses.map((p,i)=>(
                        <div key={i} style={{minWidth:'150px',flexShrink:0,background:'white',borderRadius:'8px',padding:'10px',border:'2px solid #86efac',position:'relative'}}>
                          {i>0&&<div style={{position:'absolute',left:'-12px',top:'50%',transform:'translateY(-50%)',color:'#9699a6',fontSize:'0.7rem'}}>→</div>}
                          <div style={{fontWeight:'bold',fontSize:'0.78rem',color:'#166534',marginBottom:'3px'}}>🏭 {p.name}</div>
                          {p.subLabel&&<div style={{fontSize:'0.65rem',color:'#676879',marginBottom:'4px'}}>Line: {p.subLabel}</div>}
                          {p.po&&<div style={{fontSize:'0.62rem',color:'#7c3aed',marginBottom:'4px'}}>PO: {p.po}</div>}
                          <span style={{background:'#e3faf0',color:'#00a65e',padding:'2px 6px',borderRadius:'8px',fontSize:'0.62rem',fontWeight:'bold'}}>ALLOCATED</span>
                          {p.qty?<div style={{fontSize:'0.72rem',fontWeight:'bold',color:'#323338',marginTop:'5px'}}>{parseInt(p.qty).toLocaleString()} pcs</div>:null}
                          {p.planDate&&<div style={{fontSize:'0.62rem',color:'#676879',marginTop:'4px'}}>Start: {p.planDate}</div>}
                          {p.actualDate&&<div style={{fontSize:'0.62rem',color:'#00a65e'}}>End: {p.actualDate}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* CASE 4: Nothing */}
                {planProcesses.length===0 && !hasPlanKeyDates && allocProcesses.length===0 && (
                  <div style={{color:'#9699a6',fontSize:'0.85rem',textAlign:'center',padding:'20px 0'}}>
                    No planning data linked to this style.<br/><small>Create a plan in T&amp;A → Planning, or allocate lines in T&amp;A → Execution → Allocation.</small>
                  </div>
                )}
              </Section>

              {/* Production Follow-Up Sub-Section (3b) — inline summary under timeline */}
              <Section title="PRODUCTION FOLLOW-UP SUMMARY" icon={<Activity size={14} />} color="#c2410c" bg="#fff7ed" defaultOpen={true}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', marginBottom: '12px' }}>
                  {[
                    { l: 'TOTAL QTY', v: ts.totalQty || 0, c: '#323338' },
                    { l: 'CUT', v: prodSummary.cut, c: '#f59e0b' },
                    { l: 'SEWING', v: prodSummary.sew, c: '#3b82f6' },
                    { l: 'FINISHED', v: prodSummary.pack, c: '#8b5cf6' },
                    { l: 'SHIPPED', v: prodSummary.ship, c: '#00a65e' },
                    { l: 'BALANCE', v: (ts.totalQty || 0) - (prodSummary.ship || 0), c: '#e2445c' },
                  ].map(({ l, v, c }) => (
                    <div key={l} style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.62rem', color: '#9699a6', letterSpacing: '0.5px', marginBottom: '3px' }}>{l}</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: c }}>{(v || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                {/* Placed Qty per supplier from POs */}
                {linkedQuotations.filter(q => q.status === 'Selected').length > 0 && (
                  <div style={{ marginBottom: '10px', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #b3f5d6', borderRadius: '6px', fontSize: '0.78rem' }}>
                    <strong style={{ color: '#166534' }}>Placed Supplier:</strong>{' '}
                    {linkedQuotations.filter(q => q.status === 'Selected').map(q => q.supplier).join(', ')}
                    {' — '}<strong>Placed Qty:</strong>{' '}
                    {(ts.pos || []).reduce((a, b) => a + (parseFloat(b.qty) || 0), 0).toLocaleString()} pcs
                  </div>
                )}
                <ProductionChartView data={prodSummary} totalQty={ts.totalQty || 0} />
                {/* Color-wise breakdown */}
                {Object.keys(prodSummary.byColor || {}).length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.75rem', color: '#323338', marginBottom: '8px' }}>COLOR-WISE BREAKDOWN</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                        <thead><tr style={{ background: '#fff7ed' }}>{['Color', 'Cut', 'Sew', 'Finish', 'Ship'].map(h => <th key={h} style={{ padding: '5px 8px', textAlign: h === 'Color' ? 'left' : 'right', borderBottom: '1px solid #fed7aa', color: '#c2410c', fontSize: '0.7rem' }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {Object.entries(prodSummary.byColor)
                            .sort(([a],[b]) => a==='(Unspecified)'?1:b==='(Unspecified)'?-1:a.localeCompare(b))
                            .map(([color, qty]) => (
                            <tr key={color} style={{ borderBottom:'1px solid #fff7ed',
                              background: color==='(Unspecified)'?'#fafafa':'white', opacity: color==='(Unspecified)'?0.75:1 }}>
                              <td style={{ padding: '4px 8px', fontWeight: 'bold',
                                color: color==='(Unspecified)'?'#94a3b8':'#323338',
                                fontStyle: color==='(Unspecified)'?'italic':'normal' }}>
                                {color}
                                {color==='(Unspecified)' && <span style={{fontSize:'0.65rem',color:'#94a3b8',marginLeft:'4px',fontWeight:'normal'}}>
                                  (DataPad entries — no color logged)
                                </span>}
                              </td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', color: '#f59e0b' }}>{qty.cut || 0}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', color: '#3b82f6' }}>{qty.sew || 0}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', color: '#8b5cf6' }}>{qty.pack || 0}</td>
                              <td style={{ padding: '4px 8px', textAlign: 'right', color: '#00a65e' }}>{qty.ship || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: '10px', textAlign: 'right' }}>
                  <button onClick={() => setPassportTab('production')} style={{ padding: '5px 12px', background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', borderRadius: '6px', cursor: 'pointer', fontSize: '0.73rem', fontWeight: 'bold' }}>Full Production Follow-Up →</button>
                </div>
              </Section>

              {/* Factory Allocation — reads from ts.allocatedLines (confirmed) enriched by db.allocations factory name */}
              {factoryAllocCards.length > 0 && (
                <Section title="FACTORY ALLOCATION" icon={<Layers size={14} />} color="#7c3aed" bg="#faf5ff" defaultOpen={true}>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'10px' }}>
                    {factoryAllocCards.map((al,i)=>(
                      <div key={i} style={{background:'#f5f3ff',border:'1px solid #e9d5ff',borderRadius:'8px',padding:'12px'}}>
                        <div style={{fontWeight:'bold',fontSize:'0.85rem',color:'#7c3aed',marginBottom:'2px'}}>🏭 {al.resolvedFactory||'(Factory TBD)'}</div>
                        {al.resolvedLine&&<div style={{fontSize:'0.72rem',color:'#676879',marginBottom:'6px'}}>Line: <strong>{al.resolvedLine}</strong></div>}
                        {al.resolvedPO&&<div style={{fontSize:'0.68rem',color:'#7c3aed',marginBottom:'4px'}}>PO: {al.resolvedPO}</div>}
                        {al.resolvedQty>0&&<div style={{fontSize:'0.78rem',color:'#676879'}}>Qty: <strong style={{color:'#323338'}}>{al.resolvedQty.toLocaleString()}</strong></div>}
                        {al.resolvedStart&&<div style={{fontSize:'0.72rem',color:'#676879'}}>Start: <strong>{al.resolvedStart}</strong></div>}
                        {al.resolvedEnd&&<div style={{fontSize:'0.72rem',color:'#676879'}}>End: <strong>{al.resolvedEnd}</strong></div>}
                      </div>
                    ))}
                  </div>
                  {factoryAllocCards.some(a=>!a.resolvedFactory)&&<div style={{fontSize:'0.68rem',color:'#f59e0b',marginTop:'8px'}}>⚠ Some factory names unresolved — deploy get_all_data.php fix and hard-refresh.</div>}
                </Section>
              )}
            </div>
          )}

          {/* ---- TAB: PRODUCTION FOLLOW-UP ---- */}
          {passportTab === 'production' && (
            <div>
              <Section title="PRODUCTION FOLLOW UP" icon={<Activity size={14} />} color="#c2410c" bg="#fff7ed" defaultOpen={true}>
                {/* Summary Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', marginBottom: '16px' }}>
                  {[
                    { l: 'TOTAL QTY', v: ts.totalQty || 0, c: '#323338' },
                    { l: 'CUT', v: prodSummary.cut, c: '#f59e0b' },
                    { l: 'SEWING', v: prodSummary.sew, c: '#3b82f6' },
                    { l: 'FINISHED', v: prodSummary.pack, c: '#8b5cf6' },
                    { l: 'SHIPPED', v: prodSummary.ship, c: '#00a65e' },
                    { l: 'BALANCE', v: (ts.totalQty || 0) - (prodSummary.ship || 0), c: '#e2445c' },
                  ].map(({ l, v, c }) => (
                    <div key={l} style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.65rem', color: '#9699a6', letterSpacing: '0.5px', marginBottom: '4px' }}>{l}</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: c }}>{(v || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>

                {/* Toggle Chart / Bar */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => setProdChartMode('bar')} style={{ padding: '5px 12px', background: prodChartMode === 'bar' ? '#323338' : 'white', color: prodChartMode === 'bar' ? 'white' : '#676879', border: '1px solid #e6e9ef', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>Style Level Bar</button>
                  <button onClick={() => setProdChartMode('tracker')} style={{ padding: '5px 12px', background: prodChartMode === 'tracker' ? '#323338' : 'white', color: prodChartMode === 'tracker' ? 'white' : '#676879', border: '1px solid #e6e9ef', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>Progress Tracker</button>
                  <button onClick={() => setProdChartMode('po')} style={{ padding: '5px 12px', background: prodChartMode === 'po' ? '#323338' : 'white', color: prodChartMode === 'po' ? 'white' : '#676879', border: '1px solid #e6e9ef', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>PO-Wise</button>
                  <button onClick={() => setProdChartMode('color')} style={{ padding: '5px 12px', background: prodChartMode === 'color' ? '#323338' : 'white', color: prodChartMode === 'color' ? 'white' : '#676879', border: '1px solid #e6e9ef', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>Color-Wise</button>
                  <button onClick={() => setProdChartMode('size')} style={{ padding: '5px 12px', background: prodChartMode === 'size' ? '#323338' : 'white', color: prodChartMode === 'size' ? 'white' : '#676879', border: '1px solid #e6e9ef', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem' }}>Size-Wise</button>
                </div>

                {prodChartMode === 'bar' ? (
                  <ProductionChartView data={prodSummary} totalQty={ts.totalQty || 0} />
                ) : prodChartMode === 'tracker' ? (
                  <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '8px', padding: '16px' }}>
                    {[
                      { label: 'Cutting', key: 'cut', color: '#f59e0b' },
                      { label: 'Sewing', key: 'sew', color: '#3b82f6' },
                      { label: 'Finishing', key: 'pack', color: '#8b5cf6' },
                      { label: 'Shipped', key: 'ship', color: '#00a65e' },
                    ].map(s => <ProductionBar key={s.key} label={s.label} value={prodSummary[s.key] || 0} total={ts.totalQty || 0} color={s.color} />)}
                  </div>
                ) : prodChartMode === 'po' ? (
                  /* PO-Wise view — each PO with its qty and ship status from productionLogs */
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead><tr style={{ background: '#f5f6f8' }}>{['PO #', 'Color', 'PO Qty', 'Cut', 'Sew', 'Finished', 'Shipped', 'Ex-Factory', '% Complete'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: h === 'PO #' || h === 'Color' ? 'left' : 'right', borderBottom: '2px solid #e6e9ef', color: '#676879', fontSize: '0.7rem' }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {(ts.pos || []).length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', padding: '20px', color: '#9699a6' }}>No POs linked to this style</td></tr>}
                        {(ts.pos || []).map((po, i) => {
                          // Match production logs by color
                          const colorLogs = productionLogs.filter(l => l.style === ts.styleRef && (!po.col || l.color === po.col || po.col === 'Mix'));
                          const poCut = colorLogs.filter(l => l.type === 'CUT').reduce((a, b) => a + (parseInt(b.quantity) || 0), 0);
                          const poSew = colorLogs.filter(l => l.type === 'SEW').reduce((a, b) => a + (parseInt(b.quantity) || 0), 0);
                          const poPack = colorLogs.filter(l => l.type === 'PACK').reduce((a, b) => a + (parseInt(b.quantity) || 0), 0);
                          const poShip = colorLogs.filter(l => l.type === 'SHIP').reduce((a, b) => a + (parseInt(b.quantity) || 0), 0);
                          const pct = po.qty > 0 ? Math.min(100, Math.round((poShip / po.qty) * 100)) : 0;
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f1f4', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                              <td style={{ padding: '5px 10px', fontWeight: 'bold' }}>{po.num}</td>
                              <td style={{ padding: '5px 10px' }}>{po.col || '—'}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 'bold' }}>{(po.qty || 0).toLocaleString()}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#f59e0b' }}>{poCut.toLocaleString()}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#3b82f6' }}>{poSew.toLocaleString()}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#8b5cf6' }}>{poPack.toLocaleString()}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#00a65e', fontWeight: 'bold' }}>{poShip.toLocaleString()}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#676879' }}>{po.date || '—'}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                                  <div style={{ width: '50px', background: '#f0f1f4', borderRadius: '6px', height: '6px' }}>
                                    <div style={{ width: `${pct}%`, background: pct >= 100 ? '#00a65e' : '#3b82f6', height: '100%', borderRadius: '6px' }} />
                                  </div>
                                  <span style={{ fontSize: '0.7rem', color: pct >= 100 ? '#00a65e' : '#323338', fontWeight: 'bold' }}>{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : prodChartMode === 'size' ? (
                  /* Size-Wise view — extracted from PO sizeBreakdown field */
                  (() => {
                    const sizeMap = {};
                    (ts.pos || []).forEach(po => {
                      if (po.sizeBreakdown) {
                        try {
                          const sb = typeof po.sizeBreakdown === 'string' ? JSON.parse(po.sizeBreakdown) : po.sizeBreakdown;
                          Object.entries(sb).forEach(([size, qty]) => {
                            sizeMap[size] = (sizeMap[size] || 0) + (parseInt(qty) || 0);
                          });
                        } catch (e) {
                          // sizeBreakdown might be a plain string like "S:100, M:200, L:150"
                          const parts = String(po.sizeBreakdown).split(',');
                          parts.forEach(part => {
                            const [sz, q] = part.split(':').map(x => x.trim());
                            if (sz && q) sizeMap[sz] = (sizeMap[sz] || 0) + (parseInt(q) || 0);
                          });
                        }
                      }
                    });
                    const total = Object.values(sizeMap).reduce((a, b) => a + b, 0);
                    return Object.keys(sizeMap).length > 0 ? (
                      <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '8px', padding: '16px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '0.78rem', color: '#323338', marginBottom: '10px' }}>SIZE-WISE QTY (from PO Size Breakdown)</div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', height: '120px', padding: '0 8px' }}>
                          {Object.entries(sizeMap).map(([size, qty]) => {
                            const pct = total > 0 ? (qty / total) * 100 : 0;
                            return (
                              <div key={size} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                <span style={{ fontSize: '0.72rem', fontWeight: 'bold' }}>{qty.toLocaleString()}</span>
                                <div style={{ width: '100%', background: '#f0f1f4', borderRadius: '4px 4px 0 0', height: '80px', display: 'flex', alignItems: 'flex-end' }}>
                                  <div style={{ width: '100%', background: '#3b82f6', borderRadius: '4px 4px 0 0', height: `${pct}%`, transition: 'height 0.5s' }} />
                                </div>
                                <span style={{ fontSize: '0.7rem', color: '#676879' }}>{size}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : <div style={{ color: '#9699a6', textAlign: 'center', padding: '20px' }}>No size breakdown data in POs. Add size breakdown (e.g. S:100,M:200) when uploading or editing POs.</div>;
                  })()
                ) : (
                  <div style={{ background: 'white', border: '1px solid #e6e9ef', borderRadius: '8px', padding: '16px' }}>
                    {/* color mode */}
                    <div style={{ fontWeight: 'bold', fontSize: '0.78rem', color: '#323338', marginBottom: '10px' }}>COLOR-WISE PRODUCTION BREAKDOWN</div>
                    {Object.keys(prodSummary.byColor || {}).length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                          <thead><tr style={{ background: '#f5f6f8' }}>{['Color', 'Cut', 'Sew', 'Finish', 'Ship'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Color' ? 'left' : 'right', borderBottom: '1px solid #e6e9ef', color: '#676879', fontSize: '0.72rem' }}>{h}</th>)}</tr></thead>
                          <tbody>
                            {Object.entries(prodSummary.byColor).map(([color, qty]) => (
                              <tr key={color} style={{ borderBottom: '1px solid #f5f6f8' }}>
                                <td style={{ padding: '5px 10px', fontWeight: 'bold' }}>{color}</td>
                                <td style={{ padding: '5px 10px', textAlign: 'right', color: '#f59e0b' }}>{qty.cut || 0}</td>
                                <td style={{ padding: '5px 10px', textAlign: 'right', color: '#3b82f6' }}>{qty.sew || 0}</td>
                                <td style={{ padding: '5px 10px', textAlign: 'right', color: '#8b5cf6' }}>{qty.pack || 0}</td>
                                <td style={{ padding: '5px 10px', textAlign: 'right', color: '#00a65e' }}>{qty.ship || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <div style={{ color: '#9699a6', textAlign: 'center', padding: '20px' }}>No color-wise production data. Log production entries with color field in the Production Follow-Up module.</div>}
                  </div>
                )}

                {/* Color-wise breakdown */}
                {Object.keys(prodSummary.byColor || {}).length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.78rem', color: '#323338', marginBottom: '10px' }}>COLOR-WISE BREAKDOWN</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                        <thead><tr style={{ background: '#f5f6f8' }}>{['Color', 'Cut', 'Sew', 'Finish', 'Ship'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Color' ? 'left' : 'right', borderBottom: '1px solid #e6e9ef', color: '#676879', fontSize: '0.72rem' }}>{h}</th>)}</tr></thead>
                        <tbody>
                          {Object.entries(prodSummary.byColor).map(([color, qty]) => (
                            <tr key={color} style={{ borderBottom: '1px solid #f5f6f8' }}>
                              <td style={{ padding: '5px 10px', fontWeight: 'bold' }}>{color}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#f59e0b' }}>{qty.cut || 0}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#3b82f6' }}>{qty.sew || 0}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#8b5cf6' }}>{qty.pack || 0}</td>
                              <td style={{ padding: '5px 10px', textAlign: 'right', color: '#00a65e' }}>{qty.ship || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Section>
            </div>
          )}

          {/* ---- TAB: PRICE COMPARISON ---- */}
          {passportTab === 'pricing' && (
            <div>
              <Section title="SUPPLIER PRICE COMPARISON" icon={<DollarSign size={14} />} color="#1e40af" bg="#e8f0ff" defaultOpen={true}>
                {linkedQuotations.length > 0 ? (
                  <div>
                    <div style={{ fontSize: '0.78rem', color: '#676879', marginBottom: '12px' }}>Comparison of prices quoted by all suppliers from the Sourcing module.</div>
                    <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
                      {linkedQuotations.map((q, i) => (
                        <SupplierQuoteCard key={i} supplier={q.supplier || `Supplier ${i + 1}`} isSelected={q.status === 'Selected'}
                          costHeads={{
                            'Material': q.material || q.data?.material || 0,
                            'CM': q.cm || q.data?.cm || 0,
                            'Trims': q.trims || q.data?.trims || 0,
                            'FOB': q.fob || q.data?.fob || 0,
                          }}
                        />
                      ))}
                    </div>
                    <div style={{ marginTop: '16px', padding: '10px', background: '#f0fdf4', border: '1px solid #e3faf0', borderRadius: '6px', fontSize: '0.78rem', color: '#166534' }}>
                      <strong><Star size={12} style={{ display: 'inline' }} /> Selected Supplier:</strong> {linkedQuotations.find(q => q.status === 'Selected')?.supplier || 'Not yet selected'}
                    </div>
                  </div>
                ) : <div style={{ textAlign: 'center', color: '#9699a6', padding: '30px 0' }}>No supplier quotations found for this style in Sourcing module.</div>}
              </Section>
            </div>
          )}

          {/* ---- TAB: DOCUMENTS & BOM ---- */}
          {passportTab === 'docs' && (
            <div>
              {/* PO Section */}
              <Section title="PURCHASE ORDERS" icon={<FileText size={14} />} color="#1e40af" bg="#e8f0ff" defaultOpen={true}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <label style={{ cursor: 'pointer', padding: '6px 12px', background: '#e8f0ff', border: '1px solid #a8d1ff', color: '#1e40af', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                    <input type="file" hidden onChange={e => handlePassportDocUpload(e, 'PO')} /> AI Extract PO
                  </label>
                </div>
                <EditableTable
                  headers={['PO#', 'Color', 'Qty', 'FOB', 'Ex-Factory', 'Destination', 'Size Breakdown']}
                  rows={(ts.pos || []).map(p => ({ 'po#': p.num, color: p.col, qty: p.qty, fob: p.fob, 'ex-factory': p.date, destination: p.dest, 'size breakdown': p.sizeBreakdown }))}
                  emptyMsg="No POs. Upload file for AI extraction or add manually."
                  onAddRow={() => {
                    const newRow = { id: Date.now(), num: 'NEW', col: '', qty: 0, date: '', fob: '', dest: '' };
                    savePassportPOs([...(ts.pos || []), newRow]);
                  }}
                  onDeleteRow={idx => savePassportPOs((ts.pos || []).filter((_, i) => i !== idx))}
                  onEditRow={(idx, row) => {
                    const updated = (ts.pos || []).map((p, i) => i === idx ? { ...p, num: row['po#'], col: row.color, qty: parseFloat(row.qty) || 0, date: row['ex-factory'], fob: row.fob, dest: row.destination, sizeBreakdown: row['size breakdown'] } : p);
                    savePassportPOs(updated);
                  }}
                />
              </Section>

              {/* BOM / Techpack Section */}
              <Section title="TECHPACK — BILL OF MATERIALS" icon={<Layers size={14} />} color="#6d28d9" bg="#faf5ff" defaultOpen={true}
                badge={ts.bom ? `${ts.bom.length} items` : 'Not extracted'}>
                {/* Upload + controls row */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <label style={{ cursor: 'pointer', padding: '6px 14px', background: '#7c3aed', color: 'white', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Upload size={12} /> AI Extract Techpack
                    <input type="file" accept="image/*" hidden onChange={e => handlePassportDocUpload(e, 'TECHPACK')} />
                  </label>
                  <span style={{ fontSize: '0.7rem', color: '#9699a6' }}>Upload one page at a time — each upload adds to existing data</span>
                  {ts.bom && ts.bom.length > 0 && (
                    <button onClick={() => { if(window.confirm('Clear all BOM data?')) savePassportBOM([]); }}
                      style={{ marginLeft: 'auto', padding: '4px 10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#e2445c', borderRadius: '6px', cursor: 'pointer', fontSize: '0.72rem' }}>
                      Clear All
                    </button>
                  )}
                </div>

                {(ts.bom || []).length > 0 ? (() => {
                  // Group rows by section/category
                  const sections = [...new Set((ts.bom).map(b => b.category || 'General'))];
                  return (
                    <div>
                      {sections.map(section => {
                        const items = (ts.bom).filter(b => (b.category || 'General') === section);
                        // Only show columns that have actual data in this section
                        const has = f => items.some(b => b[f] && String(b[f]).trim() && String(b[f]).trim() !== '-');
                        const cols = [
                          { f: 'item_name',       label: 'Item / Code',           bold: true  },
                          { f: 'description',     label: 'Description / Material', bold: false },
                          { f: 'supplier',        label: 'Supplier',               bold: false },
                          { f: 'reference',       label: 'Reference',              bold: false },
                          { f: 'consumption',     label: 'Consumption',            bold: false },
                          { f: 'placement',       label: 'Placement / Comments',   bold: false },
                          { f: 'color_breakdown', label: 'Colours',                bold: false },
                          { f: 'size_variation',  label: 'Size Var.',              bold: false },
                          { f: 'comments',        label: 'Comments',               bold: false },
                        ].filter(({ f }) => has(f));
                        return (
                          <div key={section} style={{ marginBottom: '18px' }}>
                            <div style={{ fontWeight: 'bold', fontSize: '0.78rem', color: 'white', padding: '6px 12px', background: '#7c3aed', borderRadius: '5px 5px 0 0', letterSpacing: '0.06em' }}>
                              {section.toUpperCase()} <span style={{ fontWeight: 'normal', opacity: 0.8, fontSize: '0.7rem' }}>({items.length} items)</span>
                            </div>
                            <div style={{ overflowX: 'auto', border: '1px solid #e9d5ff', borderTop: 'none', borderRadius: '0 0 5px 5px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                <thead>
                                  <tr style={{ background: '#faf5ff' }}>
                                    {cols.map(({ f, label }) => (
                                      <th key={f} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e9d5ff', color: '#6d28d9', fontSize: '0.68rem', fontWeight: '700', whiteSpace: 'nowrap' }}>{label}</th>
                                    ))}
                                    <th style={{ padding: '6px 8px', borderBottom: '2px solid #e9d5ff', width: '28px' }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((b, bi) => {
                                    const globalIdx = (ts.bom || []).indexOf(b);
                                    return (
                                      <tr key={bi} style={{ background: bi % 2 === 0 ? 'white' : '#fdf4ff', borderBottom: '1px solid #f3e8ff' }}>
                                        {cols.map(({ f, bold }) => (
                                          <td key={f} style={{ padding: '6px 10px', border: '1px solid #f3e8ff', maxWidth: '220px', verticalAlign: 'top' }}>
                                            <span style={{ fontWeight: bold ? '700' : 'normal', color: bold ? '#323338' : '#323338', fontSize: '0.73rem', display: 'block', overflowWrap: 'break-word' }}>
                                              {b[f] ? String(b[f]) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                            </span>
                                          </td>
                                        ))}
                                        <td style={{ padding: '4px 6px', border: '1px solid #f3e8ff', textAlign: 'center', verticalAlign: 'middle' }}>
                                          <button onClick={() => savePassportBOM((ts.bom || []).filter((_, i) => i !== globalIdx))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e2445c', padding: '2px' }}>
                                            <Trash2 size={12} />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                      <button onClick={() => savePassportBOM([...(ts.bom || []), { category: sections[0] || 'Fabric', item_name: '', description: '', consumption: '', placement: '' }])}
                        style={{ padding: '5px 14px', background: '#f3e8ff', color: '#6d28d9', border: '1px solid #d8b4fe', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold' }}>
                        + Add Row
                      </button>
                    </div>
                  );
                })() : (
                  <div style={{ textAlign: 'center', padding: '30px 20px', color: '#9699a6' }}>
                    <Layers size={32} color="#e9d5ff" style={{ display: 'block', margin: '0 auto 10px' }} />
                    <div style={{ fontSize: '0.82rem', marginBottom: '6px' }}>No BOM data yet.</div>
                    <div style={{ fontSize: '0.73rem' }}>Click <strong style={{ color: '#7c3aed' }}>AI Extract Techpack</strong> above to upload a page.<br/>Upload multiple pages one at a time — data accumulates automatically.</div>
                  </div>
                )}
              </Section>

              {/* Spec Sheet Section */}
              <Section title="SPEC SHEET — MEASUREMENTS" icon={<Calculator size={14} />} color="#b45309" bg="#fefce8" defaultOpen={true}
                badge={ts.measurements ? `${ts.measurements.rows?.length || 0} POMs` : 'Not extracted'}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <label style={{ cursor: 'pointer', padding: '6px 12px', background: '#fefce8', border: '1px solid #fde68a', color: '#b45309', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                    <input type="file" hidden onChange={e => handlePassportDocUpload(e, 'SPEC')} /> AI Extract Spec
                  </label>
                </div>
                {ts.measurements?.rows ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.78rem' }}>
                      <thead>
                        <tr style={{ background: '#fefce8' }}>
                          <th style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #fde68a', fontWeight: 'bold' }}>POM</th>
                          {(ts.measurements.headers || []).filter(h => h !== 'pom' && h !== 'tolerance').map((h, i) => <th key={i} style={{ padding: '6px 10px', textAlign: 'center', border: '1px solid #fde68a' }}>{h}</th>)}
                          <th style={{ padding: '6px 10px', textAlign: 'center', border: '1px solid #fde68a' }}>TOL ±</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ts.measurements.rows || []).map((row, i) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fffbeb', borderBottom: '1px solid #fde68a' }}>
                            <td style={{ padding: '5px 10px', fontWeight: 'bold', border: '1px solid #fde68a', color: '#323338' }}>{row.pom || row[Object.keys(row)[0]]}</td>
                            {(ts.measurements.headers || []).filter(h => h !== 'pom' && h !== 'tolerance').map((h, j) => <td key={j} style={{ padding: '5px 10px', textAlign: 'center', border: '1px solid #fde68a' }}>{row[h] || '—'}</td>)}
                            <td style={{ padding: '5px 10px', textAlign: 'center', border: '1px solid #fde68a', color: '#b45309' }}>{row.tolerance || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div style={{ color: '#9699a6', textAlign: 'center', padding: '20px' }}>No spec data extracted yet.</div>}
              </Section>
            </div>
          )}

          {/* ---- TAB: OPERATION BULLETIN ---- */}
          {passportTab === 'ob' && (
            <div>
              {/* Standard OB from Workstation */}
              <Section title="STANDARD OPERATION BULLETIN (Workstation)" icon={<Settings size={14} />} color="#323338" bg="#f5f6f8" defaultOpen={true}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '6px', background: '#f5f6f8', border: '1px solid #e6e9ef', padding: '8px 12px', borderRadius: '6px', alignItems: 'center' }}>
                    <label style={{ fontSize: '0.72rem', color: '#676879', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>Efficiency %</span>
                      <input type="number" value={obEffVal} min={1} max={100} onChange={e => setTargetStyle(prev => ({ ...prev, obSettings: { ...prev.obSettings, eff: parseFloat(e.target.value) } }))} style={{ width: '65px', border: '1px solid #e6e9ef', borderRadius: '6px', padding: '3px 5px', fontSize: '0.8rem' }} />
                    </label>
                    <label style={{ fontSize: '0.72rem', color: '#676879', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span>Work Hours</span>
                      <input type="number" value={obHoursVal} min={1} max={24} onChange={e => setTargetStyle(prev => ({ ...prev, obSettings: { ...prev.obSettings, hours: parseFloat(e.target.value) } }))} style={{ width: '55px', border: '1px solid #e6e9ef', borderRadius: '6px', padding: '3px 5px', fontSize: '0.8rem' }} />
                    </label>
                  </div>
                  <button onClick={() => setShowVisualStudio(true)} style={{ padding: '8px 14px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 'bold' }}>Open 3D Studio</button>
                </div>

                {/* OB Summary Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '14px' }}>
                  {[
                    { l: 'TOTAL SMV', v: totalSMV.toFixed(3), c: '#5b21b6' },
                    { l: 'HOURLY TARGET', v: `${hourlyTarget} pcs`, c: '#5b21b6' },
                    { l: 'DAILY TARGET', v: `${dailyTarget} pcs`, c: '#5b21b6' },
                    { l: 'MACHINES', v: uniqueMachines, c: '#5b21b6' },
                    { l: 'MANPOWER', v: `${ops.length} + ${qcStaff} QC`, c: '#5b21b6' },
                  ].map(({ l, v, c }) => (
                    <div key={l} style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.62rem', color: c, letterSpacing: '0.5px', marginBottom: '3px' }}>{l}</div>
                      <div style={{ fontWeight: 'bold', fontSize: '1rem', color: c }}>{v}</div>
                    </div>
                  ))}
                </div>

                {ops.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                      <thead><tr style={{ background: '#f5f3ff' }}>{['Seq', 'Operation', 'Part', 'Machine', 'SMV'].map(h => <th key={h} style={{ padding: '6px 10px', textAlign: h === 'SMV' ? 'right' : 'left', border: '1px solid #e6e9ef', color: '#5b21b6', fontSize: '0.7rem' }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {ops.map((op, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f0f1f4', background: i % 2 === 0 ? 'white' : '#fafaf9' }}>
                            <td style={{ padding: '5px 10px', color: '#9699a6' }}>{i + 1}</td>
                            <td style={{ padding: '5px 10px', fontWeight: '600' }}>{op.procName}</td>
                            <td style={{ padding: '5px 10px', color: '#676879' }}>{op.partName || 'Body'}</td>
                            <td style={{ padding: '5px 10px' }}><span style={{ background: '#f0f1f4', padding: '1px 6px', borderRadius: '6px', fontSize: '0.7rem' }}>{op.machine}</span></td>
                            <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace' }}>{parseFloat(op.smv || 0).toFixed(3)}</td>
                          </tr>
                        ))}
                        <tr style={{ background: '#f5f3ff', fontWeight: 'bold' }}>
                          <td colSpan={4} style={{ padding: '6px 10px', color: '#5b21b6', textAlign: 'right' }}>TOTAL</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: '#5b21b6', fontFamily: 'monospace' }}>{totalSMV.toFixed(3)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : <div style={{ textAlign: 'center', color: '#9699a6', padding: '20px' }}>No operations from Workstation. Open 3D Studio to create layout.</div>}
              </Section>

              {/* Supplier OBs Comparison */}
              <Section title="SUPPLIER OBs — COMPARISON" icon={<ArrowUpDown size={14} />} color="#b45309" bg="#fefce8" defaultOpen={true}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', alignItems: 'center' }}>
                  <label style={{ cursor: 'pointer', padding: '7px 14px', background: '#fefce8', border: '1px solid #fde68a', color: '#b45309', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 'bold', opacity: obLoading ? 0.6 : 1 }}>
                    <input type="file" hidden onChange={handleOBUpload} disabled={obLoading} />
                    {obLoading ? 'Extracting...' : '+ Upload Supplier OB (AI)'}
                  </label>
                  <span style={{ fontSize: '0.72rem', color: '#676879' }}>Supports image, CSV, text files. Top 5 OBs compared against Standard.</span>
                </div>
                {storedOBs.length > 0 ? (
                  <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
                    {storedOBs.slice(0, 5).map((ob, i) => (
                      <OBSupplierCard key={i} ob={ob} standard={standardOps} onRemove={removeSupplierOB} idx={i} />
                    ))}
                  </div>
                ) : <div style={{ color: '#9699a6', textAlign: 'center', padding: '20px' }}>No supplier OBs uploaded yet. Upload OB files for AI-powered comparison against Standard OB.</div>}
              </Section>
            </div>
          )}

          {/* ---- TAB: SEASON HISTORY ---- */}
          {passportTab === 'history' && (
            <div>
              <Section title="SEASON HISTORY — SUPPLIER PERFORMANCE" icon={<TrendingUp size={14} />} color="#166534" bg="#f0fdf4" defaultOpen={true}>
                {sameStyleAllSeasons.length > 1 ? (
                  <div>
                    <div style={{ fontSize: '0.78rem', color: '#676879', marginBottom: '12px' }}>Historical performance for styles with same base reference across seasons.</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                        <thead>
                          <tr style={{ background: '#e3faf0' }}>
                            {['Season', 'Supplier', 'Total Qty', 'Placed Qty', 'Price', 'Lead Time (Plan)', 'Lead Time (Actual)', 'SMV', 'Efficiency'].map(h => (
                              <th key={h} style={{ padding: '7px 10px', textAlign: h === 'Season' || h === 'Supplier' ? 'left' : 'right', border: '1px solid #b3f5d6', color: '#166534', fontSize: '0.72rem' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sameStyleAllSeasons.map((s, i) => {
                            const sCost = getCostSummary(s.id);
                            const sProd = getProdSummary(s.styleRef);
                            const sQuote = quotations.find(q => q.name === s.styleRef && q.status === 'Selected');
                            const posSorted = (s.pos || []).sort((a, b) => new Date(a.date) - new Date(b.date));
                            const exFtyPlan = posSorted[0]?.date || '—';
                            // Compute placed qty for this style's supplier
                            const sPlacedQty = (s.pos || []).reduce((a, b) => a + (parseFloat(b.qty) || 0), 0);
                            // Use shipmentLogs (from DB) for actual ex-factory — most accurate source
                            const shipLogs = (db.shipmentLogs || []).filter(l => l.style_ref === s.styleRef && l.actual_exfty_date);
                            const lastActualExFty = shipLogs.length > 0 
                              ? shipLogs.sort((a, b) => new Date(b.actual_exfty_date) - new Date(a.actual_exfty_date))[0].actual_exfty_date 
                              : null;
                            // Fallback: use last SHIP production log
                            const actualShipLogs = productionLogs.filter(l => l.style === s.styleRef && l.type === 'SHIP');
                            const lastShipDate = lastActualExFty || (actualShipLogs.length > 0 ? actualShipLogs.sort((a, b) => new Date(b.date) - new Date(a.date))[0].date : '—');
                            // Plan leadtime: days from first PO date to last PO ex-fty
                            const firstPODate = posSorted[0]?.date;
                            const lastPOExFty = posSorted[posSorted.length - 1]?.date;
                            const planLeadtime = (firstPODate && lastPOExFty && firstPODate !== lastPOExFty)
                              ? Math.round((new Date(lastPOExFty) - new Date(firstPODate)) / (1000 * 60 * 60 * 24))
                              : (exFtyPlan !== '—' ? 90 : 0);
                            // Actual leadtime: from first PO date to actual last ship/ex-fty date
                            const actualLeadtime = (firstPODate && lastShipDate !== '—')
                              ? Math.round((new Date(lastShipDate) - new Date(firstPODate)) / (1000 * 60 * 60 * 24))
                              : planLeadtime;
                            return (
                              <SeasonHistoryRow key={i}
                                season={s.season} supplier={sQuote?.supplier || '—'}
                                qty={s.totalQty} placedQty={sPlacedQty} price={sCost ? format(sCost.fob) : '—'}
                                leadtimePlan={planLeadtime} leadtimeActual={actualLeadtime}
                                smv={sCost?.smv?.toFixed(2) || '—'} efficiency={obEffVal}
                                isPlaced={s.id === ts.id}
                              />
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ marginTop: '12px', padding: '10px', background: '#f0fdf4', border: '1px solid #b3f5d6', borderRadius: '6px', fontSize: '0.75rem', color: '#676879' }}>
                      <Star size={12} color="#f59e0b" style={{ display: 'inline', marginRight: '4px' }} /> Highlighted row = current style. Green supplier = order placed.
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#9699a6', padding: '30px' }}>
                    No previous season data found for styles matching this base reference.<br />
                    <small>Tip: Style references with same base prefix (e.g. ST-100-SS24 and ST-100-FW24) are grouped here.</small>
                  </div>
                )}
              </Section>

              {/* Season Price Summary */}
              <Section title="PREVIOUS SEASON COSTING SUMMARY" icon={<BarChart size={14} />} color="#1e40af" bg="#e8f0ff" defaultOpen={false}>
                {sameStyleAllSeasons.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                    {sameStyleAllSeasons.map((s, i) => {
                      const sCost = getCostSummary(s.id);
                      return (
                        <div key={i} style={{ background: 'white', border: `2px solid ${s.id === ts.id ? '#3b82f6' : '#e6e9ef'}`, borderRadius: '8px', padding: '12px' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '0.85rem', color: s.id === ts.id ? '#1e40af' : '#323338', marginBottom: '8px' }}>
                            {s.season} {s.id === ts.id && <span style={{ fontSize: '0.65rem', color: '#3b82f6' }}>(current)</span>}
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#676879' }}>Qty: <strong>{(s.totalQty || 0).toLocaleString()}</strong></div>
                          {sCost && <>
                            <div style={{ fontSize: '0.78rem', color: '#676879' }}>Material: <strong>{sCost.m.toFixed(2)}</strong></div>
                           <div style={{ fontSize: '0.78rem', color: '#676879' }}>CM: <strong>{sCost.cm.toFixed(2)}</strong></div>
<div style={{ fontSize: '0.78rem', color: '#00a65e', fontWeight: 'bold' }}>
  FOB: {(() => {
    const activeBaseObj = db?.currencies?.find(c => c.isBase) || db?.currencyList?.find(c => c.isBase);
    const baseNameString = activeBaseObj ? activeBaseObj.name : 'USD';
    const finalCurrencyLabel = sCost.currency || currency?.name || baseNameString;
    return `${finalCurrencyLabel} ${parseFloat(sCost.fob || 0).toFixed(2)}`;
  })()}
</div>
                          </>}
                        </div>
                      );
                    })}
                  </div>
                ) : <div style={{ color: '#9699a6', textAlign: 'center', padding: '20px' }}>No history data available.</div>}
              </Section>
            </div>
          )}

          <div style={{ height: '80px' }} />
        </div>
        <NavigationArrows />

        {/* Gallery hover CSS */}
        <style>{`
          .sr-card:hover .sr-hover-overlay { display: flex !important; }
          .npd-table { width: 100%; border-collapse: collapse; }
          .npd-table th { padding: 6px 10px; text-align: left; background: #f5f6f8; border-bottom: 2px solid #e6e9ef; font-size: 0.72rem; color: #676879; font-weight: 700; letter-spacing: 0.5px; }
          .npd-table td { padding: 6px 10px; border-bottom: 1px solid #f0f1f4; font-size: 0.8rem; color: #323338; }
          .npd-table tr:last-child td { border-bottom: none; }
          .npd-table tr:hover td { background: #f5f6f8; }
        `}</style>
      </div>
    );
  }

  return <div style={{ padding: '40px', textAlign: 'center', color: '#9699a6' }}>Loading Style Registry...</div>;
}
